import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  BackgroundVariant,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
  NodeResizer,
  reconnectEdge,
  useEdges,
  useNodes,
  useUpdateNodeInternals,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng, toSvg } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config/apiConfig';
import LayoutStudio from '../LayoutStudio/LayoutStudio';
import { createLayoutDocument, normalizeLayoutDocument } from '../LayoutStudio/layoutDocument';

export const loadExportImage = (source) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Failed to decode export image'));
  image.src = source;
});

// ─── SVG Component Symbols ────────────────────────────────────────────────────

const SYMBOL_SIZE = 60;

// Returns an SVG transform that counter-rotates text by `rot` degrees
// around the point (cx, cy) so label text always reads upright.
const lt = (rot, cx, cy) => rot ? `rotate(${-rot},${cx},${cy})` : undefined;

// Diagram-level settings (label font size, etc.) shared via context so all
// symbols and floating labels update together without prop-drilling.
const DiagramSettingsCtx = React.createContext({ labelFontSize: 7 });

// Renders a single text label positioned outside OR inside the component boundary.
// side: 'top'|'bottom'|'left'|'right'         – outside (beyond SVG viewBox edge)
//        'top-in'|'bottom-in'|'left-in'|'right-in' – inside (within SVG viewBox)
// lineNo: 0=primary(ref), 1=secondary(value)
const LabelText = ({ side = 'top', vbW, vbH, rotation, fill = '#1a56db', fontSize: fontSizeProp = 7, fontWeight = 'bold', fontFamily = 'monospace', lineNo = 0, children }) => {
  const { labelFontSize = 7 } = React.useContext(DiagramSettingsCtx) || {};
  const scale = labelFontSize / 7;
  const fontSize = fontSizeProp * scale;
  if (!children) return null;
  const midX = vbW / 2, midY = vbH / 2;
  let x, y, anchor;
  switch (side) {
    case 'bottom':    x = midX;    y = vbH + 4 + lineNo * 9;              anchor = 'middle'; break;
    case 'left':      x = -7;      y = midY + (lineNo - 0.5) * 9;         anchor = 'end';    break;
    case 'right':     x = vbW + 7; y = midY + (lineNo - 0.5) * 9;         anchor = 'start';  break;
    case 'top-in':    x = midX;    y = fontSize + 2 + lineNo * 9;          anchor = 'middle'; break;
    case 'bottom-in': x = midX;    y = vbH - (fontSize + 2) - lineNo * 9;  anchor = 'middle'; break;
    case 'left-in':   x = 4;       y = midY + (lineNo - 0.5) * 9;         anchor = 'start';  break;
    case 'right-in':  x = vbW - 4; y = midY + (lineNo - 0.5) * 9;         anchor = 'end';    break;
    default:          x = midX;    y = -4 - lineNo * 9;                    anchor = 'middle'; // 'top' – outside top
  }
  // Counter-rotate around the component centre so the text stays upright.
  // Pivoting on the component centre (midX, midY) keeps top/bottom labels
  // visually centred over the component regardless of its CSS rotation.
  return (
    <text x={x} y={y} textAnchor={anchor} fontSize={fontSize} fill={fill}
          fontFamily={fontFamily} fontWeight={fontWeight}
          transform={lt(rotation, midX, midY)}>
      {children}
    </text>
  );
};

// Resistor: IEEE zigzag body
const ResistorSymbol = ({ value, label, rotation, labelPos, width = 60, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 60 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="30" y1="0" x2="30" y2="15" stroke="#333" strokeWidth="1.5"/>
    <polyline points="30,15 24,20 36,25 24,30 36,35 24,40 36,45 30,45" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="30" y1="45" x2="30" y2="60" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={60} rotation={rotation}>{label}{value ? ` - ${value}` : ''}</LabelText>
  </svg>
);

// Capacitor: two parallel plates
const CapacitorSymbol = ({ value, label, polar, rotation, labelPos, width = 60, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 60 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="30" y1="0" x2="30" y2="26" stroke="#333" strokeWidth="1.5"/>
    <line x1="10" y1="26" x2="50" y2="26" stroke="#333" strokeWidth="2.5"/>
    {polar
      ? <path d="M10,34 Q30,28 50,34" fill="none" stroke="#333" strokeWidth="2.5"/>
      : <line x1="10" y1="34" x2="50" y2="34" stroke="#333" strokeWidth="2.5"/>}
    {polar && <text x="4" y="30" fontSize="9" fill="#333" fontWeight="bold">+</text>}
    <line x1="30" y1="34" x2="30" y2="60" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={60} rotation={rotation}>{label}{value ? ` - ${value}` : ''}</LabelText>
  </svg>
);

// Iron-core inductor (choke) — air-core coil bumps with two solid core lines
// 4 bumps spanning y=15..45, lead stubs top y=0..15 and bottom y=45..60
const IronCoreInductorSymbol = ({ value, label, rotation, labelPos, width = 60, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 60 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Lead stubs */}
    <line x1="30" y1="0"  x2="30" y2="15" stroke="#333" strokeWidth="1.5"/>
    <line x1="30" y1="45" x2="30" y2="60" stroke="#333" strokeWidth="1.5"/>
    {/* 4 coil bumps bulging right (IEC style, viewed top-to-bottom) */}
    <path d="M 30,15 a 7.5,7.5 0 0 1 0,15 a 7.5,7.5 0 0 1 0,15" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Iron core — two vertical lines on the left side */}
    <line x1="14" y1="13" x2="14" y2="47" stroke="#333" strokeWidth="1.5"/>
    <line x1="18" y1="13" x2="18" y2="47" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={60} rotation={rotation}>{label}{value ? ` - ${value}` : ''}</LabelText>
  </svg>
);

// Triode tube (uses envelope circle with greek symbols inside)
// Grid at y=40 so the left-handle default top:50% (=40px in 80px node) aligns exactly.
const TriodeSymbol = ({ label, rotation, labelPos, width = 80, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 80 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <circle cx="40" cy="40" r="32" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Plate — lead exits top edge at y=0 */}
    <line x1="22" y1="22" x2="58" y2="22" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="0" x2="40" y2="22" stroke="#333" strokeWidth="1.5"/>
    {/* Grid — at y=40; exit lead from x=0 to dashes; handle default top:50%=40px ✓ */}
    <line x1="22" y1="40" x2="32" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="38" y1="40" x2="48" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="54" y1="40" x2="64" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="0" y1="40" x2="22" y2="40" stroke="#333" strokeWidth="1.5"/>
    {/* Cathode — lead exits bottom edge at y=80 */}
    <line x1="28" y1="50" x2="52" y2="50" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="50" x2="40" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Heater (visual only, no handles) — at x=30,50 (multiples of 10); exit y=80 */}
    <line x1="30" y1="62" x2="30" y2="80" stroke="#c53030" strokeWidth="1.5"/>
    <line x1="50" y1="62" x2="50" y2="80" stroke="#c53030" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={80} vbH={80} rotation={rotation} fontSize={8}>{label}</LabelText>
  </svg>
);

// Pentode tube
// Height=100 so all handles land on 10px grid multiples:
//   screen (right)  top:30% = 30px; control grid (left) top:40% = 40px
const PentodeSymbol = ({ label, rotation, labelPos, width = 80, height = 100 }) => (
  <svg width={width} height={height} viewBox="0 0 80 100" overflow="visible" style={{ pointerEvents: 'none' }}>
    <circle cx="40" cy="44" r="32" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Plate — lead exits top at y=0 */}
    <line x1="22" y1="20" x2="58" y2="20" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="0" x2="40" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Screen grid (g2) — at y=30; exits right to x=80; handle top:30%=30px ✓ */}
    <line x1="22" y1="30" x2="58" y2="30" stroke="#333" strokeWidth="1.5" strokeDasharray="4,2"/>
    <line x1="58" y1="30" x2="80" y2="30" stroke="#333" strokeWidth="1.5"/>
    {/* Control grid (g1) — at y=40; exits left to x=0; handle top:40%=40px ✓ */}
    <line x1="22" y1="40" x2="32" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="38" y1="40" x2="48" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="54" y1="40" x2="64" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="0" y1="40" x2="22" y2="40" stroke="#333" strokeWidth="1.5"/>
    {/* Suppressor grid (g3) */}
    <line x1="22" y1="52" x2="58" y2="52" stroke="#555" strokeWidth="1" strokeDasharray="2,3"/>
    {/* Cathode — lead exits bottom at y=100 */}
    <line x1="28" y1="62" x2="52" y2="62" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="62" x2="40" y2="100" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={80} vbH={100} rotation={rotation} fontSize={8}>{label}</LabelText>
  </svg>
);

// Beam power tetrode (KT66, KT88, 6L6) — three internal elements:
//   g1 (control) exits RIGHT; g2 (screen) exits RIGHT; beam-forming plates exit LEFT
// Height=100:  grid (right) top:30%=30px; screen (right) top:40%=40px
const BeamTetrodeSymbol = ({ label, rotation, labelPos, width = 80, height = 100 }) => (
  <svg width={width} height={height} viewBox="0 0 80 100" overflow="visible" style={{ pointerEvents: 'none' }}>
    <circle cx="40" cy="44" r="32" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Plate — lead exits top at y=0 */}
    <line x1="22" y1="20" x2="58" y2="20" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="0"  x2="40" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Control grid (g1) — dashed, exits RIGHT; handle top:30%=30px ✓ */}
    <line x1="22" y1="30" x2="32" y2="30" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="38" y1="30" x2="48" y2="30" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="54" y1="30" x2="64" y2="30" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="58" y1="30" x2="80" y2="30" stroke="#333" strokeWidth="1.5"/>
    {/* Screen grid (g2) — dashed, exits RIGHT; handle top:40%=40px ✓ */}
    <line x1="22" y1="40" x2="32" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="4,2"/>
    <line x1="38" y1="40" x2="48" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="4,2"/>
    <line x1="54" y1="40" x2="64" y2="40" stroke="#333" strokeWidth="1.5" strokeDasharray="4,2"/>
    <line x1="58" y1="40" x2="80" y2="40" stroke="#333" strokeWidth="1.5"/>
    {/* Beam-forming plates — solid, exits LEFT (visual only; no wiring handle) */}
    <line x1="22" y1="52" x2="58" y2="52" stroke="#555" strokeWidth="1.5"/>
    <line x1="0"  y1="52" x2="22" y2="52" stroke="#555" strokeWidth="1.5"/>
    {/* Cathode — lead exits bottom at y=100 */}
    <line x1="28" y1="64" x2="52" y2="64" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="64" x2="40" y2="100" stroke="#333" strokeWidth="1.5"/>
    {/* Heater (visual only, no handles) */}
    <line x1="30" y1="76" x2="30" y2="100" stroke="#c53030" strokeWidth="1.5"/>
    <line x1="50" y1="76" x2="50" y2="100" stroke="#c53030" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={80} vbH={100} rotation={rotation} fontSize={8}>{label}</LabelText>
  </svg>
);

// Off-page connector / net label — circle with a filled dot at the wire terminal
const NetLabelSymbol = ({ label, rotation, labelPos, width = 40, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 40 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={40} vbH={60} rotation={rotation} fontSize={9}>{label}</LabelText>
    <circle cx="20" cy="30" r="14" fill="white" stroke="#333" strokeWidth="1.5"/>
    <line x1="20" y1="44" x2="20" y2="57" stroke="#333" strokeWidth="1.5"/>
    <circle cx="20" cy="57" r="3" fill="#333"/>
  </svg>
);

// Ground symbol
const GroundSymbol = ({ chassis, width = 40, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 40 40" style={{ pointerEvents: 'none' }}>
    <line x1="20" y1="0" x2="20" y2="16" stroke="#333" strokeWidth="1.5"/>
    <line x1="6" y1="16" x2="34" y2="16" stroke="#333" strokeWidth="2"/>
    {chassis ? (
      <>
        <line x1="10" y1="22" x2="30" y2="22" stroke="#333" strokeWidth="2"/>
        <line x1="15" y1="28" x2="25" y2="28" stroke="#333" strokeWidth="2"/>
        <line x1="19" y1="34" x2="21" y2="34" stroke="#333" strokeWidth="2"/>
      </>
    ) : (
      <>
        <line x1="12" y1="22" x2="28" y2="22" stroke="#333" strokeWidth="1.5"/>
        <line x1="16" y1="28" x2="24" y2="28" stroke="#333" strokeWidth="1.5"/>
      </>
    )}
  </svg>
);

// Power rail / B+ bus marker
// Width=60, height=40 so center x=30, bottom y=40 are both multiples of 10.
const PowerRailSymbol = ({ label, rotation, width = 60, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    <polygon points="30,2 56,22 30,38 4,22" fill="#fff3cd" stroke="#b7791f" strokeWidth="1.5"/>
    {/* Lead stub from diamond bottom vertex to node edge at y=40 */}
    <line x1="30" y1="38" x2="30" y2="40" stroke="#b7791f" strokeWidth="1.5"/>
    <text x="30" y="26" textAnchor="middle" fontSize="9" fill="#b7791f" fontWeight="bold" fontFamily="monospace" transform={lt(rotation,30,26)}>{label || 'B+'}</text>
  </svg>
);

// Potentiometer
// Height=80 so: top/bottom center handles at 50% = 40px; wiper exits right at y=20
// (25% of 80 = 20px ✓ multiple of 10).
const PotSymbol = ({ value, label, rotation, labelPos, width = 60, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 60 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="30" y1="0" x2="30" y2="15" stroke="#333" strokeWidth="1.5"/>
    <polyline points="30,15 24,20 36,25 24,30 36,35 24,40 36,45 30,45" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="30" y1="45" x2="30" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Wiper arm exits right at y=20 — handle top:25%=20px ✓ */}
    <line x1="30" y1="30" x2="50" y2="20" stroke="#333" strokeWidth="1.5"/>
    <polygon points="50,20 44,16 46,22" fill="#333"/>
    <line x1="50" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={80} rotation={rotation}>{label}{value ? ` - ${value}` : ''}</LabelText>
  </svg>
);

// Dual Triode — two triode sections in one envelope (e.g. 12AX7, 12AU7, 6SN7)
// Shape: two D-shapes back-to-back — left D curves left, right D curves right,
// flat edges face each other at x=65 and x=85 with two solid vertical dividers.
// Section A center ≈ x=50, Section B center ≈ x=100.
const DualTriodeSymbol = ({ label, value, rotation, labelPos, width = 160, height = 100 }) => (
  <svg width={width} height={height} viewBox="0 0 160 100" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Left D-shape: r=30, flat edge at x=65, arc bulges left to x=35, y=20..80 */}
    <path d="M 65,20 A 30,30 0 0 0 65,80" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="65" y1="20" x2="65" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Right D-shape: r=30, flat edge at x=95, arc bulges right to x=125, y=20..80 */}
    <path d="M 95,20 A 30,30 0 0 1 95,80" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="95" y1="20" x2="95" y2="80" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={160} vbH={100} rotation={rotation} fontSize={8}>{label}{value ? ` - ${value}` : ''}</LabelText>
    {/* === Section A (interior x=35..65, center x=50) === */}
    {/* Plate A — bar + lead to top edge (handle at top, left=31.25%=50px) */}
    <line x1="40" y1="28" x2="60" y2="28" stroke="#333" strokeWidth="2"/>
    <line x1="50" y1="0" x2="50" y2="28" stroke="#333" strokeWidth="1.5"/>
    {/* Grid A — solid lead exits left edge, dashes cross full D interior at y=50 */}
    <line x1="0" y1="50" x2="35" y2="50" stroke="#333" strokeWidth="1.5"/>
    <line x1="35" y1="50" x2="65" y2="50" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    {/* Cathode A — bar + lead to bottom edge (handle at bottom, left=31.25%=50px) */}
    <line x1="40" y1="68" x2="60" y2="68" stroke="#333" strokeWidth="2"/>
    <line x1="50" y1="68" x2="50" y2="100" stroke="#333" strokeWidth="1.5"/>
    {/* Heater A — red leads to bottom */}
    <line x1="46" y1="74" x2="46" y2="100" stroke="#c53030" strokeWidth="1.5"/>
    <line x1="54" y1="74" x2="54" y2="100" stroke="#c53030" strokeWidth="1.5"/>
    {/* === Section B (interior x=95..125, center x=110) === */}
    {/* Plate B — bar + lead to top edge (handle at top, left=68.75%=110px) */}
    <line x1="100" y1="28" x2="120" y2="28" stroke="#333" strokeWidth="2"/>
    <line x1="110" y1="0" x2="110" y2="28" stroke="#333" strokeWidth="1.5"/>
    {/* Grid B — dashes cross full D interior at y=50, solid lead exits right edge */}
    <line x1="95" y1="50" x2="125" y2="50" stroke="#333" strokeWidth="1.5" strokeDasharray="3,2"/>
    <line x1="125" y1="50" x2="160" y2="50" stroke="#333" strokeWidth="1.5"/>
    {/* Cathode B — bar + lead to bottom edge (handle at bottom, left=68.75%=110px) */}
    <line x1="100" y1="68" x2="120" y2="68" stroke="#333" strokeWidth="2"/>
    <line x1="110" y1="68" x2="110" y2="100" stroke="#333" strokeWidth="1.5"/>
    {/* Heater B — red leads to bottom */}
    <line x1="106" y1="74" x2="106" y2="100" stroke="#c53030" strokeWidth="1.5"/>
    <line x1="114" y1="74" x2="114" y2="100" stroke="#c53030" strokeWidth="1.5"/>
  </svg>
);

// ¼" TS (mono) jack — side-profile schematic symbol:
// rectangle body | tip contact line with dot | sleeve contact below
// 1/4" jack — front view of the socket face: outer ring (housing) + inner ring (plug hole),
// with tip contact lead entering at ~top-left and sleeve lead at ~bottom-left.
// The circular profile makes it immediately distinguishable from any passive component.
const QuarterInchJackSymbol = ({ label, speaker, rotation, labelPos, width = 68, height = 40 }) => {
  const accentColor = speaker ? '#b7791f' : '#1a56db';
  // Leads at y=10,30 (multiples of 10) so wires snap to the grid.
  // Circle centred at CY=20 keeps the two leads symmetric.
  const CX = 52, CY = 20, R = 15;
  const tipY = 10, slvY = 30;
  const ix = (y) => CX - Math.sqrt(R * R - (CY - y) * (CY - y));
  const tipX = Math.round(ix(tipY));
  const slvX = Math.round(ix(slvY));
  return (
    <svg width={width} height={height} viewBox="0 0 68 40" overflow="visible" style={{ pointerEvents: 'none' }}>
      <LabelText side={labelPos} vbW={68} vbH={40} rotation={rotation} fill={accentColor}>{label}</LabelText>
      {/* Tip lead — exits left, colored */}
      <line x1="0" y1={tipY} x2={tipX} y2={tipY} stroke={accentColor} strokeWidth="1.5"/>
      {/* Sleeve lead — exits left below */}
      <line x1="0" y1={slvY} x2={slvX} y2={slvY} stroke="#333" strokeWidth="1.5"/>
      {/* Outer housing ring */}
      <circle cx={CX} cy={CY} r={R} fill="#f0f4f8" stroke="#333" strokeWidth="1.5"/>
      {/* Inner plug-hole ring */}
      <circle cx={CX} cy={CY} r="5" fill="white" stroke="#555" strokeWidth="1.2"/>
      {/* Type hint */}
      <text x={CX - 4} y={CY + 2} fontSize="5" fontFamily="monospace" fill="#888"
            textAnchor="middle" transform={lt(rotation, CX, CY)}>{speaker ? 'SPK' : 'IN'}</text>
      {/* "tip" label near the tip lead */}
      <text x="2" y={tipY - 2} fontSize="6" fontFamily="monospace" fontWeight="bold"
            fill={accentColor} transform={lt(rotation, 2, tipY - 2)}>tip</text>
    </svg>
  );
};

// Standard TS (mono) jack — classic schematic symbol used on most amplifier
// schematics: a vertical barrel line on the right, a diagonal tip-spring contact
// coming from the upper-left, and a horizontal sleeve lead at the bottom.
// Leads at y=10 (tip) and y=30 (sleeve), height=40 — all grid-aligned.
const StandardJackSymbol = ({ label, speaker, rotation, labelPos, width = 50, height = 40 }) => {
  const accentColor = speaker ? '#b7791f' : '#1a56db';
  return (
    <svg width={width} height={height} viewBox="0 0 50 40" overflow="visible" style={{ pointerEvents: 'none' }}>
      <LabelText side={labelPos} vbW={50} vbH={40} rotation={rotation} fill={accentColor}>{label}</LabelText>
      {/* Tip lead — horizontal from left */}
      <line x1="0" y1="10" x2="20" y2="10" stroke={accentColor} strokeWidth="1.5"/>
      {/* Tip spring contact — angled down toward barrel */}
      <line x1="20" y1="10" x2="38" y2="26" stroke={accentColor} strokeWidth="1.5"/>
      {/* Sleeve lead — horizontal from left to barrel */}
      <line x1="0" y1="30" x2="40" y2="30" stroke="#333" strokeWidth="1.5"/>
      {/* Barrel / body — vertical line */}
      <line x1="40" y1="6" x2="40" y2="34" stroke="#333" strokeWidth="6"/>
      {/* "tip" label */}
      <text x="2" y="8" fontSize="6" fontFamily="monospace" fontWeight="bold"
            fill={accentColor} transform={lt(rotation, 2, 8)}>tip</text>
    </svg>
  );
};

// Output / power transformer — two coupled inductors with iron core
// Primary (left):  plate-a top(y=10), B+CT mid(y=40), plate-b bottom(y=70)
// Secondary (right): 16Ω y=10, 8Ω y=30, 4Ω y=50, Common y=70
// Width=160, height=80. Primary backbone x=28 (bumps reach x=38, 5px from core).
// Secondary backbone x=64 (bumps reach x=54, 5px from core) — symmetric spacing.
const TransformerSymbol = ({ label, rotation, labelPos, width = 160, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 160 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={160} vbH={80} rotation={rotation}>{label}</LabelText>
    {/* Primary winding — 3 bumps r=10 bulging RIGHT, backbone at x=28, y=10→70 */}
    <path d="M 28,10 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20"
      fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Secondary winding — 3 bumps r=10 bulging LEFT, backbone at x=64, y=10→70 */}
    <path d="M 64,10 a 10 10 0 0 0 0 20 a 10 10 0 0 0 0 20 a 10 10 0 0 0 0 20"
      fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Iron core — two vertical lines */}
    <line x1="43" y1="5" x2="43" y2="75" stroke="#333" strokeWidth="1.5"/>
    <line x1="49" y1="5" x2="49" y2="75" stroke="#333" strokeWidth="1.5"/>
    {/* Primary leads — exit left at x=0 */}
    <line x1="0" y1="10" x2="28" y2="10" stroke="#333" strokeWidth="1.5"/>{/* Plate A */}
    <line x1="0" y1="40" x2="14" y2="40" stroke="#333" strokeWidth="1.5"/>{/* B+ CT */}
    <line x1="0" y1="70" x2="28" y2="70" stroke="#333" strokeWidth="1.5"/>{/* Plate B */}
    <text x="1" y="38" fontSize="5" fill="#b7791f" fontFamily="sans-serif" transform={lt(rotation,1,38)}>B+CT</text>
    {/* Secondary taps — exit right at x=160 */}
    <line x1="64" y1="10" x2="160" y2="10" stroke="#333" strokeWidth="1.5"/>{/* 16Ω */}
    <circle cx="152" cy="10" r="2.5" fill="white" stroke="#333" strokeWidth="1"/>
    <text x="136" y="8" fontSize="6" fill="#555" fontFamily="sans-serif" transform={lt(rotation,136,8)}>16Ω</text>
    <line x1="64" y1="30" x2="160" y2="30" stroke="#333" strokeWidth="1.5"/>{/* 8Ω */}
    <circle cx="152" cy="30" r="2.5" fill="white" stroke="#333" strokeWidth="1"/>
    <text x="136" y="28" fontSize="6" fill="#555" fontFamily="sans-serif" transform={lt(rotation,136,28)}>8Ω</text>
    <line x1="64" y1="50" x2="160" y2="50" stroke="#333" strokeWidth="1.5"/>{/* 4Ω */}
    <circle cx="152" cy="50" r="2.5" fill="white" stroke="#333" strokeWidth="1"/>
    <text x="136" y="48" fontSize="6" fill="#555" fontFamily="sans-serif" transform={lt(rotation,136,48)}>4Ω</text>
    <line x1="64" y1="70" x2="160" y2="70" stroke="#333" strokeWidth="1.5"/>{/* Common */}
    <text x="130" y="78" fontSize="6" fill="#888" fontFamily="sans-serif" transform={lt(rotation,130,78)}>COM</text>
  </svg>
);

// Power Transformer
// windings=2: HV secondary (with CT) + 6.3V filament secondary
// windings=3: HV secondary (with CT) + 5V rectifier heater + 6.3V filament
// Width=160, height=120 for both variants so all pin y-values are exact multiples of 10.
//   Left — secondary:
//     hv-a=10  hv-ct=30  hv-b=50
//     (3W) rect-fil-a=60  rect-fil-b=80  fil-a=90  fil-b=110
//     (2W) fil-a=60  fil-b=80
//   Right — primary voltage taps (wall-voltage selector):
//     pri-110v=10  pri-120v=30  pri-200v=50  pri-220v=70  pri-240v=90  pri-com=110
const PowerTransformerSymbol = ({ label, windings = 2, rotation, labelPos, width = 160, height, hvVoltage, filVoltage, rectVoltage }) => {
  const h = height || 120;
  const vbH = 120;
  return (
    <svg width={width} height={h} viewBox={`0 0 160 ${vbH}`} overflow="visible" style={{ pointerEvents: 'none' }}>
      <LabelText side={labelPos} vbW={160} vbH={vbH} rotation={rotation}>{label}</LabelText>
      {/* Iron core */}
      <line x1="66" y1="4" x2="66" y2="114" stroke="#333" strokeWidth="1.5"/>
      <line x1="72" y1="4" x2="72" y2="114" stroke="#333" strokeWidth="1.5"/>
      {/* HV Secondary: 4 bumps bulging LEFT, y=10→50, CT at y=30 */}
      <path d="M 52,10 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10"
        fill="none" stroke="#333" strokeWidth="1.5"/>
      <line x1="0"  y1="10" x2="52" y2="10" stroke="#333" strokeWidth="1.5"/>
      <line x1="0"  y1="30" x2="26" y2="30" stroke="#333" strokeWidth="1.5"/>
      <line x1="0"  y1="50" x2="52" y2="50" stroke="#333" strokeWidth="1.5"/>
      <text x="1" y="28" fontSize="5" fill="#b7791f" fontFamily="sans-serif" transform={lt(rotation,1,28)}>{hvVoltage ? `${hvVoltage}V CT` : 'HV CT'}</text>
      {windings === 3 ? (
        <>
          {/* 5V rectifier heater: 2 bumps, y=60→80 */}
          <path d="M 52,60 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10"
            fill="none" stroke="#333" strokeWidth="1.5"/>
          <line x1="0" y1="60" x2="52" y2="60" stroke="#c53030" strokeWidth="1.5"/>
          <line x1="0" y1="80" x2="52" y2="80" stroke="#c53030" strokeWidth="1.5"/>
          <text x="2" y="74" fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,2,74)}>{rectVoltage ? `${rectVoltage}V Rect` : '5V Rect'}</text>
          {/* Filament winding: 2 bumps, y=90→110 */}
          <path d="M 52,90 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10"
            fill="none" stroke="#333" strokeWidth="1.5"/>
          <line x1="0" y1="90"  x2="52" y2="90"  stroke="#c53030" strokeWidth="1.5"/>
          <line x1="0" y1="110" x2="52" y2="110" stroke="#c53030" strokeWidth="1.5"/>
          <text x="2" y="104" fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,2,104)}>{filVoltage ? `${filVoltage}V` : '6.3V'}</text>
        </>
      ) : (
        <>
          {/* Filament winding: 2 bumps, y=60→80 */}
          <path d="M 52,60 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10"
            fill="none" stroke="#333" strokeWidth="1.5"/>
          <line x1="0" y1="60" x2="52" y2="60" stroke="#c53030" strokeWidth="1.5"/>
          <line x1="0" y1="80" x2="52" y2="80" stroke="#c53030" strokeWidth="1.5"/>
          <text x="2" y="74" fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,2,74)}>{filVoltage ? `${filVoltage}V` : '6.3V'}</text>
        </>
      )}
      {/* Primary: 4 bumps bulging RIGHT, y=10→90; five voltage taps + COM */}
      <path d="M 84,10 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20"
        fill="none" stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="10"  x2="160" y2="10"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="30"  x2="160" y2="30"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="50"  x2="160" y2="50"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="70"  x2="160" y2="70"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="90"  x2="160" y2="90"  stroke="#333" strokeWidth="1.5"/>
      {/* Common/neutral — dashed, separate from voltage taps */}
      <line x1="84" y1="110" x2="160" y2="110" stroke="#777" strokeWidth="1.5" strokeDasharray="4 2"/>
      {/* Primary voltage tap labels — x and dy are rotation-aware:
          rotation=0:   px=96  dy=-2  → text 2px above each lead, clear of arcs
          rotation=180: px=112 dy=+2  → after the CSS flip, text is 2px above each
                        lead (screen-space) and the reflected text body (x=100–112)
                        maps to screen x=48–60, 6px clear of the arc max at x=66. */}
      {(() => {
        const px = rotation === 180 ? 112 : 96;
        const dy = rotation === 180 ? 2 : -2;
        return (<>
          <text x={px} y={10+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,10+dy)}>110V</text>
          <text x={px} y={30+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,30+dy)}>120V</text>
          <text x={px} y={50+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,50+dy)}>200V</text>
          <text x={px} y={70+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,70+dy)}>220V</text>
          <text x={px} y={90+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,90+dy)}>240V</text>
          <text x={px} y={110+dy} fontSize="5" fill="#777" fontFamily="monospace" transform={lt(rotation,px,110+dy)}>COM</text>
        </>);
      })()}
    </svg>
  );
};

// Diode — standard schematic symbol: triangle (anode) + cathode bar, horizontal
// Height=40 so center y=20 (multiple of 10) for grid-aligned left/right handles.
const DiodeSymbol = ({ label, rotation, labelPos, width = 30, height = 20 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Anode lead */}
    <line x1="0" y1="20" x2="14" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Triangle body (anode→cathode) */}
    <polygon points="14,8 14,32 42,20" fill="#333" stroke="#333" strokeWidth="1.5"/>
    {/* Cathode bar */}
    <line x1="42" y1="8" x2="42" y2="32" stroke="#333" strokeWidth="2"/>
    {/* Cathode lead */}
    <line x1="42" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Fuse — ANSI/IEEE wavy S-curve fuse element with leads exiting left and right
// Height=40 so center y=20 (multiple of 10) for grid-aligned left/right handles.
const FuseSymbol = ({ label, rotation, labelPos, width = 60, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Left lead */}
    <line x1="0" y1="20" x2="10" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Fuse element — double S-curve */}
    <path d="M 10 20 C 15 10 20 10 25 20 C 30 30 35 30 40 20 C 45 10 50 10 50 20"
          fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Right lead */}
    <line x1="50" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Switch — SPST normally-open; contact arm angled upward to show open state
// Height=40 so center y=20 (multiple of 10) for grid-aligned left/right handles.
const SwitchSymbol = ({ label, rotation, labelPos, width = 60, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Left lead */}
    <line x1="0" y1="20" x2="14" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Left terminal pivot */}
    <circle cx="14" cy="20" r="2" fill="#333"/>
    {/* Contact arm — angled open */}
    <line x1="14" y1="20" x2="44" y2="8" stroke="#333" strokeWidth="1.5"/>
    {/* Right terminal */}
    <circle cx="46" cy="20" r="2" fill="#333"/>
    {/* Right lead */}
    <line x1="46" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Impedance Switch — SP3T rotary; three input taps (from OT secondary) on left,
// common (pole) exits right toward speaker.  Height=60: tap y-positions 10/30/50
// match the output transformer secondary tap spacing exactly (20 px apart) so
// wires connect straight across when placed side-by-side.  Arm is open (angled
// between 16 Ω and 8 Ω contacts) to make it obvious this is a switch.
const ImpedanceSwitchSymbol = ({ label, rotation, labelPos, width = 80, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 80 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Input leads (from OT secondary) — exit left; order matches transformer: 16Ω top, 4Ω bottom */}
    <line x1="0" y1="10" x2="22" y2="10" stroke="#333" strokeWidth="1.5"/>{/* 16Ω */}
    <line x1="0" y1="30" x2="22" y2="30" stroke="#333" strokeWidth="1.5"/>{/* 8Ω */}
    <line x1="0" y1="50" x2="22" y2="50" stroke="#333" strokeWidth="1.5"/>{/* 4Ω */}
    {/* Contact dots — all open circles (arm not engaged on any) */}
    <circle cx="22" cy="10" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="30" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="50" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Common lead (to speaker) — exits right */}
    <line x1="58" y1="30" x2="80" y2="30" stroke="#333" strokeWidth="1.5"/>
    {/* Pivot dot */}
    <circle cx="58" cy="30" r="2.5" fill="#333"/>
    {/* Switch arm — open: angles left-upward, ending between 16 Ω and 8 Ω contacts */}
    <line x1="58" y1="30" x2="36" y2="19" stroke="#333" strokeWidth="1.5"/>
    {/* Position labels — in the gap between contact dots and pivot */}
    <text x="25" y="8"  fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,8)}>16Ω</text>
    <text x="25" y="28" fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,28)}>8Ω</text>
    <text x="25" y="48" fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,48)}>4Ω</text>
    <LabelText side={labelPos} vbW={80} vbH={60} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Voltage Selector Switch — SP5T rotary; five input taps match power-transformer
// primary tap y-positions (20 px apart) so wires connect straight across when
// placed side-by-side.  Common (pole) exits right at y=50 (center of the five taps).
// Arm is open (angled between 110 V and 120 V contacts) to make it clear.
const VoltageSwitchSymbol = ({ label, rotation, labelPos, width = 80, height = 100 }) => (
  <svg width={width} height={height} viewBox="0 0 80 100" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Input leads (from PT primary taps) — exit left */}
    <line x1="0" y1="10" x2="22" y2="10" stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="30" x2="22" y2="30" stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="50" x2="22" y2="50" stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="70" x2="22" y2="70" stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="90" x2="22" y2="90" stroke="#333" strokeWidth="1.5"/>
    {/* Contact dots — open circles */}
    <circle cx="22" cy="10" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="30" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="50" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="70" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="90" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Common lead (to mains / wall power) — exits right at centre */}
    <line x1="58" y1="50" x2="80" y2="50" stroke="#333" strokeWidth="1.5"/>
    {/* Pivot dot */}
    <circle cx="58" cy="50" r="2.5" fill="#333"/>
    {/* Switch arm — open: angles left-upward, ending between 110 V and 120 V contacts */}
    <line x1="58" y1="50" x2="36" y2="19" stroke="#333" strokeWidth="1.5"/>
    {/* Position labels — rotation-aware:
        rotation=0:   px=25 dy=-2  → text 2px above each lead, right of contact dots (x≈24.5)
        rotation=180: px=12 dy=+2  → screen-x=68, 10px clear of reflected dots (screen-x=58) */}
    {(() => {
      const px = rotation === 180 ? 12 : 25;
      const dy = rotation === 180 ? 2 : -2;
      return (<>
        <text x={px} y={10+dy} fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,px,10+dy)}>110V</text>
        <text x={px} y={30+dy} fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,px,30+dy)}>120V</text>
        <text x={px} y={50+dy} fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,px,50+dy)}>200V</text>
        <text x={px} y={70+dy} fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,px,70+dy)}>220V</text>
        <text x={px} y={90+dy} fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,px,90+dy)}>240V</text>
      </>);
    })()}
    <LabelText side={labelPos} vbW={80} vbH={100} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Bridge rectifier — 4 diodes in diamond; AC inputs left/right; DC+ top; DC− bottom
// Stub lines connect diamond corner vertices to the node edges so drawn wires touch.
const BridgeRectifierSymbol = ({ label, rotation, labelPos, width = 90, height = 90 }) => (
  <svg width={width} height={height} viewBox="0 0 90 90" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Lead stubs from node edges to diamond corners */}
    <line x1="45" y1="0"  x2="45" y2="5"  stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="45" x2="5"  y2="45" stroke="#333" strokeWidth="1.5"/>
    <line x1="85" y1="45" x2="90" y2="45" stroke="#333" strokeWidth="1.5"/>
    <line x1="45" y1="85" x2="45" y2="90" stroke="#333" strokeWidth="1.5"/>
    {/* Diamond sides */}
    <line x1="5"  y1="45" x2="45" y2="5"  stroke="#333" strokeWidth="1.5"/>
    <line x1="85" y1="45" x2="45" y2="5"  stroke="#333" strokeWidth="1.5"/>
    <line x1="45" y1="85" x2="5"  y2="45" stroke="#333" strokeWidth="1.5"/>
    <line x1="45" y1="85" x2="85" y2="45" stroke="#333" strokeWidth="1.5"/>
    {/* Corner dots */}
    <circle cx="45" cy="5"  r="2.5" fill="#333"/>
    <circle cx="5"  cy="45" r="2.5" fill="#333"/>
    <circle cx="85" cy="45" r="2.5" fill="#333"/>
    <circle cx="45" cy="85" r="2.5" fill="#333"/>
    {/* D1 — anode=left(5,45), cathode=top(45,5): triangle pointing up-right, mid=(25,25) */}
    <polygon points="30,20 26,32 18,24" fill="#333"/>
    <line x1="26" y1="16" x2="34" y2="24" stroke="#333" strokeWidth="1.5"/>
    {/* D2 — anode=right(85,45), cathode=top(45,5): triangle pointing up-left, mid=(65,25) */}
    <polygon points="60,20 72,24 64,32" fill="#333"/>
    <line x1="56" y1="24" x2="64" y2="16" stroke="#333" strokeWidth="1.5"/>
    {/* D3 — anode=bottom(45,85), cathode=left(5,45): triangle pointing up-left, mid=(25,65) */}
    <polygon points="20,60 32,64 24,72" fill="#333"/>
    <line x1="16" y1="64" x2="24" y2="56" stroke="#333" strokeWidth="1.5"/>
    {/* D4 — anode=bottom(45,85), cathode=right(85,45): triangle pointing up-right, mid=(65,65) */}
    <polygon points="70,60 66,72 58,64" fill="#333"/>
    <line x1="66" y1="56" x2="74" y2="64" stroke="#333" strokeWidth="1.5"/>
    {/* Terminal labels */}
    <text x="45" y="3"  textAnchor="middle" fontSize="6" fill="#276749" fontFamily="monospace" fontWeight="bold" transform={lt(rotation,45,3)}>B+</text>
    <text x="1"  y="48" fontSize="6" fill="#666" fontFamily="monospace" transform={lt(rotation,1,48)}>~</text>
    <text x="80" y="48" fontSize="6" fill="#666" fontFamily="monospace" transform={lt(rotation,80,48)}>~</text>
    <LabelText side={labelPos} vbW={90} vbH={90} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Rectifier tube (5AR4 / GZ34 / 5Y3) — full-wave vacuum rectifier
// Height=100 so all handle left positions land on 10px grid multiples:
//   plate-a  left:25%  = 20px  plate-b  left:75%  = 60px
//   cathode  left:50%  = 40px
//   heater-1 left:37.5%= 30px  heater-2 left:62.5%= 50px
// Extra 10px of space below the envelope (bottom at y≈76) so the heater
// leads are clearly visible before reaching the node bottom edge at y=100.
const RectifierTubeSymbol = ({ label, rotation, labelPos, width = 80, height = 110 }) => (
  <svg width={width} height={height} viewBox="0 0 80 110" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Envelope */}
    <circle cx="40" cy="46" r="30" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Plate A — vertical bar at x=20; lead exits top at y=0 */}
    <line x1="20" y1="22" x2="20" y2="36" stroke="#333" strokeWidth="2"/>
    <line x1="20" y1="0"  x2="20" y2="22" stroke="#333" strokeWidth="1.5"/>
    {/* Plate B — vertical bar at x=60; lead exits top at y=0 */}
    <line x1="60" y1="22" x2="60" y2="36" stroke="#333" strokeWidth="2"/>
    <line x1="60" y1="0"  x2="60" y2="22" stroke="#333" strokeWidth="1.5"/>
    {/* Cathode bar at y=46; B+ lead exits top at x=40 y=0 */}
    <line x1="26" y1="46" x2="54" y2="46" stroke="#333" strokeWidth="2.5"/>
    <line x1="40" y1="0"  x2="40" y2="46" stroke="#333" strokeWidth="1.5"/>
    {/* Cathode arrow — downward triangle */}
    <polygon points="30,46 50,46 40,60" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Heater leads at x=30, x=50 — exit bottom at y=110 (separate, unconnected) */}
    <line x1="30" y1="74" x2="30" y2="110" stroke="#c53030" strokeWidth="1.5"/>
    <line x1="50" y1="74" x2="50" y2="110" stroke="#c53030" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={80} vbH={110} rotation={rotation}>{label}</LabelText>
  </svg>
);

// ─── Custom React Flow Node Types ─────────────────────────────────────────────

// Returns the logical handle Position after rotating CW by `deg` degrees.
// When a node wrapper is CSS-rotated, the handles move visually but their
// declared `position` prop stays the same, so React Flow reports the wrong
// sourcePosition/targetPosition to the edge and the wire stub exits the wrong
// face.  Pass every Handle's base position through rotatePos(pos, data.rotation)
// to keep the declared position in sync with the visual face.
const rotatePos = (pos, deg = 0) => {
  const order = [Position.Top, Position.Right, Position.Bottom, Position.Left];
  const steps = ((Math.round((deg || 0) / 90)) % 4 + 4) % 4;
  return order[(order.indexOf(pos) + steps) % 4];
};

// CSS position properties for each base handle position.
//
// React Flow's Handle component uses the `position` prop for TWO purposes:
//  1. It applies the CSS class `react-flow__handle-{position}` which places
//     the handle dot at the correct edge of the div.
//  2. It writes `data-handlepos="{position}"` which xyflow reads during
//     updateNodeInternals to decide which edge-formula to use when computing
//     sourceX/Y from the handle's bounding rect:
//       Top    → { x: x+w/2, y }          (top edge, centered x)
//       Right  → { x: x+w,   y: y+h/2 }   (right edge, centered y)
//       Bottom → { x: x+w/2, y: y+h }     (bottom edge, centered x)
//       Left   → { x,        y: y+h/2 }   (left edge, centered y)
//
// After CSS rotation of the wrapper div, the handle moves visually to a new
// face, but `data-handlepos` still reflects the original face.  The wrong
// formula fires and the computed sourceX/Y is off by half the handle size
// (~2.5 px) in the wrong axis — producing a visible perpendicular gap.
//
// Fix: use FixedHandle (below) which passes `position=rotatePos(base, r)` so
// xyflow uses the correct formula AND overrides the CSS class layout back to
// the base-position values so the div rotation remains the only thing moving
// the handle visually (no double-rotation).
const HANDLE_CSS = {
  // Transform places the 0×0 handle centre exactly on the node edge so
  // wire endpoints connect flush with the SVG lead tips.  With width/height
  // collapsed to 0 via global CSS, translate(-50%) is a no-op (−0), so we
  // keep the original transforms for the rare case CSS doesn't apply.
  [Position.Top]:    { top: 0,      left: '50%',  right: 'auto', bottom: 'auto', transform: 'translate(-50%, 0)' },
  [Position.Bottom]: { top: 'auto', left: '50%',  right: 'auto', bottom: 0,      transform: 'translate(-50%, 0)'  },
  [Position.Left]:   { top: '50%',  left: 0,      right: 'auto', bottom: 'auto', transform: 'translate(0, -50%)' },
  [Position.Right]:  { top: '50%',  left: 'auto', right: 0,      bottom: 'auto', transform: 'translate(0, -50%)'  },
};

// Drop-in replacement for Handle that keeps the visual placement (via CSS
// override) at the BASE position while telling xyflow the ROTATED position
// (via the `position` prop / data-handlepos attribute).
const FixedHandle = ({ basePosition, rotation = 0, style: extraStyle = {}, ...rest }) => (
  <Handle
    {...rest}
    position={rotatePos(basePosition, rotation)}
    style={{ ...HANDLE_CSS[basePosition], ...extraStyle }}
  />
);

// Shared wrapper style
const nwStyle = (sel, rot, bg = 'transparent') => ({
  // Use outline (not border) for the selection indicator so it sits *outside*
  // the layout box and does not inflate the div's content dimensions.  Any
  // border would make the div larger than the SVG, shifting the SVG lead tips
  // away from the handle-dot centres after CSS rotation (the transform rotates
  // around the centre of the inflated box, not the SVG centre).
  outline: sel ? '2px solid #2563eb' : '2px solid transparent',
  outlineOffset: '0px',
  borderRadius: 6,
  background: bg,
  cursor: 'pointer',
  position: 'relative',
  lineHeight: 0,      // prevent inline-SVG descender gap that pushes node div taller than SVG
  overflow: 'visible', // allow SVG labels that sit outside the viewBox to render
  ...(rot ? { transform: `rotate(${rot}deg)`, transformOrigin: 'center center' } : {}),
});

// Stores resize result in node data so the symbol can re-render at the new size.
// Also calls updateNodeInternals so xyflow re-measures handle positions immediately
// rather than waiting for the async ResizeObserver — stale bounds after a resize
// are what causes connections to snap to the wrong handle.
const useResizeInData = (id) => {
  const { setNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  return useCallback((_, { width, height }) => {
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, width, height } } : n));
    // Defer by one frame so the new SVG dimensions are in the DOM before measuring.
    requestAnimationFrame(() => updateNodeInternals(id));
  }, [id, setNodes, updateNodeInternals]);
};

// Re-measure handle DOM positions after rotation changes.
// Must live INSIDE each node component (inside the ReactFlow context) and use
// useEffect so it fires after React has committed the new CSS rotation to the
// DOM — only at that point is getBoundingClientRect() returning correct values.
const useUpdateAfterRotation = (id, rotation) => {
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, rotation]);
};

// Freely-draggable label decoupled from the component's CSS rotation.
// labelDx / labelDy are offsets (flow px) from the component centre.
// Defaults to 14 px above the component top edge when not yet set.
const FloatingLabel = ({ nodeId, label, value, rating, stacked, dx, dy, defaultH = 60, selected }) => {
  const { setNodes, getViewport } = useReactFlow();
  const { labelFontSize = 7 } = React.useContext(DiagramSettingsCtx) || {};
  const isStacked = stacked && value;
  // Inline: "R1 - 470k 2W"  /  Stacked: three lines
  const ratingStr = rating ? ` ${rating}` : '';
  const text = isStacked ? label : (value ? `${label} - ${value}${ratingStr}` : `${label}${ratingStr}`);
  if (!text) return null;
  const rx = dx ?? 0;
  const ry = dy ?? -(Math.round((defaultH || 60) / 2) + 14);

  const handlePointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault(); // prevent text selection; no stopPropagation so RF can still select the node
    const { zoom } = getViewport();
    const sx = e.clientX, sy = e.clientY;
    const onMove = (ev) => {
      setNodes(nds => nds.map(n =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, labelDx: rx + (ev.clientX - sx) / zoom, labelDy: ry + (ev.clientY - sy) / zoom } }
          : n
      ));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  return (
    <div
      className="nodrag"
      onPointerDown={handlePointerDown}
      style={{
        position: 'absolute',
        left: `calc(50% + ${rx}px)`,
        top: `calc(50% + ${ry}px)`,
        transform: 'translate(-50%, -50%)',
        fontSize: Math.round(11 * labelFontSize / 7),
        fontFamily: 'monospace',
        fontWeight: 'bold',
        color: '#1a56db',
        cursor: 'grab',
        userSelect: 'none',
        pointerEvents: 'all',
        whiteSpace: isStacked ? 'normal' : 'nowrap',
        textAlign: isStacked ? 'center' : undefined,
        padding: '1px 3px',
        borderRadius: 3,
        background: selected ? 'rgba(37,99,235,0.08)' : 'transparent',
        border: selected ? '1px dashed rgba(37,99,235,0.35)' : '1px solid transparent',
        lineHeight: 1.3,
        zIndex: 10,
      }}
    >
      {isStacked ? (
        <>
          <div>{label}</div>
          <div style={{ fontWeight: 'normal' }}>{value}</div>
          {rating && <div style={{ fontWeight: 'normal' }}>{rating}</div>}
        </>
      ) : text}
    </div>
  );
};

const IronCoreInductorNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"    style={{ background: '#555' }} />
        <IronCoreInductorSymbol rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const ResistorNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"    style={{ background: '#555' }} />
        <ResistorSymbol rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} rating={data.rating} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const CapacitorNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"    style={{ background: '#555' }} />
        <CapacitorSymbol polar={data.polar} rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} rating={data.rating} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const TriodeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="grid" style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Top} rotation={r} id="plate" style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="cathode" style={{ background: '#555' }} />
        <TriodeSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const PentodeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="grid" style={{ background: '#555', top: '40%' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="screen" style={{ background: '#888', top: '30%' }} />
        <FixedHandle type="source" basePosition={Position.Top} rotation={r} id="plate" style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="cathode" style={{ background: '#555' }} />
        <PentodeSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const BeamTetrodeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="grid"   style={{ background: '#555', top: '30%' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="screen" style={{ background: '#888', top: '40%' }} />
        <FixedHandle type="source" basePosition={Position.Top}   rotation={r} id="plate"   style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="cathode" style={{ background: '#555' }} />
        <BeamTetrodeSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// Off-page connector — single bidirectional connection at the dot (bottom)
const NetLabelNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r, 'transparent')}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="out" style={{ background: '#555', top: '50%' }} />
        <NetLabelSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const GroundNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={nwStyle(selected, r)}>
      <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
      <FixedHandle type="target" basePosition={Position.Top} rotation={r} id="top" style={{ background: '#555' }} />
      <GroundSymbol chassis={data.chassis} width={data.width} height={data.height} />
    </div>
  );
};

const PowerRailNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={nwStyle(selected, r, 'transparent')}>
      <NodeResizer isVisible={selected} minWidth={20} minHeight={15} onResize={onResize} />
      <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#b7791f' }} />
      <PowerRailSymbol label={data.label} rotation={r} width={data.width} height={data.height} />
    </div>
  );
};

const PotNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"    style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Right}  rotation={r} id="wiper"  style={{ background: '#555', top: '25%' }} />
        <PotSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// Note / annotation box
const NoteNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  return (
    <div style={{
      border: selected ? '2px solid #2563eb' : '1px dashed #999',
      borderRadius: 6,
      background: '#fffde7',
      padding: '8px 12px',
      minWidth: 120,
      fontSize: 12,
      color: '#555',
      cursor: 'pointer',
      whiteSpace: 'pre-wrap',
      position: 'relative',
    }}>
      <NodeResizer isVisible={selected} minWidth={80} minHeight={40} onResize={onResize} />
      {data.text || 'Double-click to edit'}
    </div>
  );
};

const DualTriodeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={50} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="grid-a"   style={{ background: '#555', top: '50%' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="grid-b"   style={{ background: '#555', top: '50%' }} />
        <FixedHandle type="source" basePosition={Position.Top}   rotation={r} id="plate-a"  style={{ background: '#555', left: '31.25%' }} />
        <FixedHandle type="source" basePosition={Position.Top}   rotation={r} id="plate-b"  style={{ background: '#555', left: '68.75%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="cathode-a" style={{ background: '#555', left: '31.25%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="cathode-b" style={{ background: '#555', left: '68.75%' }} />
        <DualTriodeSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const QuarterInchJackNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="tip"    style={{ background: '#1a56db', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="sleeve" style={{ background: '#555',    top: '30px' }} />
        <QuarterInchJackSymbol speaker={false} rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const SpeakerJackNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="tip"    style={{ background: '#b7791f', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="sleeve" style={{ background: '#555',    top: '30px' }} />
        <QuarterInchJackSymbol speaker={true} rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const StandardInputJackNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="tip"    style={{ background: '#1a56db', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="sleeve" style={{ background: '#555',    top: '30px' }} />
        <StandardJackSymbol speaker={false} rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const StandardSpeakerJackNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="tip"    style={{ background: '#b7791f', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="sleeve" style={{ background: '#555',    top: '30px' }} />
        <StandardJackSymbol speaker={true} rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const TransformerNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={50} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="plate-a" style={{ background: '#555',    top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="ct"      style={{ background: '#b7791f', top: '40px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="plate-b" style={{ background: '#555',    top: '70px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="tap-16" style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="tap-8"  style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="tap-4"  style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="common" style={{ background: '#555', top: '70px' }} />
        <TransformerSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const PowerTransformerNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const w = data.windings === 3 ? 3 : 2;
  useUpdateAfterRotation(id, data.rotation);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, data.rotation)}>
        <NodeResizer isVisible={selected} minWidth={50} minHeight={40} onResize={onResize} />
        {/* HV Secondary — left, always present; pixel top values = multiples of 10 */}
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="hv-a"  style={{ background: '#555',    top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="hv-ct" style={{ background: '#b7791f', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="hv-b"  style={{ background: '#555',    top: '50px' }} />
        {/* 5V rectifier heater — only on 3-winding */}
        {w === 3 && <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="rect-fil-a" style={{ background: '#c53030', top: '60px' }} />}
        {w === 3 && <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="rect-fil-b" style={{ background: '#c53030', top: '80px' }} />}
        {/* 6.3V tube filament */}
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="fil-a" style={{ background: '#c53030', top: w === 3 ? '90px' : '60px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="fil-b" style={{ background: '#c53030', top: w === 3 ? '110px' : '80px' }} />
        {/* Primary (mains) — right side; five voltage taps + common */}
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-110v"  style={{ background: '#555',    top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-120v"  style={{ background: '#555',    top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-200v"  style={{ background: '#555',    top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-220v"  style={{ background: '#555',    top: '70px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-240v"  style={{ background: '#555',    top: '90px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-com"   style={{ background: '#777',    top: '110px' }} />
        <PowerTransformerSymbol windings={w} rotation={data.rotation} width={data.width} height={data.height} hvVoltage={data.hv_voltage} filVoltage={data.fil_voltage} rectVoltage={data.rect_voltage} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const RectifierTubeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Top} rotation={r} id="plate-a" style={{ background: '#555', left: '25%' }} />
        <FixedHandle type="source" basePosition={Position.Top} rotation={r} id="cathode" style={{ background: '#276749', left: '50%' }} />
        <FixedHandle type="target" basePosition={Position.Top} rotation={r} id="plate-b" style={{ background: '#555', left: '75%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="heater-1" style={{ background: '#c53030', left: '37.5%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="heater-2" style={{ background: '#c53030', left: '62.5%' }} />
        <RectifierTubeSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const DiodeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={10} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="anode"   style={{ background: '#555' }} />
        <DiodeSymbol rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="cathode" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const BridgeRectifierNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="ac-1"     style={{ background: '#555' }} />
        <FixedHandle type="target" basePosition={Position.Right}  rotation={r} id="ac-2"     style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="dc-plus"  style={{ background: '#276749' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="dc-minus" style={{ background: '#333' }} />
        <BridgeRectifierSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const FuseNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={10} onResize={onResize} />
        {/* Passive bidirectional component — both ends accept source and target connections */}
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="a-in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="a-out" style={{ background: '#555', top: '50%' }} />
        <FuseSymbol rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="b-in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="b-out" style={{ background: '#555', top: '50%' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const SwitchNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={10} onResize={onResize} />
        {/* Passive bidirectional component — both ends accept source and target connections */}
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="a-in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="a-out" style={{ background: '#555', top: '50%' }} />
        <SwitchSymbol rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="b-in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="b-out" style={{ background: '#555', top: '50%' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const ImpedanceSwitchNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={30} onResize={onResize} />
        {/* Input taps from OT secondary — left side, y-positions match transformer tap-16/8/4 handles */}
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="16ohm-in"  style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="16ohm-out" style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="8ohm-in"   style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="8ohm-out"  style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="4ohm-in"   style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="4ohm-out"  style={{ background: '#555', top: '50px' }} />
        <ImpedanceSwitchSymbol rotation={r} width={data.width} height={data.height} />
        {/* Common output (pole) — right side, centre of the three taps */}
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="common-out" style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="common-in"  style={{ background: '#555', top: '30px' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const VoltageSwitchNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={50} onResize={onResize} />
        {/* Input taps from PT primary — left side, y-positions match transformer pri-* handles */}
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="110v-in"  style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="110v-out" style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="120v-in"  style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="120v-out" style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="200v-in"  style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="200v-out" style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="220v-in"  style={{ background: '#555', top: '70px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="220v-out" style={{ background: '#555', top: '70px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="240v-in"  style={{ background: '#555', top: '90px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="240v-out" style={{ background: '#555', top: '90px' }} />
        <VoltageSwitchSymbol rotation={r} width={data.width} height={data.height} />
        {/* Common output (pole) — right side, centre of the five taps */}
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="common-out" style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="common-in"  style={{ background: '#555', top: '50px' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// Wall plug (IEC 320 / mains inlet) — side-profile schematic symbol.
// Three pins exit right: Line (top), Neutral (bottom), Earth (centre).
// Two AC leads enter from the left.
// Wall plug (IEC mains inlet) — 3 conductors on each side at 20 px intervals.
// viewBox is 80 × 60 so lead tips sit at y = 10, 30, 50 — each 20 px apart
// (2 × the 10 px snap grid).  The node handles use the same absolute pixel
// values, so a neighbouring component placed on the grid can wire straight
// across without needing a bend.
const WallPlugSymbol = ({ label, rotation, labelPos, width = 80, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 80 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={80} vbH={60} rotation={rotation}>{label}</LabelText>
    {/* Plug body — occupies the left ~70% of the component */}
    <rect x="2" y="2" width="54" height="56" rx="6" fill="#f0f4f8" stroke="#333" strokeWidth="1.5"/>
    {/* NEMA 5-15 plug face — Neutral blade (left, wider/taller) */}
    <rect x="12" y="12" width="8" height="18" rx="2" fill="#888"/>
    {/* NEMA 5-15 plug face — Hot blade (right, narrower) */}
    <rect x="30" y="13" width="6" height="16" rx="2" fill="#888"/>
    {/* NEMA 5-15 plug face — Ground pin (round, below blades) */}
    <circle cx="23" cy="41" r="5" fill="none" stroke="#888" strokeWidth="2"/>
    {/* Wire leads on the right — power flows out into the amp circuit */}
    {/* Hot / Line (L) — y = 10 */}
    <line x1="56" y1="10" x2="80" y2="10" stroke="#c53030" strokeWidth="1.5"/>
    {/* Earth (E) — y = 30 */}
    <line x1="56" y1="30" x2="80" y2="30" stroke="#276749" strokeWidth="1.5"/>
    {/* Neutral (N) — y = 50 */}
    <line x1="56" y1="50" x2="80" y2="50" stroke="#1a56db" strokeWidth="1.5"/>
    {/* Wire labels */}
    <text x="58" y="8"  fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,58,8)}>L</text>
    <text x="58" y="28" fontSize="5" fill="#276749" fontFamily="sans-serif" transform={lt(rotation,58,28)}>E</text>
    <text x="58" y="48" fontSize="5" fill="#1a56db" fontFamily="sans-serif" transform={lt(rotation,58,48)}>N</text>
  </svg>
);

// Loudspeaker — voice coil rectangle + cone trapezoid; two leads exit left.
// ViewBox 80×70: top lead y=20, bottom lead y=50 (10px grid multiples).
const LoudspeakerSymbol = ({ label, rotation, labelPos, width = 80, height = 70 }) => (
  <svg width={width} height={height} viewBox="0 0 80 70" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="20" x2="18" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="50" x2="18" y2="50" stroke="#333" strokeWidth="1.5"/>
    <rect x="18" y="14" width="16" height="42" fill="none" stroke="#333" strokeWidth="1.5"/>
    <polygon points="34,14 34,56 76,66 76,4" fill="none" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={80} vbH={70} rotation={rotation}>{label}</LabelText>
  </svg>
);

// Zener Diode — same triangle body as DiodeSymbol but cathode bar has bent ends.
// ViewBox 60×40 matches DiodeSymbol so the same node geometry can be reused.
const ZenerDiodeSymbol = ({ label, rotation, labelPos, width = 30, height = 20 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="20" x2="14" y2="20" stroke="#333" strokeWidth="1.5"/>
    <polygon points="14,8 14,32 42,20" fill="#333" stroke="#333" strokeWidth="1.5"/>
    <line x1="42" y1="8"  x2="42" y2="32" stroke="#333" strokeWidth="2"/>
    <line x1="36" y1="8"  x2="42" y2="8"  stroke="#333" strokeWidth="2"/>
    <line x1="42" y1="32" x2="48" y2="32" stroke="#333" strokeWidth="2"/>
    <line x1="42" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// LED — diode body with two emission arrow rays departing upper-right.
const LedSymbol = ({ label, rotation, labelPos, width = 30, height = 20 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="20" x2="14" y2="20" stroke="#333" strokeWidth="1.5"/>
    <polygon points="14,8 14,32 42,20" fill="#333" stroke="#333" strokeWidth="1.5"/>
    <line x1="42" y1="8"  x2="42" y2="32" stroke="#333" strokeWidth="2"/>
    <line x1="42" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Emission rays */}
    <line x1="40" y1="14" x2="50" y2="4"  stroke="#333" strokeWidth="1.5"/>
    <polygon points="50,4 43,6 46,11" fill="#333"/>
    <line x1="46" y1="10" x2="56" y2="0"  stroke="#333" strokeWidth="1.5"/>
    <polygon points="56,0 49,2 52,7" fill="#333"/>
    <LabelText side={labelPos} vbW={60} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// NPN Transistor — vertical base bar, collector exits top-right, emitter exits
// bottom-right with outward arrow (conventional current exits emitter in NPN).
// ViewBox 60×80: base lead center y=40, collector at x=50 y=0, emitter at x=50 y=80.
const NpnTransistorSymbol = ({ label, rotation, labelPos, width = 60, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 60 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="20" y1="20" x2="20" y2="60" stroke="#333" strokeWidth="2"/>
    <line x1="0"  y1="40" x2="20" y2="40" stroke="#333" strokeWidth="1.5"/>
    <line x1="20" y1="28" x2="50" y2="13" stroke="#333" strokeWidth="1.5"/>
    <line x1="50" y1="13" x2="50" y2="0"  stroke="#333" strokeWidth="1.5"/>
    {/* Emitter line + outward arrowhead */}
    <line x1="20" y1="52" x2="43" y2="64" stroke="#333" strokeWidth="1.5"/>
    <polygon points="50,67 44,62 42,68" fill="#333"/>
    <line x1="50" y1="67" x2="50" y2="80" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={80} rotation={rotation}>{label}</LabelText>
  </svg>
);

// PNP Transistor — same geometry as NPN but emitter arrow points inward toward
// the base bar (conventional current enters emitter in PNP).
const PnpTransistorSymbol = ({ label, rotation, labelPos, width = 60, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 60 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="20" y1="20" x2="20" y2="60" stroke="#333" strokeWidth="2"/>
    <line x1="0"  y1="40" x2="20" y2="40" stroke="#333" strokeWidth="1.5"/>
    <line x1="20" y1="28" x2="50" y2="13" stroke="#333" strokeWidth="1.5"/>
    <line x1="50" y1="13" x2="50" y2="0"  stroke="#333" strokeWidth="1.5"/>
    {/* Emitter line + inward arrowhead (tip points toward base bar) */}
    <line x1="50" y1="67" x2="29" y2="57" stroke="#333" strokeWidth="1.5"/>
    <polygon points="24,54 30,56 28,61" fill="#333"/>
    <line x1="50" y1="80" x2="50" y2="67" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={60} vbH={80} rotation={rotation}>{label}</LabelText>
  </svg>
);

// Neon Lamp — circular envelope with two internal electrodes and a warm glow.
const NeonLampSymbol = ({ label, rotation, labelPos, width = 60, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 60 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="20" x2="16" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="44" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    <circle cx="30" cy="20" r="14" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="22" y1="13" x2="22" y2="27" stroke="#333" strokeWidth="1.5"/>
    <line x1="38" y1="13" x2="38" y2="27" stroke="#333" strokeWidth="1.5"/>
    <circle cx="30" cy="20" r="5" fill="rgba(255,180,30,0.5)" stroke="none"/>
    <LabelText side={labelPos} vbW={60} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Test Point — diamond marker with a single lead exiting bottom.
// ViewBox 30×40; single handle at bottom.
const TestPointSymbol = ({ label, rotation, labelPos, width = 30, height = 40 }) => (
  <svg width={width} height={height} viewBox="0 0 30 40" overflow="visible" style={{ pointerEvents: 'none' }}>
    <polygon points="15,4 25,15 15,26 5,15" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="15" y1="26" x2="15" y2="40" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos || 'right'} vbW={30} vbH={40} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// DPDT Switch — two poles (COM1 y=20, COM2 y=60 on left), four throw contacts
// (A1 y=10, B1 y=30 for pole 1; A2 y=50, B2 y=70 for pole 2 on right).
const DpdtSwitchSymbol = ({ label, rotation, labelPos, width = 80, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 80 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="20" x2="22" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="60" x2="22" y2="60" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="20" r="2" fill="#333"/>
    <circle cx="22" cy="60" r="2" fill="#333"/>
    <line x1="58" y1="10" x2="80" y2="10" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="30" x2="80" y2="30" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="50" x2="80" y2="50" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="70" x2="80" y2="70" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="10" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="30" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="50" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="70" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Arms open toward throw-1 contacts */}
    <line x1="22" y1="20" x2="52" y2="10" stroke="#333" strokeWidth="1.5"/>
    <line x1="22" y1="60" x2="52" y2="50" stroke="#333" strokeWidth="1.5"/>
    {/* Separator between the two poles */}
    <line x1="6" y1="40" x2="74" y2="40" stroke="#ccc" strokeWidth="0.75" strokeDasharray="3 2"/>
    <LabelText side={labelPos} vbW={80} vbH={80} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// Relay — coil (left) coupled via dashed mechanical link to SPDT contact (right).
// Coil terminals y=15 and y=45; COM y=30, NO y=10, NC y=50 on right.
// ViewBox 100×60.
const RelaySymbol = ({ label, rotation, labelPos, width = 100, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 100 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="15" x2="16" y2="15" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="45" x2="16" y2="45" stroke="#333" strokeWidth="1.5"/>
    <rect x="16" y="8" width="22" height="44" fill="none" stroke="#333" strokeWidth="1.5"/>
    <path d="M16,12 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10"
          fill="none" stroke="#333" strokeWidth="1.2"/>
    {/* Mechanical coupling */}
    <line x1="38" y1="30" x2="58" y2="30" stroke="#888" strokeWidth="1" strokeDasharray="3 2"/>
    {/* COM lead + pivot */}
    <line x1="58" y1="30" x2="100" y2="30" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="30" r="2.5" fill="#333"/>
    {/* Arm open toward NO contact */}
    <line x1="58" y1="30" x2="82" y2="14" stroke="#333" strokeWidth="1.5"/>
    <circle cx="84" cy="10" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="84" y1="10" x2="100" y2="10" stroke="#333" strokeWidth="1.5"/>
    {/* NC contact */}
    <circle cx="84" cy="50" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="84" y1="50" x2="100" y2="50" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={100} vbH={60} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// SPST Relay — coil + single normally-open contact only (no NC).
// Used for: speaker protection, soft-start inrush limiting, muting.
// ViewBox 90×50: coil leads y=12/38, COM y=25, NO y=8.
const SpstRelaySymbol = ({ label, rotation, labelPos, width = 90, height = 50 }) => (
  <svg width={width} height={height} viewBox="0 0 90 50" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="12" x2="16" y2="12" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="38" x2="16" y2="38" stroke="#333" strokeWidth="1.5"/>
    <rect x="16" y="6" width="22" height="38" fill="none" stroke="#333" strokeWidth="1.5"/>
    <path d="M16,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10"
          fill="none" stroke="#333" strokeWidth="1.2"/>
    <line x1="38" y1="25" x2="58" y2="25" stroke="#888" strokeWidth="1" strokeDasharray="3 2"/>
    <line x1="58" y1="25" x2="90" y2="25" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="25" r="2.5" fill="#333"/>
    <line x1="58" y1="25" x2="80" y2="11" stroke="#333" strokeWidth="1.5"/>
    <circle cx="82" cy="8"  r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="82" y1="8"  x2="90" y2="8"  stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={90} vbH={50} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

// DPDT Relay — one coil drives two independent SPDT contact sets.
// Used for: true-bypass effects loops (switch signal and return simultaneously),
// channel switching, balanced-signal routing.
// ViewBox 110×90: coil leads y=20/70; pole 1 COM y=25, NO y=10, NC y=40;
// pole 2 COM y=65, NO y=50, NC y=80.
const DpdtRelaySymbol = ({ label, rotation, labelPos, width = 110, height = 90 }) => (
  <svg width={width} height={height} viewBox="0 0 110 90" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0"  y1="20" x2="16" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="70" x2="16" y2="70" stroke="#333" strokeWidth="1.5"/>
    <rect x="16" y="14" width="22" height="62" fill="none" stroke="#333" strokeWidth="1.5"/>
    <path d="M16,14 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10 a5,5 0 0 1 0,10"
          fill="none" stroke="#333" strokeWidth="1.2"/>
    {/* Separator between poles */}
    <line x1="42" y1="45" x2="108" y2="45" stroke="#ccc" strokeWidth="0.75" strokeDasharray="3 2"/>
    {/* Pole 1 mechanical coupling */}
    <line x1="38" y1="25" x2="62" y2="25" stroke="#888" strokeWidth="1" strokeDasharray="3 2"/>
    {/* Pole 1 COM + pivot */}
    <line x1="62" y1="25" x2="110" y2="25" stroke="#333" strokeWidth="1.5"/>
    <circle cx="62" cy="25" r="2.5" fill="#333"/>
    {/* Pole 1 arm open toward NO1 */}
    <line x1="62" y1="25" x2="84" y2="12" stroke="#333" strokeWidth="1.5"/>
    <circle cx="86" cy="10" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="86" y1="10" x2="110" y2="10" stroke="#333" strokeWidth="1.5"/>
    {/* Pole 1 NC contact */}
    <circle cx="86" cy="40" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="86" y1="40" x2="110" y2="40" stroke="#333" strokeWidth="1.5"/>
    {/* Pole 2 mechanical coupling */}
    <line x1="38" y1="65" x2="62" y2="65" stroke="#888" strokeWidth="1" strokeDasharray="3 2"/>
    {/* Pole 2 COM + pivot */}
    <line x1="62" y1="65" x2="110" y2="65" stroke="#333" strokeWidth="1.5"/>
    <circle cx="62" cy="65" r="2.5" fill="#333"/>
    {/* Pole 2 arm open toward NO2 */}
    <line x1="62" y1="65" x2="84" y2="52" stroke="#333" strokeWidth="1.5"/>
    <circle cx="86" cy="50" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="86" y1="50" x2="110" y2="50" stroke="#333" strokeWidth="1.5"/>
    {/* Pole 2 NC contact */}
    <circle cx="86" cy="80" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="86" y1="80" x2="110" y2="80" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={110} vbH={90} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

const WallPlugNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={30} onResize={onResize} />
        {/* Right side only — three output terminals carrying power into the amp.
             The left side of the SVG shows the physical NEMA plug face and has
             no handles; nothing connects to it (you just plug it into the wall).
             Each output has dual source+target handles so drag works from either
             handle type (avoids xyflow error-008 when the role swap occurs). */}
        {/* Hot / Line — top, y = 10 px */}
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="hot-out"      style={{ background: '#c53030', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="hot-out-t"    style={{ background: '#c53030', top: '10px' }} />
        {/* Earth — middle, y = 30 px */}
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="earth-out"    style={{ background: '#276749', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="earth-out-t"  style={{ background: '#276749', top: '30px' }} />
        {/* Neutral — bottom, y = 50 px */}
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="neutral-out"  style={{ background: '#1a56db', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="neutral-out-t" style={{ background: '#1a56db', top: '50px' }} />
        <WallPlugSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const LoudspeakerNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={35} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="pos-in"  style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="pos-out" style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="neg-in"  style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="neg-out" style={{ background: '#555', top: '50px' }} />
        <LoudspeakerSymbol rotation={r} labelPos={data.labelPos} width={data.width || 80} height={data.height || 70} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 70} selected={selected} />
    </div>
  );
};

const ZenerDiodeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={10} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="anode"   style={{ background: '#555' }} />
        <ZenerDiodeSymbol rotation={r} labelPos={data.labelPos} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="cathode" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const LedNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={10} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="anode"   style={{ background: '#555' }} />
        <LedSymbol rotation={r} labelPos={data.labelPos} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="cathode" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

const NpnTransistorNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="base"       style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}   rotation={r} id="base-out"   style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="collector"  style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="emitter"    style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="emitter-in" style={{ background: '#555', left: '83%' }} />
        <NpnTransistorSymbol rotation={r} labelPos={data.labelPos} width={data.width || 60} height={data.height || 80} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 80} selected={selected} />
    </div>
  );
};

const PnpTransistorNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="base"       style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}   rotation={r} id="base-out"   style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="collector"  style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="emitter"    style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="emitter-in" style={{ background: '#555', left: '83%' }} />
        <PnpTransistorSymbol rotation={r} labelPos={data.labelPos} width={data.width || 60} height={data.height || 80} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 80} selected={selected} />
    </div>
  );
};

const NeonLampNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={10} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="a-in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="a-out" style={{ background: '#555' }} />
        <NeonLampSymbol rotation={r} labelPos={data.labelPos} width={data.width || 60} height={data.height || 40} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="b-in"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="b-out" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 40} selected={selected} />
    </div>
  );
};

const TestPointNode = ({ id, data, selected }) => {
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="tp"     style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="tp-out" style={{ background: '#555' }} />
        <TestPointSymbol rotation={r} labelPos={data.labelPos || 'right'} label={data.label} />
      </div>
    </div>
  );
};

const DpdtSwitchNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="com1-in"  style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="com1-out" style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="com2-in"  style={{ background: '#555', top: '60px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="com2-out" style={{ background: '#555', top: '60px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="a1-in"   style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="a1-out"  style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="b1-in"   style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="b1-out"  style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="a2-in"   style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="a2-out"  style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="b2-in"   style={{ background: '#555', top: '70px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="b2-out"  style={{ background: '#555', top: '70px' }} />
        <DpdtSwitchSymbol rotation={r} labelPos={data.labelPos} width={data.width || 80} height={data.height || 80} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 80} selected={selected} />
    </div>
  );
};

const RelayNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={50} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="coil-a-in"  style={{ background: '#c53030', top: '15px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="coil-a-out" style={{ background: '#c53030', top: '15px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="coil-b-in"  style={{ background: '#c53030', top: '45px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="coil-b-out" style={{ background: '#c53030', top: '45px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="com-in"   style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="com-out"  style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="no-in"    style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="no-out"   style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="nc-in"    style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="nc-out"   style={{ background: '#555', top: '50px' }} />
        <RelaySymbol rotation={r} labelPos={data.labelPos} width={data.width || 100} height={data.height || 60} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 60} selected={selected} />
    </div>
  );
};

const SpstRelayNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={45} minHeight={25} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="coil-a-in"  style={{ background: '#c53030', top: '12px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="coil-a-out" style={{ background: '#c53030', top: '12px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="coil-b-in"  style={{ background: '#c53030', top: '38px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="coil-b-out" style={{ background: '#c53030', top: '38px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="com-in"   style={{ background: '#555', top: '25px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="com-out"  style={{ background: '#555', top: '25px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="no-in"    style={{ background: '#555', top: '8px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="no-out"   style={{ background: '#555', top: '8px' }} />
        <SpstRelaySymbol rotation={r} labelPos={data.labelPos} width={data.width || 90} height={data.height || 50} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 50} selected={selected} />
    </div>
  );
};

const DpdtRelayNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={55} minHeight={45} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="coil-a-in"  style={{ background: '#c53030', top: '20px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="coil-a-out" style={{ background: '#c53030', top: '20px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="coil-b-in"  style={{ background: '#c53030', top: '70px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="coil-b-out" style={{ background: '#c53030', top: '70px' }} />
        {/* Pole 1 contacts */}
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="no1-in"    style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="no1-out"   style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="com1-in"   style={{ background: '#555', top: '25px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="com1-out"  style={{ background: '#555', top: '25px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="nc1-in"    style={{ background: '#555', top: '40px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="nc1-out"   style={{ background: '#555', top: '40px' }} />
        {/* Pole 2 contacts */}
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="no2-in"    style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="no2-out"   style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="com2-in"   style={{ background: '#555', top: '65px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="com2-out"  style={{ background: '#555', top: '65px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="nc2-in"    style={{ background: '#555', top: '80px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="nc2-out"   style={{ background: '#555', top: '80px' }} />
        <DpdtRelaySymbol rotation={r} labelPos={data.labelPos} width={data.width || 110} height={data.height || 90} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 90} selected={selected} />
    </div>
  );
};

// ─── Toroidal Power Transformer ───────────────────────────────────────────────
// Same secondary/primary winding layout as PowerTransformerSymbol but with a
// circular (toroidal) core — an ellipse — instead of two straight EI-core lines.
const ToroidalTransformerSymbol = ({ label, windings = 2, rotation, labelPos, width = 160, height, hvVoltage, filVoltage, rectVoltage }) => {
  const h = height || 120;
  const vbH = 120;
  return (
    <svg width={width} height={h} viewBox={`0 0 160 ${vbH}`} overflow="visible" style={{ pointerEvents: 'none' }}>
      <LabelText side={labelPos} vbW={160} vbH={vbH} rotation={rotation}>{label}</LabelText>
      {/* Toroidal core — thin ellipse centred between secondary (x=52) and primary (x=84) backbones */}
      <ellipse cx="69" cy="60" rx="8" ry="56" fill="none" stroke="#333" strokeWidth="2"/>
      {/* HV Secondary: 4 bumps bulging LEFT, backbone x=52, y=10→50, CT lead at y=30 */}
      <path d="M 52,10 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10"
        fill="none" stroke="#333" strokeWidth="1.5"/>
      <line x1="0"  y1="10" x2="52" y2="10" stroke="#333" strokeWidth="1.5"/>
      <line x1="0"  y1="30" x2="26" y2="30" stroke="#333" strokeWidth="1.5"/>
      <line x1="0"  y1="50" x2="52" y2="50" stroke="#333" strokeWidth="1.5"/>
      <text x="1" y="28" fontSize="5" fill="#b7791f" fontFamily="sans-serif" transform={lt(rotation,1,28)}>{hvVoltage ? `${hvVoltage}V CT` : 'HV CT'}</text>
      {windings === 3 ? (
        <>
          <path d="M 52,60 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10" fill="none" stroke="#333" strokeWidth="1.5"/>
          <line x1="0" y1="60" x2="52" y2="60" stroke="#c53030" strokeWidth="1.5"/>
          <line x1="0" y1="80" x2="52" y2="80" stroke="#c53030" strokeWidth="1.5"/>
          <text x="2" y="74" fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,2,74)}>{rectVoltage ? `${rectVoltage}V Rect` : '5V Rect'}</text>
          <path d="M 52,90 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10" fill="none" stroke="#333" strokeWidth="1.5"/>
          <line x1="0" y1="90"  x2="52" y2="90"  stroke="#c53030" strokeWidth="1.5"/>
          <line x1="0" y1="110" x2="52" y2="110" stroke="#c53030" strokeWidth="1.5"/>
          <text x="2" y="104" fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,2,104)}>{filVoltage ? `${filVoltage}V` : '6.3V'}</text>
        </>
      ) : (
        <>
          <path d="M 52,60 a 5 5 0 0 0 0 10 a 5 5 0 0 0 0 10" fill="none" stroke="#333" strokeWidth="1.5"/>
          <line x1="0" y1="60" x2="52" y2="60" stroke="#c53030" strokeWidth="1.5"/>
          <line x1="0" y1="80" x2="52" y2="80" stroke="#c53030" strokeWidth="1.5"/>
          <text x="2" y="74" fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,2,74)}>{filVoltage ? `${filVoltage}V` : '6.3V'}</text>
        </>
      )}
      {/* Primary: 4 bumps bulging RIGHT, backbone x=84, y=10→90; five voltage taps + COM */}
      <path d="M 84,10 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20 a 10 10 0 0 1 0 20"
        fill="none" stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="10"  x2="160" y2="10"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="30"  x2="160" y2="30"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="50"  x2="160" y2="50"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="70"  x2="160" y2="70"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="90"  x2="160" y2="90"  stroke="#333" strokeWidth="1.5"/>
      <line x1="84" y1="110" x2="160" y2="110" stroke="#777" strokeWidth="1.5" strokeDasharray="4 2"/>
      {(() => {
        const px = rotation === 180 ? 112 : 96;
        const dy = rotation === 180 ? 2 : -2;
        return (<>
          <text x={px} y={10+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,10+dy)}>110V</text>
          <text x={px} y={30+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,30+dy)}>120V</text>
          <text x={px} y={50+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,50+dy)}>200V</text>
          <text x={px} y={70+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,70+dy)}>220V</text>
          <text x={px} y={90+dy}  fontSize="5" fill="#555" fontFamily="monospace" transform={lt(rotation,px,90+dy)}>240V</text>
          <text x={px} y={110+dy} fontSize="5" fill="#777" fontFamily="monospace" transform={lt(rotation,px,110+dy)}>COM</text>
        </>);
      })()}
    </svg>
  );
};

const ToroidalTransformerNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const w = data.windings === 3 ? 3 : 2;
  useUpdateAfterRotation(id, data.rotation);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, data.rotation)}>
        <NodeResizer isVisible={selected} minWidth={50} minHeight={40} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="hv-a"  style={{ background: '#555',    top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="hv-ct" style={{ background: '#b7791f', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="hv-b"  style={{ background: '#555',    top: '50px' }} />
        {w === 3 && <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="rect-fil-a" style={{ background: '#c53030', top: '60px' }} />}
        {w === 3 && <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="rect-fil-b" style={{ background: '#c53030', top: '80px' }} />}
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="fil-a" style={{ background: '#c53030', top: w === 3 ? '90px' : '60px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={data.rotation} id="fil-b" style={{ background: '#c53030', top: w === 3 ? '110px' : '80px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-110v"  style={{ background: '#555',    top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-120v"  style={{ background: '#555',    top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-200v"  style={{ background: '#555',    top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-220v"  style={{ background: '#555',    top: '70px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-240v"  style={{ background: '#555',    top: '90px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={data.rotation} id="pri-com"   style={{ background: '#777',    top: '110px' }} />
        <ToroidalTransformerSymbol windings={w} rotation={data.rotation} width={data.width} height={data.height} hvVoltage={data.hv_voltage} filVoltage={data.fil_voltage} rectVoltage={data.rect_voltage} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// ─── Trimmer / Bias Pot ────────────────────────────────────────────────────────
// Screwdriver-adjustable pot for bias trimming. Same connections as panel pot.
// Distinguished by a screwdriver-slot circle at the wiper tip.
const TrimmerSymbol = ({ value, label, rotation, labelPos, width = 60, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 60 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="30" y1="0"  x2="30" y2="15" stroke="#333" strokeWidth="1.5"/>
    <polyline points="30,15 24,20 36,25 24,30 36,35 24,40 36,45 30,45" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="30" y1="45" x2="30" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Wiper exits right at y=20 (top:25% of 80px) */}
    <line x1="30" y1="30" x2="50" y2="20" stroke="#333" strokeWidth="1.5"/>
    <polygon points="50,20 44,16 46,22" fill="#333"/>
    <line x1="50" y1="20" x2="60" y2="20" stroke="#333" strokeWidth="1.5"/>
    {/* Screwdriver-slot circle distinguishes trimmer from a panel pot */}
    <circle cx="55" cy="20" r="4" fill="none" stroke="#555" strokeWidth="1"/>
    <line x1="52" y1="17" x2="58" y2="23" stroke="#555" strokeWidth="1"/>
    <LabelText side={labelPos} vbW={60} vbH={80} rotation={rotation}>{label}{value ? ` - ${value}` : ''}</LabelText>
  </svg>
);

const TrimmerNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"    style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Right}  rotation={r} id="wiper"  style={{ background: '#555', top: '25%' }} />
        <TrimmerSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// ─── VR Tube (Cold-Cathode Voltage Regulator Tube) ────────────────────────────
// OA2 (150 V), 0B2 (108 V), OC3 (105 V).  No heater — two connections:
// anode (unregulated B+ input, top) and cathode (regulated B+ output, bottom).
const VRTubeSymbol = ({ label, rotation, labelPos, width = 80, height = 100 }) => (
  <svg width={width} height={height} viewBox="0 0 80 100" overflow="visible" style={{ pointerEvents: 'none' }}>
    <circle cx="40" cy="46" r="30" fill="none" stroke="#333" strokeWidth="1.5"/>
    {/* Pale blue glow — gas discharge indicator */}
    <ellipse cx="40" cy="46" rx="12" ry="14" fill="rgba(100,160,255,0.15)" stroke="none"/>
    <circle cx="40" cy="34" r="3.5" fill="rgba(120,190,255,0.6)" stroke="#336699" strokeWidth="1"/>
    {/* Plate (anode) — horizontal bar; lead exits top at x=40 y=0 */}
    <line x1="28" y1="26" x2="52" y2="26" stroke="#333" strokeWidth="2"/>
    <line x1="40" y1="0"  x2="40" y2="26" stroke="#333" strokeWidth="1.5"/>
    {/* Cathode — bar with cold-cathode dot; lead exits bottom at x=40 y=100 */}
    <line x1="28" y1="58" x2="52" y2="58" stroke="#333" strokeWidth="2"/>
    <circle cx="40" cy="58" r="3" fill="#333"/>
    <line x1="40" y1="58" x2="40" y2="100" stroke="#333" strokeWidth="1.5"/>
    <LabelText side={labelPos} vbW={80} vbH={100} rotation={rotation}>{label}</LabelText>
  </svg>
);

const VRTubeNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Top}    rotation={r} id="anode"      style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="cathode"    style={{ background: '#276749' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="cathode-in" style={{ background: '#276749' }} />
        <VRTubeSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// ─── Spring Reverb Tank ────────────────────────────────────────────────────────
// Drive coil (left, IN/GND) + pickup coil (right, OUT/GND) coupled by a spring.
const ReverbTankSymbol = ({ label, rotation, labelPos, width = 200, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 200 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={200} vbH={60} rotation={rotation}>{label}</LabelText>
    <rect x="20" y="5" width="160" height="50" rx="4" fill="#f7f8fa" stroke="#333" strokeWidth="1.5"/>
    {/* Drive transducer — coil bumps inside left section */}
    <rect x="25" y="12" width="20" height="36" fill="none" stroke="#555" strokeWidth="1"/>
    <path d="M25,16 a4,4 0 0 1 0,8 a4,4 0 0 1 0,8 a4,4 0 0 1 0,8" fill="none" stroke="#555" strokeWidth="1.2"/>
    {/* Pickup transducer — coil bumps inside right section */}
    <rect x="155" y="12" width="20" height="36" fill="none" stroke="#555" strokeWidth="1"/>
    <path d="M155,16 a4,4 0 0 1 0,8 a4,4 0 0 1 0,8 a4,4 0 0 1 0,8" fill="none" stroke="#555" strokeWidth="1.2"/>
    {/* Spring — zigzag transmission line between transducers */}
    <polyline points="45,30 55,20 65,40 75,20 85,40 95,20 105,40 115,20 125,40 135,20 145,30" fill="none" stroke="#888" strokeWidth="1.2"/>
    <text x="100" y="16" textAnchor="middle" fontSize="6" fill="#555" fontFamily="sans-serif" fontWeight="bold" transform={lt(rotation,100,16)}>SPRING REVERB</text>
    {/* Drive leads exit left */}
    <line x1="0"  y1="20" x2="20" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="40" x2="20" y2="40" stroke="#333" strokeWidth="1.5"/>
    <text x="2" y="18" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,2,18)}>IN</text>
    <text x="2" y="38" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,2,38)}>GND</text>
    {/* Pickup leads exit right */}
    <line x1="180" y1="20" x2="200" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="180" y1="40" x2="200" y2="40" stroke="#333" strokeWidth="1.5"/>
    <text x="182" y="18" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,182,18)}>OUT</text>
    <text x="182" y="38" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,182,38)}>GND</text>
  </svg>
);

const ReverbTankNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={80} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="drive-in"      style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="drive-out"     style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="target" basePosition={Position.Left} rotation={r} id="drive-gnd-in"  style={{ background: '#333', top: '40px' }} />
        <FixedHandle type="source" basePosition={Position.Left} rotation={r} id="drive-gnd-out" style={{ background: '#333', top: '40px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="pickup-out"    style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="pickup-in"     style={{ background: '#555', top: '20px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="pickup-gnd-out" style={{ background: '#333', top: '40px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="pickup-gnd-in"  style={{ background: '#333', top: '40px' }} />
        <ReverbTankSymbol rotation={r} width={data.width || 200} height={data.height || 60} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 60} selected={selected} />
    </div>
  );
};

// ─── Rotary Switch (SP6T) ──────────────────────────────────────────────────────
// 6-position single-pole rotary for channel / EQ / feedback path selection.
// 6 contacts exit left (y=10..110); common (pole) exits right at y=60.
const RotarySwitchSymbol = ({ label, rotation, labelPos, width = 80, height = 120 }) => (
  <svg width={width} height={height} viewBox="0 0 80 120" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="0" y1="10"  x2="22" y2="10"  stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="30"  x2="22" y2="30"  stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="50"  x2="22" y2="50"  stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="70"  x2="22" y2="70"  stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="90"  x2="22" y2="90"  stroke="#333" strokeWidth="1.5"/>
    <line x1="0" y1="110" x2="22" y2="110" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="10"  r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="30"  r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="50"  r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="70"  r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="90"  r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="110" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="60" x2="80" y2="60" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="60" r="2.5" fill="#333"/>
    {/* Arm open between positions 2 and 3 */}
    <line x1="58" y1="60" x2="36" y2="39" stroke="#333" strokeWidth="1.5"/>
    <text x="25" y="8"   fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,8)}>1</text>
    <text x="25" y="28"  fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,28)}>2</text>
    <text x="25" y="48"  fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,48)}>3</text>
    <text x="25" y="68"  fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,68)}>4</text>
    <text x="25" y="88"  fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,88)}>5</text>
    <text x="25" y="108" fontSize="6" fill="#555" fontFamily="monospace" transform={lt(rotation,25,108)}>6</text>
    <LabelText side={labelPos} vbW={80} vbH={120} rotation={rotation} fontSize={6}>{label}</LabelText>
  </svg>
);

const RotarySwitchNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={60} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="pos1-in"  style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="pos1-out" style={{ background: '#555', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="pos2-in"  style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="pos2-out" style={{ background: '#555', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="pos3-in"  style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="pos3-out" style={{ background: '#555', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="pos4-in"  style={{ background: '#555', top: '70px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="pos4-out" style={{ background: '#555', top: '70px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="pos5-in"  style={{ background: '#555', top: '90px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="pos5-out" style={{ background: '#555', top: '90px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="pos6-in"  style={{ background: '#555', top: '110px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="pos6-out" style={{ background: '#555', top: '110px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="com-out"  style={{ background: '#555', top: '60px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="com-in"   style={{ background: '#555', top: '60px' }} />
        <RotarySwitchSymbol rotation={r} width={data.width || 80} height={data.height || 120} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 120} selected={selected} />
    </div>
  );
};

// ─── N-Channel JFET ───────────────────────────────────────────────────────────
// Gate exits left (y=40); drain top-right (x=50 y=0); source bottom-right (x=50 y=80).
// Arrow on gate points INTO channel — N-channel convention.
const NJFETSymbol = ({ label, rotation, labelPos, width = 60, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 60 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* Channel bar — vertical at x=35, y=15..65 */}
    <line x1="35" y1="15" x2="35" y2="65" stroke="#333" strokeWidth="2.5"/>
    {/* Drain branch — channel at y=20, leads up to x=50 y=0 */}
    <line x1="35" y1="20" x2="50" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="50" y1="20" x2="50" y2="0"  stroke="#333" strokeWidth="1.5"/>
    {/* Source branch — channel at y=60, leads down to x=50 y=80 */}
    <line x1="35" y1="60" x2="50" y2="60" stroke="#333" strokeWidth="1.5"/>
    <line x1="50" y1="60" x2="50" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Gate lead + arrowhead pointing INTO channel (N-channel) */}
    <line x1="0" y1="40" x2="25" y2="40" stroke="#333" strokeWidth="1.5"/>
    <line x1="25" y1="40" x2="33" y2="40" stroke="#333" strokeWidth="1"/>
    <polygon points="33,37 33,43 35,40" fill="#333"/>
    <LabelText side={labelPos} vbW={60} vbH={80} rotation={rotation}>{label}</LabelText>
  </svg>
);

const NJFETNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="gate"      style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}   rotation={r} id="gate-out"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="drain"     style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="source"    style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="source-in" style={{ background: '#555', left: '83%' }} />
        <NJFETSymbol rotation={r} labelPos={data.labelPos} width={data.width || 60} height={data.height || 80} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 80} selected={selected} />
    </div>
  );
};

// ─── P-Channel JFET ───────────────────────────────────────────────────────────
// Same geometry as N-JFET but gate arrow points OUTWARD (away from channel).
const PJFETSymbol = ({ label, rotation, labelPos, width = 60, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 60 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="35" y1="15" x2="35" y2="65" stroke="#333" strokeWidth="2.5"/>
    <line x1="35" y1="20" x2="50" y2="20" stroke="#333" strokeWidth="1.5"/>
    <line x1="50" y1="20" x2="50" y2="0"  stroke="#333" strokeWidth="1.5"/>
    <line x1="35" y1="60" x2="50" y2="60" stroke="#333" strokeWidth="1.5"/>
    <line x1="50" y1="60" x2="50" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Gate lead + arrowhead pointing OUTWARD (P-channel) */}
    <line x1="0" y1="40" x2="35" y2="40" stroke="#333" strokeWidth="1.5"/>
    <polygon points="25,37 25,43 22,40" fill="#333"/>
    <LabelText side={labelPos} vbW={60} vbH={80} rotation={rotation}>{label}</LabelText>
  </svg>
);

const PJFETNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={30} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="gate"      style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Left}   rotation={r} id="gate-out"  style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="drain"     style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="source"    style={{ background: '#555', left: '83%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="source-in" style={{ background: '#555', left: '83%' }} />
        <PJFETSymbol rotation={r} labelPos={data.labelPos} width={data.width || 60} height={data.height || 80} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 80} selected={selected} />
    </div>
  );
};

// ─── Op-Amp ───────────────────────────────────────────────────────────────────
// Standard triangle op-amp: + (non-inv) and − (inv) inputs left, output right.
// Optional V+ (top, y=0) and V− (bottom, y=80) power supply pins.
const OpAmpSymbol = ({ label, rotation, labelPos, width = 100, height = 80 }) => (
  <svg width={width} height={height} viewBox="0 0 100 80" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={100} vbH={80} rotation={rotation}>{label}</LabelText>
    {/* Triangle body: left edge x=10 y=10..70; apex at x=75 y=40 */}
    <polygon points="10,10 10,70 75,40" fill="#fff8e1" stroke="#333" strokeWidth="1.5"/>
    {/* Non-inverting (+) input */}
    <line x1="0" y1="25" x2="10" y2="25" stroke="#333" strokeWidth="1.5"/>
    {/* Inverting (−) input */}
    <line x1="0" y1="55" x2="10" y2="55" stroke="#333" strokeWidth="1.5"/>
    {/* Output */}
    <line x1="75" y1="40" x2="100" y2="40" stroke="#333" strokeWidth="1.5"/>
    {/* V+ power supply */}
    <line x1="43" y1="28" x2="43" y2="0" stroke="#b7791f" strokeWidth="1.5"/>
    {/* V− power supply */}
    <line x1="43" y1="52" x2="43" y2="80" stroke="#555" strokeWidth="1.5"/>
    {/* + and − labels inside triangle */}
    <text x="17" y="29" fontSize="9" fill="#333" fontFamily="sans-serif" fontWeight="bold" transform={lt(rotation,17,29)}>+</text>
    <text x="17" y="59" fontSize="9" fill="#333" fontFamily="sans-serif" fontWeight="bold" transform={lt(rotation,17,59)}>−</text>
  </svg>
);

const OpAmpNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={50} minHeight={40} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="in-plus"      style={{ background: '#276749', top: '25px' }} />
        <FixedHandle type="source" basePosition={Position.Left}   rotation={r} id="in-plus-out"  style={{ background: '#276749', top: '25px' }} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="in-minus"     style={{ background: '#c53030', top: '55px' }} />
        <FixedHandle type="source" basePosition={Position.Left}   rotation={r} id="in-minus-out" style={{ background: '#c53030', top: '55px' }} />
        <FixedHandle type="source" basePosition={Position.Right}  rotation={r} id="out"          style={{ background: '#555' }} />
        <FixedHandle type="target" basePosition={Position.Right}  rotation={r} id="out-in"       style={{ background: '#555' }} />
        <FixedHandle type="target" basePosition={Position.Top}    rotation={r} id="vplus"        style={{ background: '#b7791f', left: '43%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="vminus"       style={{ background: '#555',    left: '43%' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="vminus-in"    style={{ background: '#555',    left: '43%' }} />
        <OpAmpSymbol rotation={r} labelPos={data.labelPos} width={data.width || 100} height={data.height || 80} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 80} selected={selected} />
    </div>
  );
};

// ─── Voltage Regulator IC (3-terminal) ────────────────────────────────────────
// LM317, LM337, 7805, 7812, 7815, 7915, etc.
// IN (left), GND (bottom), OUT (right). Leads at y=30 / x=40 — multiples of 10.
const VoltageRegICSymbol = ({ label, value, rotation, labelPos, width = 80, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 80 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={80} vbH={60} rotation={rotation}>{label}{value ? ` ${value}` : ''}</LabelText>
    <rect x="18" y="5" width="44" height="50" rx="3" fill="#fffff0" stroke="#333" strokeWidth="1.5"/>
    <text x="40" y="34" textAnchor="middle" fontSize="7" fill="#555" fontFamily="monospace" fontWeight="bold" transform={lt(rotation,40,34)}>Vreg</text>
    <line x1="0"  y1="30" x2="18" y2="30" stroke="#333" strokeWidth="1.5"/>
    <text x="2"  y="28" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,2,28)}>IN</text>
    <line x1="40" y1="55" x2="40" y2="60" stroke="#333" strokeWidth="1.5"/>
    <text x="34" y="57" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,34,57)}>GND</text>
    <line x1="62" y1="30" x2="80" y2="30" stroke="#333" strokeWidth="1.5"/>
    <text x="64" y="28" fontSize="5" fill="#555" fontFamily="sans-serif" transform={lt(rotation,64,28)}>OUT</text>
  </svg>
);

const VoltageRegICNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={30} onResize={onResize} />
        <FixedHandle type="target" basePosition={Position.Left}   rotation={r} id="reg-in"    style={{ background: '#555' }} />
        <FixedHandle type="source" basePosition={Position.Right}  rotation={r} id="reg-out"   style={{ background: '#276749' }} />
        <FixedHandle type="target" basePosition={Position.Right}  rotation={r} id="reg-out-t" style={{ background: '#276749' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="reg-gnd"   style={{ background: '#333' }} />
        <FixedHandle type="target" basePosition={Position.Bottom} rotation={r} id="reg-gnd-t" style={{ background: '#333' }} />
        <VoltageRegICSymbol rotation={r} labelPos={data.labelPos} width={data.width || 80} height={data.height || 60} value={data.value} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height || 60} selected={selected} />
    </div>
  );
};

// ─── IEC C14 Mains Inlet ──────────────────────────────────────────────────────
// Chassis-mount C14 inlet (female socket on amp rear panel).
// Leads exit right (internal circuit): L y=10, E y=30, N y=50.
const IecInletSymbol = ({ label, rotation, labelPos, width = 80, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 80 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <LabelText side={labelPos} vbW={80} vbH={60} rotation={rotation}>{label}</LabelText>
    <rect x="2" y="2" width="38" height="56" rx="5" fill="#f0f4f8" stroke="#333" strokeWidth="1.5"/>
    {/* C14 slot outlines: Live (red) and Neutral (blue) rectangles, Earth (green) circle */}
    <rect x="9"  y="11" width="7" height="14" rx="1" fill="#c53030" opacity="0.7"/>
    <rect x="24" y="11" width="7" height="14" rx="1" fill="#1a56db" opacity="0.7"/>
    <circle cx="20" cy="40" r="5" fill="none" stroke="#276749" strokeWidth="1.8"/>
    {/* Internal leads exit right toward amp circuitry */}
    <line x1="40" y1="10" x2="80" y2="10" stroke="#c53030" strokeWidth="1.5"/>
    <line x1="40" y1="30" x2="80" y2="30" stroke="#276749" strokeWidth="1.5"/>
    <line x1="40" y1="50" x2="80" y2="50" stroke="#1a56db" strokeWidth="1.5"/>
    <text x="42" y="8"  fontSize="5" fill="#c53030" fontFamily="sans-serif" transform={lt(rotation,42,8)}>L</text>
    <text x="42" y="28" fontSize="5" fill="#276749" fontFamily="sans-serif" transform={lt(rotation,42,28)}>E</text>
    <text x="42" y="48" fontSize="5" fill="#1a56db" fontFamily="sans-serif" transform={lt(rotation,42,48)}>N</text>
  </svg>
);

const IecInletNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={30} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="line-out"      style={{ background: '#c53030', top: '10px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="line-out-t"    style={{ background: '#c53030', top: '10px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="earth-out"     style={{ background: '#276749', top: '30px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="earth-out-t"   style={{ background: '#276749', top: '30px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="neutral-out"   style={{ background: '#1a56db', top: '50px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="neutral-out-t" style={{ background: '#1a56db', top: '50px' }} />
        <IecInletSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// ─── NTC Thermistor ────────────────────────────────────────────────────────────
// NTC (negative temperature coefficient): zigzag body with diagonal variable
// arrow and "−t°" annotation. Used for inrush limiting and temp compensation.
const ThermistorSymbol = ({ label, rotation, labelPos, width = 60, height = 60 }) => (
  <svg width={width} height={height} viewBox="0 0 60 60" overflow="visible" style={{ pointerEvents: 'none' }}>
    <line x1="30" y1="0"  x2="30" y2="15" stroke="#333" strokeWidth="1.5"/>
    <polyline points="30,15 24,20 36,25 24,30 36,35 24,40 36,45 30,45" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="30" y1="45" x2="30" y2="60" stroke="#333" strokeWidth="1.5"/>
    {/* Diagonal variable arrow */}
    <line x1="12" y1="50" x2="46" y2="10" stroke="#333" strokeWidth="1.5"/>
    <polygon points="46,10 39,13 43,18" fill="#333"/>
    {/* −t° annotation — NTC identifier */}
    <text x="44" y="50" fontSize="6" fill="#c53030" fontFamily="monospace" fontWeight="bold" transform={lt(rotation,44,50)}>−t°</text>
    <LabelText side={labelPos} vbW={60} vbH={60} rotation={rotation}>{label}</LabelText>
  </svg>
);

const ThermistorNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={20} minHeight={20} onResize={onResize} />
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"    style={{ background: '#555' }} />
        <ThermistorSymbol rotation={r} width={data.width} height={data.height} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom" style={{ background: '#555' }} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// ─── Push-Pull Pot (DPDT switch integrated in pot shaft) ──────────────────────
// Top section (y=0..80): standard pot — pin1 top, pin3 bottom, wiper right.
// Bottom section (y=100..180): DPDT switch — com1/com2 left; a1,b1,a2,b2 right.
// Dashed mechanical linkage at y=80..100 shows the push-pull shaft coupling.
const PushPullPotSymbol = ({ value, label, rotation, labelPos, width = 80, height = 180 }) => (
  <svg width={width} height={height} viewBox="0 0 80 180" overflow="visible" style={{ pointerEvents: 'none' }}>
    {/* === Pot section === */}
    <line x1="30" y1="0"  x2="30" y2="15" stroke="#333" strokeWidth="1.5"/>
    <polyline points="30,15 24,20 36,25 24,30 36,35 24,40 36,45 30,45" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="30" y1="45" x2="30" y2="80" stroke="#333" strokeWidth="1.5"/>
    {/* Pot wiper exits right at y=20 (≈11% of 180px) */}
    <line x1="30" y1="30" x2="50" y2="20" stroke="#333" strokeWidth="1.5"/>
    <polygon points="50,20 44,16 46,22" fill="#333"/>
    <line x1="50" y1="20" x2="80" y2="20" stroke="#333" strokeWidth="1.5"/>
    <text x="40" y="40" fontSize="5" fill="#888" fontFamily="sans-serif" transform={lt(rotation,40,40)}>POT</text>
    {/* === Mechanical linkage — dashed line === */}
    <line x1="30" y1="80" x2="30" y2="100" stroke="#888" strokeWidth="1" strokeDasharray="4 3"/>
    {/* === DPDT switch section === */}
    <line x1="0"  y1="110" x2="22" y2="110" stroke="#333" strokeWidth="1.5"/>
    <line x1="0"  y1="150" x2="22" y2="150" stroke="#333" strokeWidth="1.5"/>
    <circle cx="22" cy="110" r="2" fill="#333"/>
    <circle cx="22" cy="150" r="2" fill="#333"/>
    <line x1="58" y1="100" x2="80" y2="100" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="120" x2="80" y2="120" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="140" x2="80" y2="140" stroke="#333" strokeWidth="1.5"/>
    <line x1="58" y1="160" x2="80" y2="160" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="100" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="120" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="140" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <circle cx="58" cy="160" r="2.5" fill="none" stroke="#333" strokeWidth="1.5"/>
    <line x1="22" y1="110" x2="52" y2="100" stroke="#333" strokeWidth="1.5"/>
    <line x1="22" y1="150" x2="52" y2="140" stroke="#333" strokeWidth="1.5"/>
    <line x1="6"  y1="130" x2="74" y2="130" stroke="#ccc" strokeWidth="0.75" strokeDasharray="3 2"/>
    <text x="36" y="128" fontSize="5" fill="#888" fontFamily="sans-serif" transform={lt(rotation,36,128)}>SW</text>
    <LabelText side={labelPos} vbW={80} vbH={180} rotation={rotation}>{label}{value ? ` - ${value}` : ''}</LabelText>
  </svg>
);

const PushPullPotNode = ({ id, data, selected }) => {
  const onResize = useResizeInData(id);
  const r = data.rotation;
  useUpdateAfterRotation(id, r);
  return (
    <div style={{ position: 'relative', overflow: 'visible', lineHeight: 0 }}>
      <div style={nwStyle(selected, r)}>
        <NodeResizer isVisible={selected} minWidth={40} minHeight={90} onResize={onResize} />
        {/* Pot terminals */}
        <FixedHandle type="source" basePosition={Position.Top}    rotation={r} id="top"       style={{ background: '#555', left: '37%' }} />
        <FixedHandle type="source" basePosition={Position.Bottom} rotation={r} id="bottom"    style={{ background: '#555', left: '37%' }} />
        <FixedHandle type="source" basePosition={Position.Right}  rotation={r} id="wiper"     style={{ background: '#555', top: '11%' }} />
        {/* Switch — left: two COM contacts */}
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="sw-com1-in"  style={{ background: '#555', top: '110px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="sw-com1-out" style={{ background: '#555', top: '110px' }} />
        <FixedHandle type="target" basePosition={Position.Left}  rotation={r} id="sw-com2-in"  style={{ background: '#555', top: '150px' }} />
        <FixedHandle type="source" basePosition={Position.Left}  rotation={r} id="sw-com2-out" style={{ background: '#555', top: '150px' }} />
        {/* Switch — right: four throw contacts */}
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="sw-a1-in"    style={{ background: '#555', top: '100px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="sw-a1-out"   style={{ background: '#555', top: '100px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="sw-b1-in"    style={{ background: '#555', top: '120px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="sw-b1-out"   style={{ background: '#555', top: '120px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="sw-a2-in"    style={{ background: '#555', top: '140px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="sw-a2-out"   style={{ background: '#555', top: '140px' }} />
        <FixedHandle type="target" basePosition={Position.Right} rotation={r} id="sw-b2-in"    style={{ background: '#555', top: '160px' }} />
        <FixedHandle type="source" basePosition={Position.Right} rotation={r} id="sw-b2-out"   style={{ background: '#555', top: '160px' }} />
        <PushPullPotSymbol rotation={r} width={data.width} height={data.height} />
      </div>
      <FloatingLabel nodeId={id} label={data.label} value={data.value} stacked={data.labelStacked} dx={data.labelDx} dy={data.labelDy} defaultH={data.height} selected={selected} />
    </div>
  );
};

// Free-form drawn wire — stored as a node so it needs no source/target pins.
// `data.relPts` = [{x,y}] relative to node.position (bounding-box top-left minus padding).
// Dragging the node in ReactFlow moves the whole wire as a unit.
const FreeWireNode = ({ id, data, selected }) => {
  const allEdges = useEdges();
  const allNodes = useNodes();
  const { setNodes, setEdges, getNodes, screenToFlowPosition } = useReactFlow();
  const endptDragRef = useRef(null);
  const segDragRef = useRef(null);
  const containerRef = useRef(null);

  // Make the React Flow node wrapper transparent to pointer events so that
  // clicking anywhere inside the bounding box that is NOT on the actual wire
  // stroke passes through to whatever is underneath (other components, pane).
  // Only the fat transparent path (pointer-events:'stroke') catches clicks.
  useEffect(() => {
    const wrapper = containerRef.current?.parentElement;
    if (wrapper) wrapper.style.pointerEvents = 'none';
  }, []); // run once on mount; the wrapper DOM element is stable across re-renders

  if (!data.relPts || data.relPts.length < 2) return null;

  // Absolute position of this node's origin in flow coordinates
  const thisNode = allNodes.find(n => n.id === id);
  const ox = thisNode?.position.x ?? 0;
  const oy = thisNode?.position.y ?? 0;

  // Segments of this wire in absolute flow coords (for crossing detection)
  const absPts = data.relPts.map(p => ({ x: ox + p.x, y: oy + p.y }));
  const absSegs = ptsToSegs(absPts);

  // All other wire segments in absolute flow coords:
  //   • WireEdge $segs (cached by each edge's useEffect) — skip noJump edges
  //   • Other FreeWireNode relPts converted to absolute — skip noJump nodes
  const otherSegs = [
    ...allEdges.flatMap(e => (!e.data?.$noJump && e.data?.$segs) ? e.data.$segs : []),
    ...allNodes
      .filter(n => n.type === 'freeWire' && n.id !== id && !n.data?.noJump && n.data?.relPts?.length > 1)
      .flatMap(n => {
        const pts = n.data.relPts.map(p => ({ x: n.position.x + p.x, y: n.position.y + p.y }));
        return ptsToSegs(pts);
      }),
  ];

  // Build crossing map: only H segs of this wire get arcs (standard schematic convention).
  // V segs are unmodified — the arc on the H wire is sufficient to show non-connection.
  // Store hit coords in local (relative) coords so buildSvgPath can use them against localSegs.
  // Skip entirely if this wire is marked as connected-at-crossings.
  const jumpsBySegIdx = {};
  if (!data.noJump) {
    for (let i = 0; i < absSegs.length; i++) {
      const ms = absSegs[i];
      if (!ms.isH) continue;
      for (const os of otherSegs) {
        if (os.isH) continue;
        const hit = segXsect(ms.x1, ms.y1, ms.x2, ms.y2, os.x1, os.y1, os.x2, os.y2);
        if (hit) {
          if (!jumpsBySegIdx[i]) jumpsBySegIdx[i] = [];
          jumpsBySegIdx[i].push({ ...hit, x: hit.x - ox, y: hit.y - oy });
        }
      }
    }
  }

  const localSegs = ptsToSegs(data.relPts);
  const pathD = buildSvgPath(localSegs, jumpsBySegIdx);

  const xs = data.relPts.map(p => p.x), ys = data.relPts.map(p => p.y);
  const w = Math.max(...xs) + 2, h = Math.max(...ys) + 2;

  // ── Endpoint drag ──────────────────────────────────────────────────────────
  // Find the nearest component handle within ENDPT_SNAP flow-px of a flow point.
  const ENDPT_SNAP = 20;
  const findHandle = (flowPt) => {
    let best = null, bestDist = ENDPT_SNAP;
    for (const n of getNodes()) {
      if (n.type === 'freeWire' || n.type === 'note') continue;
      const absX = n.internals?.positionAbsolute?.x ?? n.position.x;
      const absY = n.internals?.positionAbsolute?.y ?? n.position.y;
      for (const hb of (n.internals?.handleBounds?.source || [])) {
        const hx = absX + hb.x + hb.width / 2;
        const hy = absY + hb.y + hb.height / 2;
        const dist = Math.hypot(hx - flowPt.x, hy - flowPt.y);
        if (dist < bestDist) {
          bestDist = dist;
          best = { nodeId: n.id, handleId: hb.id || null, handleType: 'source', x: hx, y: hy };
        }
      }
      for (const hb of (n.internals?.handleBounds?.target || [])) {
        const hx = absX + hb.x + hb.width / 2;
        const hy = absY + hb.y + hb.height / 2;
        const dist = Math.hypot(hx - flowPt.x, hy - flowPt.y);
        if (dist < bestDist) {
          bestDist = dist;
          best = { nodeId: n.id, handleId: hb.id || null, handleType: 'target', x: hx, y: hy };
        }
      }
    }
    return best;
  };

  const onEndptMouseDown = (e, endptIdx) => {
    // Stop BOTH React synthetic propagation AND the native DOM event so that
    // React Flow's direct addEventListener('mousedown') on the node wrapper
    // never fires — otherwise both endpoint drag and node body drag start.
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent?.stopImmediatePropagation();
    endptDragRef.current = {
      endptIdx,
      origRelPts: data.relPts.map(p => ({ ...p })),
      ox, oy,
    };

    const onMove = (me) => {
      const { endptIdx: ei, origRelPts: orig, ox: startOx, oy: startOy } = endptDragRef.current;
      const fp = screenToFlowPosition({ x: me.clientX, y: me.clientY });

      // HV snap: align with adjacent point to keep the path orthogonal
      const adjIdx = ei === 0 ? 1 : orig.length - 2;
      const adjAbs = { x: startOx + orig[adjIdx].x, y: startOy + orig[adjIdx].y };
      const dxM = Math.abs(fp.x - adjAbs.x);
      const dyM = Math.abs(fp.y - adjAbs.y);
      let snapped = dxM >= dyM
        ? { x: snapV(fp.x), y: adjAbs.y }
        : { x: adjAbs.x, y: snapV(fp.y) };

      // Snap to nearby handle if one is close
      const handle = findHandle(snapped);
      const finalFp = handle ? { x: handle.x, y: handle.y } : snapped;

      // Rebuild all absolute pts with the new endpoint, then normalise bounding box
      const newAbsPts = orig.map((p, i) =>
        i === ei ? finalFp : { x: startOx + p.x, y: startOy + p.y }
      );
      const pad = 6;
      const minX = Math.min(...newAbsPts.map(p => p.x)) - pad;
      const minY = Math.min(...newAbsPts.map(p => p.y)) - pad;
      const newRelPts = newAbsPts.map(p => ({ x: p.x - minX, y: p.y - minY }));

      setNodes(nds => nds.map(n =>
        n.id !== id ? n :
          { ...n, position: { x: minX, y: minY }, data: { ...n.data, relPts: newRelPts } }
      ));
    };

    const onUp = (me) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const { endptIdx: ei } = endptDragRef.current;
      endptDragRef.current = null;

      // Check if the dropped endpoint landed on a component handle
      const fp = screenToFlowPosition({ x: me.clientX, y: me.clientY });
      const droppedHandle = findHandle(fp);
      if (!droppedHandle) return; // keep updated FreeWireNode as-is

      // Check if the OTHER endpoint is also on a handle
      const currentNodes = getNodes();
      const thisN = currentNodes.find(n => n.id === id);
      if (!thisN) return;
      const rp = thisN.data.relPts;
      const pos = thisN.position;
      const otherIdx = ei === 0 ? rp.length - 1 : 0;
      const otherAbsPt = { x: pos.x + rp[otherIdx].x, y: pos.y + rp[otherIdx].y };
      const otherHandle = findHandle(otherAbsPt);
      if (!otherHandle) return; // only one end on a handle, keep as FreeWireNode

      // Both endpoints are on handles — promote to a proper connected WireEdge
      const absPts = rp.map(p => ({ x: pos.x + p.x, y: pos.y + p.y }));
      let srcH = ei === 0 ? droppedHandle : otherHandle;
      let tgtH = ei === 0 ? otherHandle : droppedHandle;
      let finalPts = absPts;
      // Ensure source→target orientation (swap if user drew target→source)
      if (srcH.handleType === 'target' && tgtH.handleType === 'source') {
        [srcH, tgtH] = [tgtH, srcH];
        finalPts = [...absPts].reverse();
      }
      if (srcH.handleType === 'source') {
        setEdges(eds => eds.concat({
          id: `edge_drawn_${Date.now()}`,
          type: 'wire',
          source: srcH.nodeId, sourceHandle: srcH.handleId,
          target: tgtH.nodeId, targetHandle: tgtH.handleId,
          animated: false, interactionWidth: 12, selectable: true,
          data: { manualDraw: true, waypoints: finalPts },
        }));
      }
      // Remove the FreeWireNode now that it's been promoted
      setNodes(nds => nds.filter(n => n.id !== id));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Segment drag ───────────────────────────────────────────────────────────
  // Same collinear-run logic as WireEdge: drag perpendicular to the segment
  // while keeping endpoints fixed and moving all interior points on the same row/col.
  // Unlike WireEdge, FreeWireNode endpoints may be free (not on a component handle).
  // For a free endpoint whose adjacent segment is being dragged, we move the endpoint
  // itself so the end segment stays orthogonal — rather than blocking the drag.
  const onSegMouseDown = (e, segIdx) => {
    e.stopPropagation();
    e.preventDefault();
    e.nativeEvent?.stopImmediatePropagation();
    const startFP = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const origAbsPts = data.relPts.map(p => ({ x: ox + p.x, y: oy + p.y }));
    const ls = localSegs[segIdx];
    const absSeg = { isH: ls.isH, x1: ox + ls.x1, y1: oy + ls.y1, x2: ox + ls.x2, y2: oy + ls.y2 };
    const N0 = origAbsPts.length;
    const startFree = !findHandle(origAbsPts[0]);
    const endFree   = !findHandle(origAbsPts[N0 - 1]);
    segDragRef.current = { startFP, origAbsPts, absSeg, startFree, endFree };

    const onMove = (me) => {
      if (!segDragRef.current) return;
      const { origAbsPts: origAbs, startFP: sp, absSeg: sg, startFree: sf, endFree: ef } = segDragRef.current;
      const curFP = screenToFlowPosition({ x: me.clientX, y: me.clientY });
      const pts = origAbs.map(p => ({ ...p }));
      const N = pts.length;
      const segs = ptsToSegs(origAbs);

      if (sg.isH) {
        const deltaY = snapV(curFP.y - sp.y);
        const origY = sg.y1;
        const cands = new Set();
        for (let j = 1; j < N - 1; j++) {
          if (Math.abs(origAbs[j].y - origY) < 1) cands.add(j);
        }
        // Locked endpoints (on a component handle): purge candidates reachable
        // from that endpoint through consecutive same-direction segments.
        if (!sf && segs[0].isH) {
          let j = 1;
          while (j < N - 1 && cands.has(j) && segs[j - 1].isH) cands.delete(j++);
        }
        if (!ef && segs[N - 2].isH) {
          let j = N - 2;
          while (j >= 1 && cands.has(j) && segs[j].isH) cands.delete(j--);
        }
        for (const j of cands) pts[j].y = origY + deltaY;
        // Free endpoints: drag the endpoint with the segment so it stays orthogonal.
        if (sf && segs[0].isH && cands.has(1))       pts[0].y     = origY + deltaY;
        if (ef && segs[N - 2].isH && cands.has(N - 2)) pts[N - 1].y = origY + deltaY;
      } else {
        const deltaX = snapV(curFP.x - sp.x);
        const origX = sg.x1;
        const cands = new Set();
        for (let j = 1; j < N - 1; j++) {
          if (Math.abs(origAbs[j].x - origX) < 1) cands.add(j);
        }
        if (!sf && !segs[0].isH) {
          let j = 1;
          while (j < N - 1 && cands.has(j) && !segs[j - 1].isH) cands.delete(j++);
        }
        if (!ef && !segs[N - 2].isH) {
          let j = N - 2;
          while (j >= 1 && cands.has(j) && !segs[j].isH) cands.delete(j--);
        }
        for (const j of cands) pts[j].x = origX + deltaX;
        if (sf && !segs[0].isH && cands.has(1))        pts[0].x     = origX + deltaX;
        if (ef && !segs[N - 2].isH && cands.has(N - 2)) pts[N - 1].x = origX + deltaX;
      }

      const pad = 6;
      const minX = Math.min(...pts.map(p => p.x)) - pad;
      const minY = Math.min(...pts.map(p => p.y)) - pad;
      const newRelPts = pts.map(p => ({ x: p.x - minX, y: p.y - minY }));
      setNodes(nds => nds.map(n =>
        n.id !== id ? n :
          { ...n, position: { x: minX, y: minY }, data: { ...n.data, relPts: newRelPts } }
      ));
    };

    const onUp = () => {
      segDragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    // pointer-events: none here (and on the SVG) means the bounding-box rectangle
    // is invisible to clicks.  Only SVG children with an explicit non-none
    // pointer-events value (the fat path: stroke; the endpoint circles: visiblePainted)
    // can be targeted.  Clicks and mousedowns on those children still bubble normally
    // to the React Flow wrapper, so body drag and React Flow selection both work.
    <div ref={containerRef}
         style={{ width: w, height: h, background: 'transparent', pointerEvents: 'none',
                  outline: selected ? '1px dashed #2563eb' : 'none', outlineOffset: 4 }}>
      <svg width={w} height={h} style={{ display: 'block', overflow: 'visible', pointerEvents: 'none' }}>
        {/* Fat transparent hit path — ONLY the wire stroke is clickable.
            Events bubble from here to the React Flow wrapper, triggering normal
            node selection and body drag without any bounding-box click pollution. */}
        <path
          d={pathD}
          fill="none"
          stroke="transparent"
          strokeWidth={12}
          style={{ pointerEvents: 'stroke', cursor: 'move' }}
        />
        {/* Visible wire */}
        <path d={pathD} fill="none" stroke={selected ? '#2563eb' : '#333'} strokeWidth={1.5}
              strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: 'none' }} />
        {/* Segment drag handles — same style as WireEdge.
            Shown on all segments; suppressed on first/last only when that
            endpoint is anchored to a component handle (can't move it). */}
        {selected && localSegs.map((seg, i) => {
          if (i === 0) {
            const pt = { x: ox + data.relPts[0].x, y: oy + data.relPts[0].y };
            if (findHandle(pt)) return null;
          }
          if (i === localSegs.length - 1) {
            const rp = data.relPts;
            const pt = { x: ox + rp[rp.length - 1].x, y: oy + rp[rp.length - 1].y };
            if (findHandle(pt)) return null;
          }
          const len = Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
          if (len < 16) return null;
          return (
            <circle key={`seg-${i}`}
              cx={(seg.x1 + seg.x2) / 2} cy={(seg.y1 + seg.y2) / 2}
              r={SEG_HANDLE_R} className="nodrag"
              fill="white" stroke="#2563eb" strokeWidth={1.5}
              style={{ pointerEvents: 'visiblePainted', cursor: seg.isH ? 'ns-resize' : 'ew-resize' }}
              onMouseDown={(e) => onSegMouseDown(e, i)} />
          );
        })}
        {/* Endpoint drag handles — need explicit pointerEvents:'visiblePainted' so they
            can receive events despite the SVG parent having pointerEvents:'none'. */}
        {data.relPts.map((p, i) => {
          if (i !== 0 && i !== data.relPts.length - 1) return null;
          // Show a ball dot ONLY when this endpoint lands on another wire segment.
          // This covers T-junctions and endpoint-to-endpoint meetings regardless of
          // whether the wire visually looks snapped to a component or is simply floating.
          const absPt = { x: ox + p.x, y: oy + p.y };
          const BALL_TOL = 4; // px — tighter than snap so floating ends have no ball
          const onWire = otherSegs.some(s => {
            // Distance from point to segment (axis-aligned wires only, so use the simple form)
            const minX = Math.min(s.x1, s.x2), maxX = Math.max(s.x1, s.x2);
            const minY = Math.min(s.y1, s.y2), maxY = Math.max(s.y1, s.y2);
            const cx = Math.max(minX, Math.min(maxX, absPt.x));
            const cy = Math.max(minY, Math.min(maxY, absPt.y));
            return Math.hypot(cx - absPt.x, cy - absPt.y) < BALL_TOL;
          });
          return selected ? (
            // className='nodrag' tells React Flow to skip its drag initiation
            // when mousedown fires on this circle, so only our onEndptMouseDown
            // handler runs — not the node body drag.
            <circle key={i} cx={p.x} cy={p.y} r={6}
              className="nodrag"
              fill="white" stroke="#2563eb" strokeWidth={1.5}
              style={{ pointerEvents: 'visiblePainted', cursor: 'crosshair' }}
              onMouseDown={(e) => onEndptMouseDown(e, i)} />
          ) : onWire ? (
            <circle key={i} cx={p.x} cy={p.y} r={3} fill="#333"
              style={{ pointerEvents: 'none' }} />
          ) : null;
        })}
      </svg>
    </div>
  );
};

// Tiny dot node used as a free-floating wire endpoint (not attached to a component pin).
const WireAnchorNode = ({ selected }) => (
  <div style={{
    width: 8, height: 8, borderRadius: 4, background: '#555',
    outline: selected ? '2px solid #2563eb' : 'none', outlineOffset: 2,
    position: 'relative',
  }}>
    <Handle type="source" position={Position.Right} id="out" style={{ background: 'transparent', border: 'none', width: 2, height: 2, minWidth: 0, minHeight: 0 }} />
    <Handle type="target" position={Position.Left}  id="in"  style={{ background: 'transparent', border: 'none', width: 2, height: 2, minWidth: 0, minHeight: 0 }} />
  </div>
);

const nodeTypes = {
  resistor: ResistorNode,
  capacitor: CapacitorNode,
  ironCoreInductor: IronCoreInductorNode,
  triode: TriodeNode,
  pentode: PentodeNode,
  beamTetrode: BeamTetrodeNode,
  dualTriode: DualTriodeNode,
  ground: GroundNode,
  powerRail: PowerRailNode,
  pot: PotNode,
  inputJack: QuarterInchJackNode,
  speakerJack: SpeakerJackNode,
  stdInputJack: StandardInputJackNode,
  stdSpeakerJack: StandardSpeakerJackNode,
  transformer: TransformerNode,
  powerTransformer: PowerTransformerNode,
  rectifierTube: RectifierTubeNode,
  diode: DiodeNode,
  bridgeRectifier: BridgeRectifierNode,
  fuse: FuseNode,
  switch: SwitchNode,
  impedanceSwitch: ImpedanceSwitchNode,
  voltageSwitch: VoltageSwitchNode,
  wallPlug: WallPlugNode,
  loudspeaker: LoudspeakerNode,
  zenerDiode: ZenerDiodeNode,
  led: LedNode,
  npnTransistor: NpnTransistorNode,
  pnpTransistor: PnpTransistorNode,
  neonLamp: NeonLampNode,
  testPoint: TestPointNode,
  dpdtSwitch: DpdtSwitchNode,
  relay: RelayNode,
  spstRelay: SpstRelayNode,
  dpdtRelay: DpdtRelayNode,
  toroidalTransformer: ToroidalTransformerNode,
  trimmer: TrimmerNode,
  vrTube: VRTubeNode,
  reverbTank: ReverbTankNode,
  rotarySwitch: RotarySwitchNode,
  nJFET: NJFETNode,
  pJFET: PJFETNode,
  opAmp: OpAmpNode,
  voltageRegIC: VoltageRegICNode,
  iecInlet: IecInletNode,
  thermistor: ThermistorNode,
  pushPullPot: PushPullPotNode,
  netLabel: NetLabelNode,
  wireAnchor: WireAnchorNode,
  freeWire: FreeWireNode,
  note: NoteNode,
};

// ─── Custom edge: WireEdge ───────────────────────────────────────────────────
// Features:
//  • Fan-out: parallel wires from the same handle are spread 5 px apart
//  • Segment drag handles: blue circles on each straight segment when selected;
//    drag perpendicular to the segment to reposition it (stored as waypoints).
//    Double-click the wire to reset it to auto-routing.
//  • Jump arcs: horizontal wires arc upward (radius 6 px) over every vertical
//    wire they cross, so it is visually clear they are not connected.

const WIRE_OFFSET  = 20; // px fan-out: interior column/row offset per parallel wire
const JUMP_R       = 6;  // px radius of crossing-jump arc
const SEG_HANDLE_R = 5;  // px radius of segment drag handle
const SNAP_GRID    = 10; // px snap grid for segment dragging
const STUB         = 30; // px mandatory exit stub perpendicular to pin face

const snapV = v => Math.round(v / SNAP_GRID) * SNAP_GRID;

// Remove adjacent duplicate points (within 0.5 px) from a point array.
const dedupPts = pts => pts.filter((p, i) =>
  i === 0 ||
  Math.abs(p.x - pts[i - 1].x) > 0.5 ||
  Math.abs(p.y - pts[i - 1].y) > 0.5
);

// Build a strictly-orthogonal path with the fewest possible segments.
//
//   Z-path (opposite stubs, e.g. right↔left): 4 pts / 3 segs
//     [src, (col,sy), (col,ty), tgt]  — H, V, H
//   L-path (perpendicular stubs):        3 pts / 2 segs  (normal layout)
//     [src, (tx,sy) or (sx,ty), tgt]   — H+V or V+H
//   U-path (same-direction stubs):       7 pts / 6 segs
//
// The stub clearance (STUB px) is encoded as a constraint on where the spine
// column / corner lands — NOT as an explicit extra waypoint in the array.
// fanOffset shifts the interior column/row so parallel wires spread apart.
//   seg2: interior spine  (the draggable centre, offset by fanOffset)
//   seg3: corner interior routing spine → target stub-end
//   seg4: stub into target pin  (always perfectly H or V)
//
// fanOffset shifts the interior column (srcH) or row (!srcH) so parallel
// wires from the same handle spread apart without touching pin stubs.
const autoRoutePts = (sx, sy, tx, ty, srcPos, tgtPos, fanOffset = 0) => {
  const srcH = srcPos === 'left' || srcPos === 'right';

  // ── Adaptive stub length ──────────────────────────────────────────────────
  // For opposite-facing stubs (right↔left, top↔bottom) pointing toward each
  // other, the two stubs share the gap between pins.  If that gap is smaller
  // than 2×STUB the stubs would cross, making the Z-path reverse direction and
  // produce an overshoot.  Shrink both stubs to half the available gap so they
  // always meet exactly in the middle — down to 1 px if needed.
  // For same-direction and perpendicular stubs the full STUB is always valid.
  const oppGap =
    srcPos === 'right'  && tgtPos === 'left'   ? tx - sx :
    srcPos === 'left'   && tgtPos === 'right'  ? sx - tx :
    srcPos === 'bottom' && tgtPos === 'top'    ? ty - sy :
    srcPos === 'top'    && tgtPos === 'bottom' ? sy - ty :
    Infinity;
  const stub = (oppGap > 0 && oppGap < 2 * STUB) ? Math.max(1, oppGap / 2) : STUB;

  const srcDx = srcPos === 'right' ?  stub : srcPos === 'left' ? -stub : 0;
  const srcDy = srcPos === 'bottom' ? stub : srcPos === 'top'  ? -stub : 0;
  const tgtDx = tgtPos === 'right' ?  stub : tgtPos === 'left' ? -stub : 0;
  const tgtDy = tgtPos === 'bottom' ? stub : tgtPos === 'top'  ? -stub : 0;

  // Stub endpoints — always perfectly axial from the pin, never fan-offset
  const p1 = { x: sx + srcDx, y: sy + srcDy };
  const p4 = { x: tx + tgtDx, y: ty + tgtDy };

  // ── Same-direction stubs: U-shaped 8-point path ───────────────────────────
  // When both stubs exit the same face (e.g. both 'right') the 5-segment
  // Z-path would require the interior column to sit between the two stub
  // endpoints, but one of those stubs always points AWAY from that column.
  // The segment from the stub end to the column would reverse direction,
  // producing a visible overshoot/hook.  Instead, route a U around the
  // outside: bypass column/row beyond both stubs, then connect straight across.
  //
  //  src  →  p1  →  p2         p5  ←  p4  ←  tgt
  //                  ↓          ↑
  //                  p3  →→→→  p4'   (horizontal bridge at midpoint Y)
  //
  // For horizontal stubs (left/right) the bypass is a vertical column beyond
  // the furthest stub-end X.  For vertical stubs (top/bottom) it is a row.
  if (srcPos === tgtPos) {
    if (srcH) {
      // Both left or both right — bypass column to the outside
      const outerX = srcPos === 'right'
        ? snapV(Math.max(p1.x, p4.x) + STUB + fanOffset)
        : snapV(Math.min(p1.x, p4.x) - STUB + fanOffset);
      const midY = snapV((p1.y + p4.y) / 2);
      return [
        { x: sx, y: sy },
        p1,
        { x: outerX, y: p1.y },
        { x: outerX, y: midY },
        { x: outerX, y: p4.y },
        p4,
        { x: tx, y: ty },
      ];
    } else {
      // Both top or both bottom — bypass row to the outside
      const outerY = srcPos === 'bottom'
        ? snapV(Math.max(p1.y, p4.y) + STUB + fanOffset)
        : snapV(Math.min(p1.y, p4.y) - STUB + fanOffset);
      const midX = snapV((p1.x + p4.x) / 2);
      return [
        { x: sx, y: sy },
        p1,
        { x: p1.x, y: outerY },
        { x: midX,  y: outerY },
        { x: p4.x,  y: outerY },
        p4,
        { x: tx, y: ty },
      ];
    }
  }

  // ── Opposite-direction stubs: Z-path (3 segments, 4 points) ───────────────
  // [src, (col, sy), (col, ty), tgt]  for H stubs (left↔right)
  // [src, (sx, row), (tx, row), tgt]  for V stubs (top↔bottom)
  // The stub clearance is embedded in the col/row clamp — no explicit stub
  // waypoints are added, so the path is naturally 3 segments.
  // dedupPts collapses to 1 segment when sx=tx (or sy=ty) — straight wire.
  if ((srcPos === 'left' && tgtPos === 'right') ||
      (srcPos === 'right' && tgtPos === 'left') ||
      (srcPos === 'top' && tgtPos === 'bottom') ||
      (srcPos === 'bottom' && tgtPos === 'top')) {
    if (srcH) {
      const lo = Math.min(p1.x, p4.x), hi = Math.max(p1.x, p4.x);
      const col = Math.min(hi, Math.max(lo, snapV((p1.x + p4.x) / 2) + fanOffset));
      return dedupPts([{ x: sx, y: sy }, { x: col, y: sy }, { x: col, y: ty }, { x: tx, y: ty }]);
    } else {
      const lo = Math.min(p1.y, p4.y), hi = Math.max(p1.y, p4.y);
      const row = Math.min(hi, Math.max(lo, snapV((p1.y + p4.y) / 2) + fanOffset));
      return dedupPts([{ x: sx, y: sy }, { x: sx, y: row }, { x: tx, y: row }, { x: tx, y: ty }]);
    }
  }

  // ── Perpendicular stubs: L-path (2 segments, 3 points — normal layout) ────
  // Natural corner = intersection of the two exit-direction axis lines:
  //   srcH → corner at (tx, sy):  H to target column, then V to target pin
  //   srcV → corner at (sx, ty):  V to target row,    then H to target pin
  // This works (no backtracking) when:
  //   • the corner is at least STUB beyond the source pin in its exit direction
  //   • the last arm approaches the target pin in the correct entry direction
  // If either condition fails, fall back to stub-based routing (≤4 segments).
  {
    const tgtH = tgtPos === 'left' || tgtPos === 'right';
    const nc = srcH ? { x: tx, y: sy } : { x: sx, y: ty };

    // Natural corner works when:
    //   (a) the corner is strictly in the source exit direction — no backtrack.
    //       (We don't require a full STUB of clearance here; the 4-seg fallback
    //       only kicks in when the corner requires going *backwards* from the
    //       source face, which would produce a visible hook/overshoot.)
    //   (b) the last arm approaches the target pin in its correct entry direction
    //       so the wire doesn't route through the component body.
    const ncSrcOK = srcH
      ? (srcPos === 'right' && tx >= sx) || (srcPos === 'left'  && tx <= sx)
      : (srcPos === 'bottom' && ty >= sy) || (srcPos === 'top' && ty <= sy);

    // Last segment nc→tgt must travel in the correct entry direction for tgtPos.
    // srcH → nc=(tx,sy), last seg is vertical: check sy vs ty.
    // srcV → nc=(sx,ty), last seg is horizontal: check sx vs tx.
    const ncTgtOK = srcH
      ? (tgtPos === 'top'    && sy <= ty) ||
        (tgtPos === 'bottom' && sy >= ty)
      : (tgtPos === 'right'  && sx <= tx) ||
        (tgtPos === 'left'   && sx >= tx);

    if (ncSrcOK && ncTgtOK) {
      return dedupPts([{ x: sx, y: sy }, nc, { x: tx, y: ty }]);
    }

    // Fallback: stub-based corner selection (handles unusual layouts).
    const cA = { x: p1.x, y: p4.y };
    const cB = { x: p4.x, y: p1.y };
    const backtrackA =
      ( srcH ? Math.max(0, srcPos === 'right'  ? p1.x - cA.x : cA.x - p1.x) : 0) +
      (!srcH ? Math.max(0, srcPos === 'bottom' ? p1.y - cA.y : cA.y - p1.y) : 0) +
      ( tgtH ? Math.max(0, tgtPos === 'right'  ? p4.x - cA.x : cA.x - p4.x) : 0) +
      (!tgtH ? Math.max(0, tgtPos === 'bottom' ? p4.y - cA.y : cA.y - p4.y) : 0);
    const backtrackB =
      ( srcH ? Math.max(0, srcPos === 'right'  ? p1.x - cB.x : cB.x - p1.x) : 0) +
      (!srcH ? Math.max(0, srcPos === 'bottom' ? p1.y - cB.y : cB.y - p1.y) : 0) +
      ( tgtH ? Math.max(0, tgtPos === 'right'  ? p4.x - cB.x : cB.x - p4.x) : 0) +
      (!tgtH ? Math.max(0, tgtPos === 'bottom' ? p4.y - cB.y : cB.y - p4.y) : 0);
    const c = backtrackA <= backtrackB ? cA : cB;
    return dedupPts([{ x: sx, y: sy }, p1, c, p4, { x: tx, y: ty }]);
  }
};

// ── Obstacle avoidance ────────────────────────────────────────────────────────
// Pushes the interior segments of an orthogonal path away from node bounding
// boxes.  Only auto-routed paths (no manual waypoints) are adjusted.
const WIRE_CLEARANCE = 20; // px padding around nodes when routing

const avoidNodes = (pts, boxes) => {
  if (!boxes.length) return pts;
  let result = pts.map(p => ({ ...p }));
  // Up to 4 passes in case pushing one segment exposes another overlap.
  // Only push the interior spine segments (i=2 to segs.length-3).
  // Pushing i=1 or i=segs.length-2 (the corner segments adjacent to the stubs)
  // would change a shared endpoint with the stub, making the stub diagonal.
  for (let pass = 0; pass < 4; pass++) {
    const segs = ptsToSegs(result);
    let changed = false;
    // For 4-point Z-paths (3 segs), spine is seg[1]; loop range 1..segs.length-2.
    // For any other path length, same range is safe — endpoints are never moved.
    for (let i = 1; i < segs.length - 1; i++) {
      const s = segs[i];
      for (const b of boxes) {
        const bx1 = b.x - WIRE_CLEARANCE;
        const bx2 = b.x + b.w + WIRE_CLEARANCE;
        const by1 = b.y - WIRE_CLEARANCE;
        const by2 = b.y + b.h + WIRE_CLEARANCE;
        if (s.isH) {
          const sxMin = Math.min(s.x1, s.x2), sxMax = Math.max(s.x1, s.x2);
          if (s.y1 >= by1 && s.y1 <= by2 && sxMin <= bx2 && sxMax >= bx1) {
            // Push above or below — whichever is closer, snapped to grid
            const newY = snapV(
              Math.abs(s.y1 - by1) <= Math.abs(s.y1 - by2) ? by1 : by2
            );
            result[i].y     = newY;
            result[i + 1].y = newY;
            changed = true;
          }
        } else {
          const syMin = Math.min(s.y1, s.y2), syMax = Math.max(s.y1, s.y2);
          if (s.x1 >= bx1 && s.x1 <= bx2 && syMin <= by2 && syMax >= by1) {
            // Push left or right — whichever is closer, snapped to grid
            const newX = snapV(
              Math.abs(s.x1 - bx1) <= Math.abs(s.x1 - bx2) ? bx1 : bx2
            );
            result[i].x     = newX;
            result[i + 1].x = newX;
            changed = true;
          }
        }
      }
    }
    if (!changed) break;
  }
  return result;
};

// Build [{x1,y1,x2,y2,isH}] segment list from point array
const ptsToSegs = (pts) =>
  pts.slice(0, -1).map((p, i) => ({
    x1: p.x, y1: p.y, x2: pts[i + 1].x, y2: pts[i + 1].y,
    isH: Math.abs(pts[i + 1].y - p.y) < 1,
  }));

// Segment A × Segment B intersection; returns {t, x, y} or null.
// t = parameter along the H wire (segment A); exclude near its endpoints (≤3%)
//   since those indicate an intentional connection rather than a crossing.
// u = parameter along the crossing wire (segment B); only check it is strictly
//   on the segment (0 < u < 1). The old 3% exclusion on u was over-aggressive:
//   it suppressed arcs whenever the crossing landed near the *endpoint of the
//   other wire*, even though that is completely normal geometry (e.g. a wire
//   ending at the same Y as the H wire). The result was position-dependent
//   arc failures when a component pin happened to align with a wire endpoint.
const segXsect = (ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) => {
  const dx = ax2 - ax1, dy = ay2 - ay1;
  const ex = bx2 - bx1, ey = by2 - by1;
  const den = dx * ey - dy * ex;
  if (Math.abs(den) < 1e-6) return null;
  const fx = bx1 - ax1, fy = by1 - ay1;
  const t = (fx * ey - fy * ex) / den;
  const u = (fx * dy - fy * dx) / den;
  if (t <= 0.03 || t >= 0.97 || u <= 1e-9 || u >= 1 - 1e-9) return null;
  return { t, x: ax1 + t * dx, y: ay1 + t * dy };
};

// Build final SVG path, inserting semicircular jump arcs on horizontal
// segments at every crossing listed in jumpsBySegIdx.
// Arc is counter-clockwise (sweep=0) so it bows upward in screen space.
const buildSvgPath = (segs, jumpsBySegIdx) => {
  if (!segs.length) return '';
  let d = `M ${segs[0].x1} ${segs[0].y1}`;
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    const goRight = s.x2 >= s.x1;
    const goDown  = s.y2 >= s.y1;
    const jmps = [...(jumpsBySegIdx[i] || [])].sort((a, b) =>
      s.isH ? (goRight ? a.x - b.x : b.x - a.x) : (goDown ? a.y - b.y : b.y - a.y)
    );
    for (const j of jmps) {
      if (s.isH) {
        // Only draw arc if it fits entirely within the segment — skip if the
        // arc would overshoot either endpoint, which creates a visible spike.
        const lo = Math.min(s.x1, s.x2), hi = Math.max(s.x1, s.x2);
        if (j.x - JUMP_R < lo || j.x + JUMP_R > hi) continue;
        if (goRight) {
          // Left-to-right: approach from left side of arc, sweep=0 (CCW) bows upward in SVG
          d += ` L ${j.x - JUMP_R} ${s.y1}`;
          d += ` A ${JUMP_R} ${JUMP_R} 0 0 0 ${j.x + JUMP_R} ${s.y1}`;
        } else {
          // Right-to-left: approach from right side of arc, sweep=1 (CW) also bows upward in SVG
          d += ` L ${j.x + JUMP_R} ${s.y1}`;
          d += ` A ${JUMP_R} ${JUMP_R} 0 0 1 ${j.x - JUMP_R} ${s.y1}`;
        }
      }
      // Vertical segs don't get jumps — the arc on the horizontal wire is sufficient
    }
    d += ` L ${s.x2} ${s.y2}`;
  }
  return d;
};

const WireEdge = ({
  id, sourceX, sourceY, targetX, targetY,
  sourcePosition, targetPosition,
  source, sourceHandle, target,
  selected, style = {}, markerEnd, data = {},
}) => {
  const allEdges = useEdges();
  const allNodes = useNodes();
  const { setEdges, screenToFlowPosition } = useReactFlow();
  const draggingRef = useRef(null);

  // ── Use exact pin coords from React Flow ───────────────────────────────
  // Do NOT snap these to the grid.  Snapping shifts the wire start/end away
  // from the actual handle dot, causing a visible gap between the connector
  // and the wire.  Orthogonality is guaranteed without snapping because:
  //   • stubs are always purely H or V  (srcDx XOR srcDy is zero)
  //   • interior col/row is computed with snapV(), so all bend points land
  //     on the grid regardless of whether the pin coords are grid-aligned.
  const sx = sourceX, sy = sourceY;
  const tx = targetX, ty = targetY;

  // ── Fan-out: spread parallel wires along interior column/row ───────────
  // Offset is applied to the interior routing spine, NOT to pin stubs,
  // so stubs remain axially aligned with the handle face.
  // Snap to grid so fan-offsets never introduce sub-grid alignment.
  const siblings = allEdges.filter(
    e => e.source === source && e.sourceHandle === (sourceHandle ?? null)
  );
  const sibIdx = siblings.findIndex(e => e.id === id);
  const fanOffset = snapV((sibIdx - (siblings.length - 1) / 2) * WIRE_OFFSET);

  // ── Build node bounding boxes for obstacle avoidance ────────────────────
  // n.measured is set by React Flow after DOM measurement (most reliable).
  // n.data.width is set by NodeResizer when the user manually resizes.
  // n.width is the top-level flow property (rarely set for custom nodes).
  // Fall back to the SVG symbol's default size (SYMBOL_SIZE=60) if all else fails.
  //
  // NOTE: source and target are intentionally NOT excluded.  The avoidance
  // loop only touches the interior spine (i=2), never the stub segments that
  // exit from / enter into source and target.  Excluding source/target caused
  // the spine to route through the source node body when the midpoint column
  // fell inside the source's x-extent (e.g. a transformer with left-side
  // handles where the auto-routed column lands inside the transformer box).
  const nodeBBoxes = allNodes
    .filter(n => n.type !== 'freeWire') // free wire paths are not obstacles
    .map(n => ({
      x: n.position.x,
      y: n.position.y,
      w: n.measured?.width  ?? n.data?.width  ?? n.width  ?? SYMBOL_SIZE,
      h: n.measured?.height ?? n.data?.height ?? n.height ?? SYMBOL_SIZE,
    }));

  // ── Stub exit direction ───────────────────────────────────────────────────
  // FixedHandle already passes position={rotatePos(basePosition, rotation)} to
  // <Handle>, so xyflow stores the post-rotation face and delivers it here as
  // sourcePosition / targetPosition.  Use them directly — no second rotatePos.
  const srcPos = sourcePosition;
  const tgtPos = targetPosition;

  // ── Build path points ────────────────────────────────────────────────────
  const waypoints = data?.waypoints;
  let basePts;
  if (waypoints && waypoints.length > 0) {
    if (data?.manualDraw) {
      // Hand-drawn wire: waypoints contain ALL points (start through end).
      // Render them verbatim — no pin-axis alignment, no stub correction.
      // The wire retains exactly the shape the user drew.
      basePts = waypoints.map(p => ({ ...p }));
    } else {
    // Segment-dragged waypoints (interior only) with pin-axis alignment.
    //
    // Waypoints are stored as absolute flow coordinates.  The pin coords
    // sx/sy/tx/ty can drift by a sub-pixel fraction between renders (xyflow
    // remeasures the DOM after zoom/layout changes).  This makes segments that
    // should be exactly horizontal or vertical appear diagonal.
    //
    // Fix: propagate each pin's axis value through every consecutive waypoint
    // that sits on the same collinear run (within 2px tolerance — sub-pixel
    // drift is always <1px, the snap grid is 10px so genuinely different rows/
    // columns always differ by ≥10px).
    //
    //  H source stub (left/right): walk wps[0], wps[1], ... inward forcing y=sy
    //  while each successive point is within 2px of sy.
    //  H target stub:              walk wps[last], wps[last-1], ... inward forcing y=ty.
    //  V stubs: same logic applied to x.
    const wps = waypoints.map(p => ({ ...p }));
    const srcIsH = srcPos === 'left' || srcPos === 'right';
    const tgtIsH = tgtPos === 'left' || tgtPos === 'right';

    // The source stub end (wps[0]) MUST lie on the same row (H stub) or column
    // (V stub) as the source pin — unconditionally.  Then propagate the same
    // axis value through any consecutive waypoints that shared the same original
    // axis value (handles zero-length first-bend case without breaking Z-paths).
    if (srcIsH) {
      const origY = wps[0].y;
      wps[0].y = sy;
      for (let i = 1; i < wps.length; i++) {
        if (Math.abs(wps[i].y - origY) < 1) wps[i].y = sy; else break;
      }
    } else {
      const origX = wps[0].x;
      wps[0].x = sx;
      for (let i = 1; i < wps.length; i++) {
        if (Math.abs(wps[i].x - origX) < 1) wps[i].x = sx; else break;
      }
    }
    // Same treatment for the target stub arm, walking backward from wps[last].
    const last = wps.length - 1;
    if (tgtIsH) {
      const origY = wps[last].y;
      wps[last].y = ty;
      for (let i = last - 1; i >= 0; i--) {
        if (Math.abs(wps[i].y - origY) < 1) wps[i].y = ty; else break;
      }
    } else {
      const origX = wps[last].x;
      wps[last].x = tx;
      for (let i = last - 1; i >= 0; i--) {
        if (Math.abs(wps[i].x - origX) < 1) wps[i].x = tx; else break;
      }
    }

    basePts = [{ x: sx, y: sy }, ...wps, { x: tx, y: ty }];
    } // end else (segment-dragged waypoints)
  } else {
    // Auto-route then push around any overlapping nodes.
    // avoidNodes is only valid for the 6-point Z-path (opposite-direction stubs).
    // U-paths (7 points, same-direction stubs) already bypass to the outside and
    // avoidance would corrupt their outer-column points, producing diagonal stubs.
    const autoPts = autoRoutePts(sx, sy, tx, ty, srcPos, tgtPos, fanOffset);
    // avoidNodes is only safe for Z-paths (4 points, opposite-direction stubs).
    // U-paths (7 pts) and L-paths (2–4 pts) are skipped.
    if (autoPts.length === 4) {
      const avoided = avoidNodes(autoPts, nodeBBoxes);
      // Re-clamp the spine (pts[1] and pts[2]) so avoidance can never push it
      // past either pin's stub clearance, which would reverse a stub segment.
      // Use the stub-end values (p1/p4) as the valid range bounds.
      const srcIsH = srcPos === 'left' || srcPos === 'right';
      const srcDx = srcPos === 'right' ?  STUB : srcPos === 'left' ? -STUB : 0;
      const srcDy = srcPos === 'bottom' ? STUB : srcPos === 'top'  ? -STUB : 0;
      const tgtDx = tgtPos === 'right' ?  STUB : tgtPos === 'left' ? -STUB : 0;
      const tgtDy = tgtPos === 'bottom' ? STUB : tgtPos === 'top'  ? -STUB : 0;
      if (srcIsH) {
        const lo = Math.min(sx + srcDx, tx + tgtDx);
        const hi = Math.max(sx + srcDx, tx + tgtDx);
        avoided[1].x = Math.min(hi, Math.max(lo, avoided[1].x));
        avoided[2].x = avoided[1].x;
      } else {
        const lo = Math.min(sy + srcDy, ty + tgtDy);
        const hi = Math.max(sy + srcDy, ty + tgtDy);
        avoided[1].y = Math.min(hi, Math.max(lo, avoided[1].y));
        avoided[2].y = avoided[1].y;
      }
      basePts = avoided;
    } else {
      basePts = autoPts;
    }
  }
  const mySegs = ptsToSegs(basePts);

  // Keep refs so drag closures always see the latest computed values
  const basePtsRef = useRef(basePts);
  basePtsRef.current = basePts;
  const mySegsRef = useRef(mySegs);
  mySegsRef.current = mySegs;

  // ── Cache segments in edge.data.$segs for other edges' jump detection ────
  // Also cache $noJump so crossing H-wires can skip the arc when this wire
  // is marked as "connected at crossings".
  const noJump = !!data.noJump;
  const segsKey = mySegs
    .map(s => `${Math.round(s.x1)},${Math.round(s.y1)},${Math.round(s.x2)},${Math.round(s.y2)}`)
    .join('|');
  const cacheKey = `${segsKey}|nj=${noJump}`;
  const prevSegsKeyRef = useRef('');
  useEffect(() => {
    if (prevSegsKeyRef.current === cacheKey) return;
    prevSegsKeyRef.current = cacheKey;
    setEdges(eds =>
      eds.map(e => e.id !== id ? e : { ...e, data: { ...e.data, $segs: mySegs, $noJump: noJump } })
    );
  }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Detect crossings: my H segs vs other edges' and free-wire V segs ─────
  // FreeWireNode paths live in the node list (not edges), so also convert their
  // relative points to absolute flow coords and include them in the check.
  const jumpsBySegIdx = {};
  // Only build jump arcs if this wire itself is not marked as connected-at-crossings.
  if (!noJump) {
    const addCrossings = (otherSegs) => {
      for (let i = 0; i < mySegs.length; i++) {
        const ms = mySegs[i];
        if (!ms.isH) continue; // only H segs get arcs; V segs are unmodified
        for (const os of otherSegs) {
          if (os.isH) continue; // only H×V crossings produce arcs
          const hit = segXsect(ms.x1, ms.y1, ms.x2, ms.y2, os.x1, os.y1, os.x2, os.y2);
          if (hit) {
            if (!jumpsBySegIdx[i]) jumpsBySegIdx[i] = [];
            jumpsBySegIdx[i].push(hit);
          }
        }
      }
    };
    for (const other of allEdges) {
      if (other.id === id) continue;
      if (!other.data?.$segs) continue;
      // Skip: if the other wire is also connected-at-crossings, no arc needed.
      if (other.data.$noJump) continue;
      addCrossings(other.data.$segs);
    }
    // For freeWire nodes, check their noJump flag too.
    const nonJumpFreeWireSegs = allNodes
      .filter(n => n.type === 'freeWire' && !n.data?.noJump && n.data?.relPts?.length > 1)
      .flatMap(n => {
        const pts = n.data.relPts.map(p => ({ x: n.position.x + p.x, y: n.position.y + p.y }));
        return ptsToSegs(pts);
      });
    addCrossings(nonJumpFreeWireSegs);
  }

  // ── Final SVG path with jump arcs ────────────────────────────────────────
  const finalPath = buildSvgPath(mySegs, jumpsBySegIdx);

  // ── Segment drag ─────────────────────────────────────────────────────────
  const onHandleMouseDown = (e, segIdx) => {
    e.stopPropagation();
    e.preventDefault();
    const startFP  = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const origPts  = basePtsRef.current.map(p => ({ ...p }));
    const seg      = mySegsRef.current[segIdx];
    draggingRef.current = { segIdx, startFP, origPts, seg };

    const onMove = (me) => {
      if (!draggingRef.current) return;
      const { origPts: op, startFP: sp, seg: sg } = draggingRef.current;
      const curFP = screenToFlowPosition({ x: me.clientX, y: me.clientY });
      const pts = op.map(p => ({ ...p }));
      const N = pts.length;
      // pts[0] = source pin (fixed), pts[N-1] = target pin (fixed).
      //
      // Rule: moving an H segment by deltaY should update ALL interior points
      // that are part of the same collinear-Y run as the dragged segment.
      // But a point is "locked" if following same-axis segments backward
      // reaches the fixed source pin, or forward reaches the fixed target pin,
      // without crossing a perpendicular segment — because changing it would
      // make an adjacent stub diagonal.
      //
      // Example: Z-path (segs H,H,V,H,H).  All H segs sit at source-Y or
      // target-Y.  Every H point is reachable from a pin through H-only segs,
      // so no H point can change Y.  Only the V spine can be dragged (in X).
      //
      // Example: U-path bridge (segs V,V,H,H,V,V).  The three bridge points
      // are not reachable from either pin through H-only segs, so all three
      // move together when either bridge half is dragged.
      const segs = ptsToSegs(op);

      if (sg.isH) {
        const deltaY = snapV(curFP.y - sp.y);
        const origY  = sg.y1;

        // Gather all interior pts at origY
        const cands = new Set();
        for (let j = 1; j < N - 1; j++) {
          if (Math.abs(op[j].y - origY) < 1) cands.add(j);
        }

        // Purge source-locked pts: walk inward from j=1 while adjacent seg is H
        if (segs[0].isH) {
          let j = 1;
          while (j < N - 1 && cands.has(j) && segs[j - 1].isH) cands.delete(j++);
        }
        // Purge target-locked pts: walk inward from j=N-2 while adjacent seg is H
        if (segs[N - 2].isH) {
          let j = N - 2;
          while (j >= 1 && cands.has(j) && segs[j].isH) cands.delete(j--);
        }

        for (const j of cands) pts[j].y = origY + deltaY;

      } else {
        const deltaX = snapV(curFP.x - sp.x);
        const origX  = sg.x1;

        const cands = new Set();
        for (let j = 1; j < N - 1; j++) {
          if (Math.abs(op[j].x - origX) < 1) cands.add(j);
        }

        // Purge source-locked pts: walk inward from j=1 while adjacent seg is V
        if (!segs[0].isH) {
          let j = 1;
          while (j < N - 1 && cands.has(j) && !segs[j - 1].isH) cands.delete(j++);
        }
        // Purge target-locked pts: walk inward from j=N-2 while adjacent seg is V
        if (!segs[N - 2].isH) {
          let j = N - 2;
          while (j >= 1 && cands.has(j) && !segs[j].isH) cands.delete(j--);
        }

        for (const j of cands) pts[j].x = origX + deltaX;
      }

      setEdges(eds => eds.map(e =>
        e.id !== id ? e : { ...e, data: { ...e.data, waypoints: pts.slice(1, -1) } }
      ));
    };

    const onUp = () => {
      draggingRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Double-click wire to discard manual waypoints and revert to auto-route
  const onWireDblClick = (e) => {
    e.stopPropagation();
    setEdges(eds => eds.map(e =>
      e.id !== id ? e : { ...e, data: { ...e.data, waypoints: [] } }
    ));
  };

  return (
    <g>
      {/* Visible wire with jump arcs.
          NOTE: do NOT put id={id} on this path — React Flow uses the edge id
          to inject its own smoothstep d-attribute which would overwrite ours. */}
      <path
        d={finalPath}
        fill="none"
        stroke={selected ? '#2563eb' : '#333'}
        strokeWidth={selected ? 2 : 1.5}
        markerEnd={markerEnd}
        style={style}
      />
      {/* Wide transparent hit area — also captures double-click to reset routing */}
      <path
        d={finalPath}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
        onDoubleClick={onWireDblClick}
      />
      {/* Segment drag handles — shown on every segment when wire is selected.
          pts[0] (source pin) and pts[N-1] (target pin) are the only truly
          fixed points; all other points are free to move, so every segment
          can be given a drag handle safely. */}
      {selected && mySegs.map((seg, i) => {
        const len = Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
        if (len < 16) return null;
        return (
          <circle
            key={i}
            cx={(seg.x1 + seg.x2) / 2}
            cy={(seg.y1 + seg.y2) / 2}
            r={SEG_HANDLE_R}
            fill="white"
            stroke="#2563eb"
            strokeWidth={1.5}
            style={{ cursor: seg.isH ? 'ns-resize' : 'ew-resize' }}
            onMouseDown={(e) => onHandleMouseDown(e, i)}
          />
        );
      })}
    </g>
  );
};

const edgeTypes = { wire: WireEdge };

// ─── Shape palette definition ─────────────────────────────────────────────────
// ── Label auto-numbering ────────────────────────────────────────────────────
// Given a default label like "R?", "C?", "V1", "BR1" extract the letter prefix
// and return the next number not yet used by any node with that prefix.
// Returns defaultLabel unchanged for non-numeric labels ("B+", "A", etc.).
const nextAutoLabel = (currentNodes, defaultLabel) => {
  if (!defaultLabel) return defaultLabel;
  const prefixMatch = defaultLabel.match(/^([A-Za-z]+)[0-9?]+$/);
  if (!prefixMatch) return defaultLabel;
  const prefix = prefixMatch[1];
  const re = new RegExp(`^${prefix}(\\d+)$`);
  let max = 0;
  for (const n of currentNodes) {
    const m = n.data?.label?.match(re);
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `${prefix}${max + 1}`;
};

const PALETTE = [
  // ── Passives ─────────────────────────────────────────────────────────────
  { category: 'Passives', type: 'resistor',  label: 'Resistor',    icon: '⟿', defaults: { label: 'R?', value: '100k' } },
  { category: 'Passives', type: 'capacitor', label: 'Capacitor',   icon: '⊣⊢', defaults: { label: 'C?', value: '0.1µF', polar: false } },
  { category: 'Passives', type: 'capacitor', label: 'Electrolytic',icon: '⊣+⊢', defaults: { label: 'C?', value: '47µF', polar: true } },
  { category: 'Passives', type: 'ironCoreInductor', label: 'Choke', icon: '⌇⌇', defaults: { label: 'L?', value: '5H' } },
  { category: 'Passives', type: 'pot',       label: 'Pot',         icon: '⟿↗', defaults: { label: 'R?', value: '500k' } },
  { category: 'Passives', type: 'trimmer',    label: 'Trimmer Pot',              defaults: { label: 'R?', value: '100k' } },
  { category: 'Passives', type: 'thermistor', label: 'NTC Thermistor',           defaults: { label: 'RT?', value: '10Ω NTC' } },
  { category: 'Passives', type: 'pushPullPot', label: 'Push-Pull Pot',          defaults: { label: 'R?', value: '500k' } },
  // ── Tubes ─────────────────────────────────────────────────────────────────
  { category: 'Tubes', type: 'triode',      label: 'Triode',        icon: '⊙',   defaults: { label: 'V?' } },
  { category: 'Tubes', type: 'pentode',     label: 'Pentode',       icon: '⊗',   defaults: { label: 'V?' } },
  { category: 'Tubes', type: 'beamTetrode', label: 'Beam Tetrode',  icon: '⊡',   defaults: { label: 'V?', value: 'KT66' } },
  { category: 'Tubes', type: 'dualTriode',  label: 'Dual Triode',   icon: '⊙⊙',  defaults: { label: 'V?', value: '12AX7' } },
  { category: 'Tubes', type: 'rectifierTube', label: 'Rectifier',   icon: '⊙',   defaults: { label: 'V1' } },
  { category: 'Tubes', type: 'vrTube',        label: 'VR Tube',                  defaults: { label: 'V?' } },
  // ── Transformers ──────────────────────────────────────────────────────────
  { category: 'Transformers', type: 'transformer',      label: 'Output Trans', icon: '⊼⊼', defaults: { label: 'T1' } },
  { category: 'Transformers', type: 'powerTransformer', label: 'Power Trans 2W', defaults: { label: 'T2', windings: 2, hv_voltage: '', fil_voltage: '', rect_voltage: '' } },
  { category: 'Transformers', type: 'powerTransformer',    label: 'Power Trans 3W',   defaults: { label: 'T2', windings: 3, hv_voltage: '', fil_voltage: '', rect_voltage: '' } },
  { category: 'Transformers', type: 'toroidalTransformer', label: 'Toroid Trans 2W',   defaults: { label: 'T2', windings: 2, hv_voltage: '', fil_voltage: '', rect_voltage: '' } },
  { category: 'Transformers', type: 'toroidalTransformer', label: 'Toroid Trans 3W',   defaults: { label: 'T2', windings: 3, hv_voltage: '', fil_voltage: '', rect_voltage: '' } },
  // ── Connectors ────────────────────────────────────────────────────────────
  { category: 'Connectors', type: 'inputJack',     label: 'Input Jack',      icon: '⌀IN', defaults: { label: 'J1' } },
  { category: 'Connectors', type: 'speakerJack',   label: 'Spkr Jack',       icon: '⌀SP', defaults: { label: 'J3' } },
  { category: 'Connectors', type: 'stdInputJack',   label: 'Std Input Jack',  defaults: { label: 'J1' } },
  { category: 'Connectors', type: 'stdSpeakerJack', label: 'Std Spkr Jack',   defaults: { label: 'J3' } },
  { category: 'Connectors', type: 'loudspeaker',    label: 'Loudspeaker',     defaults: { label: 'LS1', width: 80, height: 70 } },
  { category: 'Connectors', type: 'iecInlet',     label: 'IEC Inlet',       defaults: { label: 'CN1' } },
  { category: 'Connectors', type: 'reverbTank',   label: 'Reverb Tank',     defaults: { label: 'RV1', width: 200, height: 60 } },
  // ── Power & Protection ────────────────────────────────────────────────────
  { category: 'Power & Prot.', type: 'diode',           label: 'Diode',        defaults: { label: 'D1', width: 30, height: 20 } },
  { category: 'Power & Prot.', type: 'zenerDiode',      label: 'Zener Diode',  defaults: { label: 'D1', width: 30, height: 20 } },
  { category: 'Power & Prot.', type: 'led',             label: 'LED',          defaults: { label: 'D1', width: 30, height: 20 } },
  { category: 'Power & Prot.', type: 'neonLamp',        label: 'Neon Lamp',    defaults: { label: 'NE1', width: 60, height: 40 } },
  { category: 'Power & Prot.', type: 'bridgeRectifier', label: 'Bridge Rect',  defaults: { label: 'BR1' } },
  { category: 'Power & Prot.', type: 'fuse',            label: 'Fuse',         defaults: { label: 'F1' } },
  { category: 'Power & Prot.', type: 'wallPlug',        label: 'Wall Plug',    defaults: { label: 'PL1' } },
  { category: 'Power & Prot.', type: 'npnTransistor',   label: 'NPN Transistor', defaults: { label: 'Q1', width: 60, height: 80 } },
  { category: 'Power & Prot.', type: 'pnpTransistor',   label: 'PNP Transistor', defaults: { label: 'Q1', width: 60, height: 80 } },
  { category: 'Power & Prot.', type: 'nJFET',         label: 'JFET N-Ch',      defaults: { label: 'Q?', width: 60, height: 80 } },
  { category: 'Power & Prot.', type: 'pJFET',         label: 'JFET P-Ch',      defaults: { label: 'Q?', width: 60, height: 80 } },
  { category: 'Power & Prot.', type: 'opAmp',         label: 'Op-Amp',         defaults: { label: 'U?', width: 100, height: 80 } },
  { category: 'Power & Prot.', type: 'voltageRegIC',  label: 'Voltage Reg IC', defaults: { label: 'U?', value: 'LM317', width: 80, height: 60 } },
  // ── Switching ─────────────────────────────────────────────────────────────
  { category: 'Switching', type: 'switch',          label: 'Switch',         defaults: { label: 'SW1' } },
  { category: 'Switching', type: 'impedanceSwitch', label: 'Imp Switch',     defaults: { label: 'SW2' } },
  { category: 'Switching', type: 'voltageSwitch',   label: 'Voltage Switch', defaults: { label: 'SW3', width: 80, height: 100 } },
  { category: 'Switching', type: 'dpdtSwitch',      label: 'DPDT Switch',    defaults: { label: 'SW1', width: 80, height: 80 } },
  { category: 'Switching', type: 'relay',           label: 'Relay (SPDT)',   defaults: { label: 'K1', width: 100, height: 60 } },
  { category: 'Switching', type: 'spstRelay',        label: 'Relay (SPST)',   defaults: { label: 'K1', width: 90,  height: 50 } },
  { category: 'Switching', type: 'dpdtRelay',        label: 'Relay (DPDT)',   defaults: { label: 'K1', width: 110, height: 90 } },
  { category: 'Switching', type: 'rotarySwitch',   label: 'Rotary Sw (SP6T)', defaults: { label: 'SW?', width: 80, height: 120 } },
  // ── Annotations ───────────────────────────────────────────────────────────
  { category: 'Annotations', type: 'ground',    label: 'Signal Gnd',  icon: '⏚',   defaults: { chassis: false } },
  { category: 'Annotations', type: 'ground',    label: 'Chassis Gnd', icon: '⏚⃝', defaults: { chassis: true } },
  { category: 'Annotations', type: 'powerRail', label: 'B+ Rail',     icon: '◇',   defaults: { label: 'B+' } },
  { category: 'Annotations', type: 'netLabel',  label: 'Net Label',   icon: '○',   defaults: { label: 'A' } },
  { category: 'Annotations', type: 'testPoint', label: 'Test Point',  defaults: { label: 'TP1', width: 30, height: 40 } },
  { category: 'Annotations', type: 'note',      label: 'Note',        icon: '✎',   defaults: { text: 'Note...' } },
];

// ─── Palette symbol previews ──────────────────────────────────────────────────
// Renders each SVG symbol clipped to an exact pixel box so it looks sharp
// inside the narrow palette sidebar.
const ScaledSymbol = ({ children, origW, origH, scale }) => (
  <div style={{ width: Math.round(origW * scale), height: Math.round(origH * scale), overflow: 'hidden', flexShrink: 0 }}>
    <div style={{ width: origW, height: origH, transform: `scale(${scale})`, transformOrigin: '0 0', lineHeight: 0 }}>
      {children}
    </div>
  </div>
);

const palettePreview = (item) => {
  switch (item.type) {
    case 'resistor':    return <ScaledSymbol origW={60} origH={60} scale={0.55}><ResistorSymbol value="" label="" /></ScaledSymbol>;
    case 'capacitor':   return <ScaledSymbol origW={60} origH={60} scale={0.55}><CapacitorSymbol value="" label="" polar={!!item.defaults.polar} /></ScaledSymbol>;
    case 'ironCoreInductor': return <ScaledSymbol origW={60} origH={60} scale={0.55}><IronCoreInductorSymbol value="" label="" /></ScaledSymbol>;
    case 'pot':         return <ScaledSymbol origW={60} origH={80} scale={0.52}><PotSymbol value="" label="" /></ScaledSymbol>;
    case 'triode':      return <ScaledSymbol origW={80} origH={80} scale={0.5}><TriodeSymbol label="" /></ScaledSymbol>;
    case 'pentode':     return <ScaledSymbol origW={80} origH={100} scale={0.5}><PentodeSymbol label="" /></ScaledSymbol>;
    case 'beamTetrode': return <ScaledSymbol origW={80} origH={100} scale={0.5}><BeamTetrodeSymbol label="" /></ScaledSymbol>;
    case 'dualTriode':  return <ScaledSymbol origW={160} origH={100} scale={0.5}><DualTriodeSymbol label="" value="12AX7" /></ScaledSymbol>;
    case 'ground':      return <ScaledSymbol origW={40} origH={40} scale={0.7}><GroundSymbol chassis={!!item.defaults.chassis} /></ScaledSymbol>;
    case 'powerRail':   return <ScaledSymbol origW={60} origH={40} scale={0.75}><PowerRailSymbol label="B+" /></ScaledSymbol>;
    case 'inputJack':   return <ScaledSymbol origW={68} origH={40} scale={0.85}><QuarterInchJackSymbol label="" speaker={false} /></ScaledSymbol>;
    case 'speakerJack': return <ScaledSymbol origW={68} origH={40} scale={0.85}><QuarterInchJackSymbol label="" speaker={true}  /></ScaledSymbol>;
    case 'stdInputJack':   return <ScaledSymbol origW={50} origH={40} scale={0.85}><StandardJackSymbol label="" speaker={false} /></ScaledSymbol>;
    case 'stdSpeakerJack': return <ScaledSymbol origW={50} origH={40} scale={0.85}><StandardJackSymbol label="" speaker={true}  /></ScaledSymbol>;
    case 'transformer':      return <ScaledSymbol origW={160} origH={80} scale={0.85}><TransformerSymbol label="" /></ScaledSymbol>;
    case 'powerTransformer': {
      const w = item.defaults.windings || 2;
      const h = w === 3 ? 120 : 100;
      return <ScaledSymbol origW={160} origH={h} scale={0.83}><PowerTransformerSymbol label="" windings={w} /></ScaledSymbol>;
    }
    case 'rectifierTube':    return <ScaledSymbol origW={80} origH={110} scale={0.65}><RectifierTubeSymbol label="" /></ScaledSymbol>;
    case 'diode':           return <ScaledSymbol origW={60} origH={40} scale={0.9}><DiodeSymbol label="" /></ScaledSymbol>;
    case 'bridgeRectifier': return <ScaledSymbol origW={90} origH={90} scale={0.65}><BridgeRectifierSymbol label="" /></ScaledSymbol>;
    case 'fuse':            return <ScaledSymbol origW={60} origH={40} scale={0.9}><FuseSymbol label="" /></ScaledSymbol>;
    case 'switch':          return <ScaledSymbol origW={60} origH={40} scale={0.85}><SwitchSymbol label="" /></ScaledSymbol>;
    case 'impedanceSwitch': return <ScaledSymbol origW={80} origH={60} scale={0.75}><ImpedanceSwitchSymbol label="" /></ScaledSymbol>;
    case 'voltageSwitch':   return <ScaledSymbol origW={80} origH={100} scale={0.65}><VoltageSwitchSymbol label="" /></ScaledSymbol>;
    case 'wallPlug':        return <ScaledSymbol origW={80} origH={60} scale={0.72}><WallPlugSymbol label="" /></ScaledSymbol>;
    case 'loudspeaker':    return <ScaledSymbol origW={80} origH={70} scale={0.72}><LoudspeakerSymbol label="" /></ScaledSymbol>;
    case 'zenerDiode':     return <ScaledSymbol origW={60} origH={40} scale={0.9}><ZenerDiodeSymbol label="" /></ScaledSymbol>;
    case 'led':            return <ScaledSymbol origW={60} origH={40} scale={0.9}><LedSymbol label="" /></ScaledSymbol>;
    case 'npnTransistor':  return <ScaledSymbol origW={60} origH={80} scale={0.6}><NpnTransistorSymbol label="" /></ScaledSymbol>;
    case 'pnpTransistor':  return <ScaledSymbol origW={60} origH={80} scale={0.6}><PnpTransistorSymbol label="" /></ScaledSymbol>;
    case 'neonLamp':       return <ScaledSymbol origW={60} origH={40} scale={0.9}><NeonLampSymbol label="" /></ScaledSymbol>;
    case 'testPoint':      return <ScaledSymbol origW={30} origH={40} scale={0.8}><TestPointSymbol label="" /></ScaledSymbol>;
    case 'dpdtSwitch':     return <ScaledSymbol origW={80} origH={80} scale={0.65}><DpdtSwitchSymbol label="" /></ScaledSymbol>;
    case 'relay':          return <ScaledSymbol origW={100} origH={60} scale={0.7}><RelaySymbol label="" /></ScaledSymbol>;
    case 'spstRelay':      return <ScaledSymbol origW={90}  origH={50} scale={0.72}><SpstRelaySymbol label="" /></ScaledSymbol>;
    case 'dpdtRelay':      return <ScaledSymbol origW={110} origH={90} scale={0.65}><DpdtRelaySymbol label="" /></ScaledSymbol>;
    case 'toroidalTransformer': {
      const w2 = item.defaults.windings || 2;
      const h2 = w2 === 3 ? 120 : 100;
      return <ScaledSymbol origW={160} origH={h2} scale={0.83}><ToroidalTransformerSymbol label="" windings={w2} /></ScaledSymbol>;
    }
    case 'trimmer':      return <ScaledSymbol origW={60} origH={80} scale={0.52}><TrimmerSymbol value="" label="" /></ScaledSymbol>;
    case 'thermistor':   return <ScaledSymbol origW={60} origH={60} scale={0.55}><ThermistorSymbol label="" /></ScaledSymbol>;
    case 'pushPullPot':  return <ScaledSymbol origW={80} origH={180} scale={0.32}><PushPullPotSymbol value="" label="" /></ScaledSymbol>;
    case 'vrTube':       return <ScaledSymbol origW={80} origH={100} scale={0.5}><VRTubeSymbol label="" /></ScaledSymbol>;
    case 'iecInlet':     return <ScaledSymbol origW={80} origH={60} scale={0.72}><IecInletSymbol label="" /></ScaledSymbol>;
    case 'reverbTank':   return <ScaledSymbol origW={200} origH={60} scale={0.45}><ReverbTankSymbol label="" /></ScaledSymbol>;
    case 'nJFET':        return <ScaledSymbol origW={60} origH={80} scale={0.6}><NJFETSymbol label="" /></ScaledSymbol>;
    case 'pJFET':        return <ScaledSymbol origW={60} origH={80} scale={0.6}><PJFETSymbol label="" /></ScaledSymbol>;
    case 'opAmp':        return <ScaledSymbol origW={100} origH={80} scale={0.55}><OpAmpSymbol label="" /></ScaledSymbol>;
    case 'voltageRegIC': return <ScaledSymbol origW={80} origH={60} scale={0.65}><VoltageRegICSymbol label="" /></ScaledSymbol>;
    case 'rotarySwitch': return <ScaledSymbol origW={80} origH={120} scale={0.52}><RotarySwitchSymbol label="" /></ScaledSymbol>;
    case 'note':        return <div style={{ fontSize: 9, color: '#999', border: '1px dashed #ccc', padding: '4px 10px', borderRadius: 4, background: '#fffde7' }}>Note</div>;
    case 'netLabel':    return <ScaledSymbol origW={40} origH={60} scale={0.6}><NetLabelSymbol label="A" /></ScaledSymbol>;
    default:            return null;
  }
};

// ─── Properties panel ─────────────────────────────────────────────────────────
const PropertiesPanel = ({ node, onChange, onDelete }) => {
  if (!node) return (
    <div style={{ padding: 16, color: '#888', fontSize: 13 }}>
      Select a component to edit its properties.
    </div>
  );

  const data = node.data;

  const update = (key, val) => onChange(node.id, { ...data, [key]: val });

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#1a202c' }}>
        {node.type?.charAt(0).toUpperCase() + node.type?.slice(1)} Properties
      </div>

      {data.label !== undefined && (
        <label style={labelStyle}>
          Label (e.g. R1)
          <input style={inputStyle} value={data.label || ''} onChange={e => update('label', e.target.value)} />
        </label>
      )}
      {data.value !== undefined && node.type !== 'transformer' && (
        <>
          <label style={labelStyle}>
            {(node.type === 'dualTriode' || node.type === 'beamTetrode') ? 'Tube type (e.g. KT66, KT88, 6L6, EL34)'
              : node.type === 'voltageRegIC' ? 'Regulator type (e.g. LM317, 7812, 7815)'
              : 'Value (e.g. 470k, 0.1µF)'}
            <input style={inputStyle} value={data.value || ''} onChange={e => update('value', e.target.value)} />
          </label>
          {node.type === 'resistor' && (
            <label style={labelStyle}>
              Wattage (e.g. 0.25W, 1W)
              <input style={inputStyle} value={data.rating || ''} onChange={e => update('rating', e.target.value || undefined)} />
            </label>
          )}
          {node.type === 'capacitor' && (
            <label style={labelStyle}>
              Voltage rating (e.g. 50V, 450V)
              <input style={inputStyle} value={data.rating || ''} onChange={e => update('rating', e.target.value || undefined)} />
            </label>
          )}
          <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={!!data.labelStacked}
              onChange={e => update('labelStacked', e.target.checked || undefined)}
            />
            Stack label & value (two lines)
          </label>
        </>
      )}
      {data.polar !== undefined && (
        <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={!!data.polar} onChange={e => update('polar', e.target.checked)} />
          Polarized (electrolytic)
        </label>
      )}
      {data.chassis !== undefined && (
        <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={!!data.chassis} onChange={e => update('chassis', e.target.checked)} />
          Chassis ground
        </label>
      )}
      {(node.type === 'powerTransformer' || node.type === 'toroidalTransformer') && (
        <>
          <label style={labelStyle}>
            Secondary windings
            <select style={inputStyle} value={data.windings || 2} onChange={e => update('windings', Number(e.target.value))}>
              <option value={2}>2 — HV + filament</option>
              <option value={3}>3 — HV + rectifier filament + filament</option>
            </select>
          </label>
          <label style={labelStyle}>
            B+ winding voltage (VAC)
            <input style={inputStyle} type="text" placeholder="e.g. 325" value={data.hv_voltage || ''} onChange={e => update('hv_voltage', e.target.value)} />
          </label>
          <label style={labelStyle}>
            Filament winding voltage (VAC)
            <input style={inputStyle} type="text" placeholder="e.g. 6.3" value={data.fil_voltage || ''} onChange={e => update('fil_voltage', e.target.value)} />
          </label>
          {(data.windings || 2) === 3 && (
            <label style={labelStyle}>
              Rectifier filament voltage (VAC)
              <input style={inputStyle} type="text" placeholder="e.g. 5" value={data.rect_voltage || ''} onChange={e => update('rect_voltage', e.target.value)} />
            </label>
          )}
        </>
      )}
      {data.text !== undefined && (
        <label style={labelStyle}>
          Note text
          <textarea
            style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
            value={data.text || ''}
            onChange={e => update('text', e.target.value)}
          />
        </label>
      )}
      {data.label2 !== undefined && (
        <label style={labelStyle}>
          Sub-label
          <input style={inputStyle} value={data.label2 || ''} onChange={e => update('label2', e.target.value)} />
        </label>
      )}

      {node.type === 'freeWire' && (
        <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!data.noJump}
            onChange={e => update('noJump', e.target.checked)}
          />
          Connected at crossings (no jump arc)
        </label>
      )}

      {node.type !== 'note' && node.type !== 'freeWire' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#4a5568', fontWeight: 600, marginBottom: 6 }}>Rotation <span style={{ color: '#999', fontWeight: 400 }}>(or press R)</span></div>
          <div style={{ display: 'flex', gap: 4 }}>
            {[0, 90, 180, 270].map(deg => (
              <button
                key={deg}
                onClick={() => update('rotation', deg === (data.rotation || 0) ? undefined : deg)}
                style={{
                  flex: 1,
                  padding: '5px 0',
                  background: (data.rotation || 0) === deg ? '#2563eb' : '#f7fafc',
                  color: (data.rotation || 0) === deg ? 'white' : '#4a5568',
                  border: '1px solid #cbd5e0',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {deg}°
              </button>
            ))}
          </div>
        </div>
      )}

      {data.label !== undefined && node.type !== 'powerRail' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: '#4a5568', fontWeight: 600, marginBottom: 6 }}>Label position</div>
          <button
            onClick={() => onChange(node.id, { ...data, labelDx: undefined, labelDy: undefined })}
            style={{ padding: '4px 10px', background: '#f7fafc', color: '#4a5568', border: '1px solid #cbd5e0', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}
          >
            ↺ Reset label position
          </button>
        </div>
      )}

      <button
        onClick={() => onDelete(node.id)}
        style={{ marginTop: 0, padding: '6px 14px', background: '#fed7d7', color: '#c53030', border: '1px solid #fc8181', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
      >
        Delete Component
      </button>
    </div>
  );
};

const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, fontSize: 12, color: '#4a5568', fontWeight: 600 };
const inputStyle = { padding: '5px 8px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: 13, color: '#1a202c' };

// ─── Wire (edge) properties panel ─────────────────────────────────────────────
const WirePropertiesPanel = ({ edge, onChange, onDelete }) => {
  if (!edge) return null;
  const data = edge.data ?? {};
  const update = (key, val) => onChange(edge.id, { ...data, [key]: val });
  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, color: '#1a202c' }}>
        Wire Properties
      </div>
      <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input
          type="checkbox"
          checked={!!data.noJump}
          onChange={e => update('noJump', e.target.checked)}
        />
        Connected at crossings (no jump arc)
      </label>
      <button
        onClick={() => onDelete(edge.id)}
        style={{ marginTop: 4, padding: '6px 14px', background: '#fed7d7', color: '#c53030', border: '1px solid #fc8181', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
      >
        Delete Wire
      </button>
    </div>
  );
};

// ─── Main Studio Component ────────────────────────────────────────────────────

let nodeIdCounter = 1;
const newId = () => `node_${Date.now()}_${nodeIdCounter++}`;

// Named target-type handle IDs across all component node types.
// xyflow's getEdgePosition looks up sourceHandle strictly in the SOURCE handle
// bucket (connectionMode=loose only relaxes the TARGET lookup).  If any of
// these IDs appear as the sourceHandle of an edge the edge is misoriented and
// will fail with error #008.  sanitizeEdges() detects and corrects this.
const TARGET_ONLY_HANDLE_IDS = new Set([
  'in',                              // NetLabel, WireAnchor
  'grid', 'screen',                  // Triode, Pentode
  'grid-a', 'grid-b',               // DualTriode
  'plate-a', 'plate-b', 'ct',       // Transformer (OT primary inputs)
  'pri-110v', 'pri-120v', 'pri-200v', 'pri-220v', 'pri-240v', 'pri-com', // PowerTransformer voltage taps
  '110v-in', '120v-in', '200v-in', '220v-in', '240v-in',    // VoltageSwitch taps
  'heater-1', 'heater-2',           // RectifierTube
  'anode',                           // Diode
  'ac-1', 'ac-2',                    // BridgeRectifier
  'a-in', 'b-in',                    // Fuse, Switch (bidirectional)
  '16ohm-in', '8ohm-in', '4ohm-in', 'common-in', // ImpedanceSwitch
  'hot-out-t', 'earth-out-t', 'neutral-out-t',   // WallPlug target mirrors
  'line-out-t',                                    // IecInlet target mirror
  'vplus',                                          // OpAmp V+ supply (target-only)
  'reg-in',                                         // VoltageRegIC input
]);

// Correct any edges where sourceHandle is a target-type handle by swapping
// source ↔ target.  Applied on load and on paste to heal diagrams saved with
// the previous buggy orientation.
const sanitizeEdges = (edges) => edges.map(e => {
  if (!e.sourceHandle || !TARGET_ONLY_HANDLE_IDS.has(e.sourceHandle)) return e;
  // Swap source ↔ target so xyflow can find sourceHandle in the source bucket.
  return {
    ...e,
    source: e.target,
    sourceHandle: e.targetHandle ?? null,
    target: e.source,
    targetHandle: e.sourceHandle,
    data: e.data?.manualDraw && e.data?.waypoints?.length
      ? { ...e.data, waypoints: [...e.data.waypoints].reverse() }
      : e.data,
  };
});

const FREE_DIAGRAM_LIMIT = 1;

const WiringDiagramStudioInner = () => {
  const { user, token, isAuthenticated } = useAuth();
  const isAdmin = user?.is_admin === true;
  const isPro = user?.subscription_tier === 'pro' || user?.subscription_tier === 'business' || user?.is_admin;
  const { screenToFlowPosition, flowToScreenPosition, getNodes, fitView, getViewport, setViewport } = useReactFlow();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [diagramTitle, setDiagramTitle] = useState('Untitled Diagram');
  const [diagramType, setDiagramType] = useState('schematic');
  const [layoutDocument, setLayoutDocument] = useState(() => createLayoutDocument());
  const [diagramId, setDiagramId] = useState(null);
  const [labelFontSize, setLabelFontSize] = useState(7);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const [saveMessage, setSaveMessage] = useState('');
  // Tracks the last non-empty saveStatus so the fade-out keeps the correct text
  // visible while the opacity transition runs (instead of going blank instantly).
  const [lastSaveStatus, setLastSaveStatus] = useState('');
  const layoutStudioRef = useRef(null);
  useEffect(() => { if (saveStatus) setLastSaveStatus(saveStatus); }, [saveStatus]);
  const [myDiagrams, setMyDiagrams] = useState([]);
  const [loadingMyDiagrams, setLoadingMyDiagrams] = useState(false);
  const [showDiagramList, setShowDiagramList] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [renamingId, setRenamingId] = useState(null);   // diagram_id being renamed inline
  const [renameValue, setRenameValue] = useState('');
  // ── Templates state ───────────────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateTab, setTemplateTab] = useState('public'); // 'public' | 'mine'
  // template being saved — { title, description, is_public } | null
  const [savingTemplate, setSavingTemplate] = useState(null);
  // My Diagrams modal top-level tab: 'diagrams' | 'templates'
  const [diagramListTab, setDiagramListTab] = useState('diagrams');
  // New Diagram picker (Blank vs. from template)
  const [showNewPicker, setShowNewPicker] = useState(false);
  const [diagramInfo, setDiagramInfo] = useState({
    author: user?.display_name || user?.username || '',
    doc_number: '',
    revision: '1.0',
    revision_date: new Date().toLocaleDateString('en-US'),
    notes: '',
  });
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [firstLayoutSaveName, setFirstLayoutSaveName] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportStatus, setExportStatus] = useState('idle');
  const exportMenuRef = useRef(null);
  const rejectUnauthorizedLayout = (type) => {
    if (type !== 'layout' || isAdmin) return false;
    setSaveStatus('error');
    setSaveMessage('Layout Studio is currently available to administrators only.');
    setShowDiagramList(false);
    setShowNewPicker(false);
    return true;
  };
  // ── Auto-save — on by default, persisted in localStorage ─────────────────
  const [autoSave, setAutoSave] = useState(() => {
    try { return localStorage.getItem('wds_autosave') !== 'off'; }
    catch { return true; }
  });
  // Active tool: 'pointer' | 'wire' | 'marquee'
  const [activeTool, setActiveTool] = useState('pointer');
  // Derived convenience — keeps all existing drawMode logic working unchanged
  const drawMode = activeTool === 'wire';
  // Draw wire mode state
  // drawState: null | { pts: [{x,y}], snapPt: {x,y}|null, startHandle: handle|null }
  const [drawState, setDrawState] = useState(null);
  // Marquee selection state: null | { x0, y0, x1, y1 } in wrapper-relative px
  const [marqueeState, setMarqueeState] = useState(null);
  // Context menu: null | { x, y, targetId, isEdge }
  const [ctxMenu, setCtxMenu] = useState(null);
  const drawStateRef = useRef(null);
  useEffect(() => { drawStateRef.current = drawState; }, [drawState]);
  // Crossing mode for newly drawn wires: false = jump arc (default), true = connected at crossings
  const [drawNoJump, setDrawNoJump] = useState(false);
  const drawNoJumpRef = useRef(false);
  // Palette category collapse state — all collapsed by default
  const [openCategories, setOpenCategories] = useState(() => new Set());
  useEffect(() => { drawNoJumpRef.current = drawNoJump; }, [drawNoJump]);

  // Close the export dropdown when clicking outside it
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExportMenu]);

  const exportDiagram = async (format) => {
    setShowExportMenu(false);
    setExportStatus('exporting');
    try {
      if (diagramType === 'layout') await layoutStudioRef.current?.exportFile(format, diagramTitle);
      else if (format === 'png') await exportToPng();
      else if (format === 'svg') await exportToSvg();
      else await exportToPdf();
      setExportStatus('idle');
    } catch (error) {
      console.error('Export failed:', error);
      setExportStatus('error');
    }
  };

  // Keep autoSaveRef in sync with the autoSave state value so timer callbacks
  // always read the latest preference without needing it in their dep array.
  useEffect(() => { autoSaveRef.current = autoSave; }, [autoSave]);
  // Persist auto-save preference whenever it changes.
  useEffect(() => {
    try { localStorage.setItem('wds_autosave', autoSave ? 'on' : 'off'); }
    catch { /* ignore storage errors */ }
  }, [autoSave]);
  const reactFlowWrapper = useRef(null);
  const selectedNodeRef = useRef(null);
  // Stores { nodeId -> position } at the start of each node drag so that
  // onNodeDragStop can compute the delta and translate manualDraw waypoints.
  const dragStartPositions = useRef(new Map());
  // Stores { edgeId -> waypoints[] } for edges whose BOTH endpoint nodes are
  // in the drag group.  Populated in onNodeDragStart, used in onNodeDrag to
  // compute translated positions from the originals each frame (not from the
  // already-translated current state), and cleared in onNodeDragStop.
  const dragStartWaypoints = useRef(new Map());
  useEffect(() => { selectedNodeRef.current = selectedNode; }, [selectedNode]);

  // ── Undo stack ────────────────────────────────────────────────────────────
  // We keep the stack and live-state refs outside React state so that pushing
  // an undo snapshot never triggers a re-render itself.
  const undoStack = useRef([]);          // [{nodes, edges}, ...]
  const nodesRef  = useRef(nodes);
  const edgesRef  = useRef(edges);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);

  // ── Auto-save refs ────────────────────────────────────────────────────────
  const autoSaveRef          = useRef(true);   // mirror of autoSave state for timer callbacks
  const autoSaveTimerRef     = useRef(null);   // pending debounce timer id
  const isLoadingDiagramRef  = useRef(false);  // true during openDiagram / newDiagram / applyTemplate
  const isFirstAutoSaveRender = useRef(true);  // skip the initial mount
  const latestSaveRef        = useRef(null);   // always points to latest save() closure

  // Call BEFORE any mutation to snapshot the current canvas state.
  const pushUndo = useCallback(() => {
    undoStack.current = [
      ...undoStack.current.slice(-49), // cap at 50 entries
      { nodes: nodesRef.current, edges: edgesRef.current },
    ];
  }, []);

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    const prev = undoStack.current[undoStack.current.length - 1];
    undoStack.current = undoStack.current.slice(0, -1);
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNode(null);
    setSelectedEdge(null);
  }, [setNodes, setEdges]);

  // ── Clipboard ───────────────────────────────────────────────────────────
  // Stored in a ref to avoid re-renders on copy/cut operations.
  const clipboardRef = useRef(null);

  const loadListSilent = useCallback(async () => {
    if (!token) {
      setMyDiagrams([]);
      return [];
    }

    try {
      const res = await fetch(`${API_URL}/wiring-diagrams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error('Failed to load wiring diagrams');
      }
      const diagrams = await res.json();
      setMyDiagrams(diagrams);
      return diagrams;
    } catch (err) {
      console.error('Load list error:', err);
      return [];
    }
  }, [token]);

  useEffect(() => {
    let isMounted = true;

    if (!token) {
      setMyDiagrams([]);
      setLoadingMyDiagrams(false);
      return undefined;
    }

    setLoadingMyDiagrams(true);
    loadListSilent().finally(() => {
      if (isMounted) setLoadingMyDiagrams(false);
    });

    return () => {
      isMounted = false;
    };
  }, [token, loadListSilent]);

  const hasReachedDiagramLimit = isAuthenticated && !isPro && !diagramId && myDiagrams.length >= FREE_DIAGRAM_LIMIT;
  const saveDisabledReason = !isAuthenticated
    ? 'Sign in to save diagrams'
    : loadingMyDiagrams && !isPro && !diagramId
      ? 'Checking save eligibility...'
      : hasReachedDiagramLimit
        ? 'Free accounts can save 1 diagram. Upgrade to Pro for unlimited diagrams.'
        : '';
  const canSave = !saveDisabledReason;
  const accessBannerText = !isAuthenticated
    ? 'Anonymous: explore only · Free: 1 saved diagram · Pro: unlimited saves + PNG/SVG/PDF export'
    : !isPro
      ? 'Free: 1 saved diagram · Pro: unlimited saves + PNG/SVG/PDF export'
      : '';

  // Collect all currently-selected nodes + their internal edges.
  const copySelection = useCallback(() => {
    const sel = nodesRef.current.filter(n => n.selected);
    if (sel.length === 0) return;
    const ids = new Set(sel.map(n => n.id));
    clipboardRef.current = {
      nodes: sel,
      edges: edgesRef.current.filter(e => ids.has(e.source) && ids.has(e.target)),
    };
  }, []);

  // Delete the current selection (multi-node + selected edges).
  const deleteSelection = useCallback(() => {
    const sel = nodesRef.current.filter(n => n.selected);
    const hasSelEdges = edgesRef.current.some(e => e.selected);
    if (sel.length === 0 && !hasSelEdges) return;
    pushUndo();
    const ids = new Set(sel.map(n => n.id));
    setNodes(nds => nds.filter(n => !ids.has(n.id)));
    setEdges(eds => eds.filter(e => !ids.has(e.source) && !ids.has(e.target) && !e.selected));
    setSelectedNode(null);
    setSelectedEdge(null);
  }, [pushUndo, setNodes, setEdges]);

  const cutSelection = useCallback(() => {
    copySelection();
    deleteSelection();
  }, [copySelection, deleteSelection]);

  // Paste with new IDs at +24,+24 offset; pasted nodes become selected.
  // Labels are auto-incremented per prefix so pasted copies never duplicate
  // existing reference designators.
  const pasteSelection = useCallback(() => {
    if (!clipboardRef.current || clipboardRef.current.nodes.length === 0) return;
    pushUndo();
    const idMap = {};
    const counter = Date.now();
    // seenNodes accumulates as we process each pasted node so that pasting
    // multiple objects at once assigns unique sequential numbers to each.
    const seenNodes = [...nodesRef.current];
    const pasted = clipboardRef.current.nodes.map((n, i) => {
      const id = `node_${counter}_paste_${i}`;
      idMap[n.id] = id;
      const origLabel = n.data?.label;
      const autoLabel = nextAutoLabel(seenNodes, origLabel);
      const newNode = {
        ...n,
        id,
        position: { x: n.position.x + 24, y: n.position.y + 24 },
        selected: true,
        data: (autoLabel && autoLabel !== origLabel)
          ? { ...n.data, label: autoLabel }
          : { ...n.data },
      };
      seenNodes.push(newNode);
      return newNode;
    });
    const pastedEdges = sanitizeEdges(
      clipboardRef.current.edges.map((e, i) => ({
        ...e,
        id: `edge_${counter}_paste_${i}`,
        source: idMap[e.source],
        target: idMap[e.target],
        selected: false,
      }))
    );
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), ...pasted]);
    setEdges(eds => [...eds, ...pastedEdges]);
    setActiveTool('pointer');  // switch to pointer so pasted items can be dragged immediately
    setDrawState(null);
  }, [pushUndo, setNodes, setEdges, setActiveTool, setDrawState]);

  const onConnect = useCallback(
    (params) => {
      pushUndo();
      // If the user dragged from a target-type handle xyflow swaps it into the
      // source position.  Detect and correct before storing so getEdgePosition
      // can always find the handle in the source bucket.
      const [fixed] = sanitizeEdges([{
        ...params,
        type: 'wire',
        animated: false,
        interactionWidth: 12,
        selectable: true,
        data: {},
      }]);
      setEdges(eds => addEdge(fixed, eds));
    },
    [pushUndo, setEdges]
  );

  // ── Draw wire helpers ──────────────────────────────────────────────────────
  // H/V snap: from prevPt toward cursorFP — keep the axis where motion is larger.
  const hvSnap = (prevPt, cursorFP) => {
    const dx = Math.abs(cursorFP.x - prevPt.x);
    const dy = Math.abs(cursorFP.y - prevPt.y);
    if (dx >= dy) return { x: snapV(cursorFP.x), y: prevPt.y };
    return { x: prevPt.x, y: snapV(cursorFP.y) };
  };

  // Find the nearest node handle within DRAW_HANDLE_SNAP flow-px of a point.
  // Uses positionAbsolute (same field xyflow's own getHandlePosition uses internally)
  // so the coordinate system is consistent with stored handleBounds.
  const DRAW_HANDLE_SNAP = 20;
  const findNearestHandle = useCallback((flowPt) => {
    let best = null, bestDist = DRAW_HANDLE_SNAP;
    for (const node of getNodes()) {
      const absX = node.internals?.positionAbsolute?.x ?? node.position.x;
      const absY = node.internals?.positionAbsolute?.y ?? node.position.y;
      const srcs = node.internals?.handleBounds?.source || [];
      const tgts = node.internals?.handleBounds?.target || [];
      for (const hb of srcs) {
        const hx = absX + hb.x + hb.width  / 2;
        const hy = absY + hb.y + hb.height / 2;
        const d  = Math.hypot(hx - flowPt.x, hy - flowPt.y);
        if (d < bestDist) {
          bestDist = d;
          best = { nodeId: node.id, handleId: hb.id || null, handleType: 'source', x: hx, y: hy };
        }
      }
      for (const hb of tgts) {
        const hx = absX + hb.x + hb.width  / 2;
        const hy = absY + hb.y + hb.height / 2;
        const d  = Math.hypot(hx - flowPt.x, hy - flowPt.y);
        if (d < bestDist) {
          bestDist = d;
          best = { nodeId: node.id, handleId: hb.id || null, handleType: 'target', x: hx, y: hy };
        }
      }
    }
    return best;
  }, [getNodes]);

  // Commit the current draw path as a WireEdge.
  // noJump: true = connected at crossings (no arc), false/undefined = jump arc (default)
  const createDrawnEdge = useCallback((pts, startHandle, endHandle, noJump) => {
    pushUndo();
    if (pts.length < 2) return;

    if (startHandle && endHandle) {
      // Ensure the edge is oriented source → target regardless of draw direction.
      // If the user started near a target-type handle and ended near a source-type
      // handle, swap them so ReactFlow can resolve the handle coordinates correctly.
      let src = startHandle, tgt = endHandle, finalPts = pts;
      if (src.handleType === 'target' && tgt.handleType === 'source') {
        src = endHandle; tgt = startHandle;
        finalPts = [...pts].reverse(); // keep wire geometry flowing src → tgt
      }
      // xyflow's getEdgePosition always looks up sourceHandle in the node's
      // SOURCE handle bucket.  connectionMode="loose" only relaxes the TARGET
      // lookup (which also searches source handles).  This means:
      //
      //   source→target  ✓ normal case
      //   source→source  ✓ targetHandle found via loose-mode source-bucket search
      //   target→source  ✓ handled above by the swap
      //   target→target  ✗ sourceHandle can never be found — must use freeWire
      //
      // Only create a proper WireEdge when the source side has a source-type handle.
      if (src.handleType === 'source') {
        setEdges(eds => eds.concat({
          id: `edge_drawn_${Date.now()}`,
          type: 'wire',
          source: src.nodeId, sourceHandle: src.handleId,
          target: tgt.nodeId, targetHandle: tgt.handleId,
          animated: false,
          interactionWidth: 12,
          selectable: true,
          data: { manualDraw: true, waypoints: finalPts, ...(noJump ? { noJump: true } : {}) },
        }));
        return;
      }
      // target→target: fall through to freeWire (both handles are targets).
    }
    // Free-form: one or both endpoints are NOT on a component pin, or both are
    // target-type handles (cannot be stored as a proper directed edge).
    // Store as a FreeWireNode (points relative to bounding-box origin) so no
    // invisible anchor dots are created.
    const pad = 6;
    const minX = Math.min(...pts.map(p => p.x)) - pad;
    const minY = Math.min(...pts.map(p => p.y)) - pad;
    const relPts = pts.map(p => ({ x: p.x - minX, y: p.y - minY }));
    setNodes(nds => nds.concat({
      id: `wire_${Date.now()}`,
      type: 'freeWire',
      position: { x: minX, y: minY },
      data: { relPts, ...(noJump ? { noJump: true } : {}) },
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setEdges, setNodes]); // pushUndo is a stable ref-based callback

  // Canvas overlay event handlers (active only in draw mode)
  const onOverlayMouseMove = useCallback((e) => {
    const ds = drawStateRef.current;
    if (!ds) return;
    const cursorFP = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const snapped = hvSnap(ds.pts[ds.pts.length - 1], cursorFP);
    setDrawState(prev => prev ? { ...prev, snapPt: snapped } : prev);
  }, [screenToFlowPosition]);

  const onOverlayClick = useCallback((e) => {
    if (e.detail === 2) return; // handled by dblclick
    const cursorFP = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const ds = drawStateRef.current;
    if (!ds) {
      // Start a new path
      const handle = findNearestHandle(cursorFP);
      const startPt = handle
        ? { x: handle.x, y: handle.y }
        : { x: snapV(cursorFP.x), y: snapV(cursorFP.y) };
      setDrawState({ pts: [startPt], snapPt: null, startHandle: handle });
    } else {
      // Add next waypoint (H/V snapped from last confirmed point)
      const newPt = hvSnap(ds.pts[ds.pts.length - 1], cursorFP);
      setDrawState(prev => prev ? { ...prev, pts: [...prev.pts, newPt], snapPt: newPt } : prev);
    }
  }, [screenToFlowPosition, findNearestHandle]);

  const onOverlayDblClick = useCallback((e) => {
    const ds = drawStateRef.current;
    if (!ds || ds.pts.length < 2) { setDrawState(null); return; }
    const cursorFP = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const endHandle = findNearestHandle(cursorFP);
    const finalPts = [...ds.pts];
    if (endHandle) {
      finalPts[finalPts.length - 1] = { x: endHandle.x, y: endHandle.y };
    }
    createDrawnEdge(finalPts, ds.startHandle, endHandle, drawNoJumpRef.current);
    setDrawState(null);
  }, [screenToFlowPosition, findNearestHandle, createDrawnEdge]);

  // Convert flow coordinates to canvas-div-relative SVG coordinates for the preview.
  const toSvgCoord = useCallback((flowPt) => {
    const sc = flowToScreenPosition(flowPt);
    const rect = reactFlowWrapper.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: sc.x - rect.left, y: sc.y - rect.top };
  }, [flowToScreenPosition]);

  // Edge reconnection — drag an endpoint to move it to a different pin
  const edgeReconnectInProgress = useRef(false);
  const onReconnectStart = useCallback(() => { edgeReconnectInProgress.current = true; }, []);
  const onReconnect = useCallback((oldEdge, newConnection) => {
    pushUndo();
    edgeReconnectInProgress.current = false;
    // Sanitize AFTER reconnectEdge updates the edge so that if the user
    // dropped onto a target-type handle (sourceHandle = "in" etc.) the
    // orientation gets corrected before it hits xyflow's renderer.
    setEdges(eds => sanitizeEdges(reconnectEdge(oldEdge, newConnection, eds)));
  }, [pushUndo, setEdges]);
  const onReconnectEnd = useCallback((_, edge) => {
    // If the user dropped on empty space (no valid target), remove the edge
    if (edgeReconnectInProgress.current) {
      pushUndo();
      setEdges(eds => eds.filter(e => e.id !== edge.id));
    }
    edgeReconnectInProgress.current = false;
  }, [pushUndo, setEdges]);

  // Shift+click adds to selection (handled by ReactFlow via multiSelectionKeyCode).
  // We still update the properties panel to show the clicked node, but only clear
  // selectedNode when pane-clicking without shift so multi-select isn't lost.
  const onNodeClick = useCallback((evt, node) => { setSelectedNode(node); setSelectedEdge(null); }, []);
  const onEdgeClick = useCallback((evt, edge) => { setSelectedEdge(edge); setSelectedNode(null); }, []);
  const onPaneClick = useCallback(() => { setSelectedNode(null); setSelectedEdge(null); setCtxMenu(null); }, []);

  // ── Context menu / Z-order ────────────────────────────────────────────────
  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);

  // Re-orders nodes or edges so that the target gets the requested z position.
  // Each item's zIndex is normalised to a consecutive integer after the move so
  // the values stay compact regardless of how many operations are performed.
  const applyZOrder = useCallback((targetId, isEdge, op) => {
    pushUndo();
    const reorder = (items) => {
      // Sort current items by their explicit zIndex (default 0).
      const sorted = [...items].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
      // Assign clean 0-based indices so arithmetic is predictable.
      const norm = sorted.map((item, i) => ({ ...item, zIndex: i }));
      const idx = norm.findIndex(item => item.id === targetId);
      if (idx === -1) return items;
      const result = [...norm];
      if (op === 'front') {
        const [t] = result.splice(idx, 1);
        result.push(t);
      } else if (op === 'back') {
        const [t] = result.splice(idx, 1);
        result.unshift(t);
      } else if (op === 'forward' && idx < result.length - 1) {
        [result[idx], result[idx + 1]] = [result[idx + 1], result[idx]];
      } else if (op === 'backward' && idx > 0) {
        [result[idx], result[idx - 1]] = [result[idx - 1], result[idx]];
      }
      return result.map((item, i) => ({ ...item, zIndex: i }));
    };
    if (isEdge) setEdges(reorder);
    else setNodes(reorder);
    setCtxMenu(null);
  }, [pushUndo, setNodes, setEdges]);

  const onNodeContextMenu = useCallback((e, node) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, targetId: node.id, isEdge: false });
  }, []);

  const onEdgeContextMenu = useCallback((e, edge) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, targetId: edge.id, isEdge: true });
  }, []);

  const updateNodeInternals = useUpdateNodeInternals();
  const onNodeDataChange = useCallback((id, newData) => {
    pushUndo();
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: newData } : n));
    setSelectedNode(prev => prev?.id === id ? { ...prev, data: newData } : prev);
    // updateNodeInternals here handles non-rotation changes (e.g. resize).
    // Rotation changes are handled by useUpdateAfterRotation inside each node
    // component, which fires from a useEffect AFTER the CSS rotation is committed.
    updateNodeInternals(id);
  }, [pushUndo, setNodes, updateNodeInternals]);

  const onDeleteNode = useCallback((id) => {
    pushUndo();
    setNodes(nds => nds.filter(n => n.id !== id));
    setEdges(eds => eds.filter(e => e.source !== id && e.target !== id));
    setSelectedNode(null);
  }, [pushUndo, setNodes, setEdges]);

  const onEdgeDataChange = useCallback((id, newData) => {
    pushUndo();
    setEdges(eds => eds.map(e => e.id === id ? { ...e, data: newData } : e));
    setSelectedEdge(prev => prev?.id === id ? { ...prev, data: newData } : prev);
  }, [pushUndo, setEdges]);

  const onDeleteEdge = useCallback((id) => {
    pushUndo();
    setEdges(eds => eds.filter(e => e.id !== id));
    setSelectedEdge(null);
  }, [pushUndo, setEdges]);

  // When a node is dragged to a new position, discard any stored waypoints on
  // Record pre-drag positions of all dragged nodes (xyflow passes the full
  // dragged-node array as the third parameter — more reliable than reading
  // getNodes() + filtering on .selected which can lag a render cycle).
  const onNodeDragStart = useCallback((_, node, nodes) => {
    // Snapshot state BEFORE the drag mutates any positions.  pushUndo must be
    // here — by the time onNodeDragStop fires, React Flow has already applied
    // position changes via onNodesChange and nodesRef.current is at the NEW
    // positions, so snapshotting there would record the post-drag state.
    pushUndo();
    const positions = new Map();
    (nodes?.length ? nodes : [node]).forEach(n =>
      positions.set(n.id, { x: n.position.x, y: n.position.y }));
    // Safety net: also include every node currently marked selected in React
    // state (nodesRef).  After a custom marquee select the xyflow internal store
    // can lag one render cycle, so the callback's `nodes` array may be missing
    // nodes whose .selected flag was just set via setNodes.  Merging from
    // nodesRef ensures startPos is always complete regardless of timing.
    nodesRef.current.filter(n => n.selected && !positions.has(n.id)).forEach(n =>
      positions.set(n.id, { x: n.position.x, y: n.position.y }));
    dragStartPositions.current = positions;

    // Snapshot original waypoints for every edge whose both endpoints are in
    // the drag group.  onNodeDrag translates from these originals each frame
    // so there is no accumulated rounding error across many drag events.
    const wpsSnap = new Map();
    edgesRef.current.forEach(e => {
      if (positions.has(e.source) && positions.has(e.target) && e.data?.waypoints?.length) {
        wpsSnap.set(e.id, e.data.waypoints.map(p => ({ ...p })));
      }
    });
    dragStartWaypoints.current = wpsSnap;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // nodesRef, edgesRef and pushUndo are stable refs — no deps needed

  // Live-translate waypoints during drag so wires follow components visually.
  // Uses the start-of-drag snapshot so each frame computes delta from the
  // original positions rather than from the already-translated current state.
  const onNodeDrag = useCallback((_, node, nodes) => {
    const startPos = dragStartPositions.current;
    const startWps = dragStartWaypoints.current;
    if (!startWps.size) return;

    const allDragged = nodes?.length ? nodes : [node];
    const refNode = allDragged.find(n => n.type !== 'freeWire') ?? node;
    const start = startPos.get(refNode.id);
    if (!start) return;
    const dx = refNode.position.x - start.x;
    const dy = refNode.position.y - start.y;
    if (dx === 0 && dy === 0) return;

    setEdges(eds => eds.map(e => {
      const origWps = startWps.get(e.id);
      if (!origWps) return e;
      return { ...e, data: { ...e.data, waypoints: origWps.map(p => ({ x: p.x + dx, y: p.y + dy })) } };
    }));
  }, [setEdges]);

  // After a drag, keep connected edge paths correct.
  //   • Auto-routed edges (no manualDraw): clear waypoints so WireEdge re-routes
  //     from the updated pin coordinates — unchanged existing behaviour.
  //   • Hand-drawn edges (manualDraw): waypoints are absolute flow coords.  When
  //     BOTH endpoint nodes moved together by the same group-drag delta, translate
  //     every waypoint by that delta so the drawn path stays intact.  When only
  //     one end moved, the original path is invalid — reset to auto-route.
  const onNodeDragStop = useCallback((_, node, nodes) => {
    const startPos = dragStartPositions.current;

    // Use any non-freeWire node from the drag group to derive the delta.
    // IMPORTANT: do NOT return early for freeWire — when the user starts their
    // drag from a freeWire node it would be the `node` param, and skipping here
    // leaves all WireEdge waypoints untranslated (wires freeze at old position).
    setEdges(eds => eds.map(e => {
      const srcInGroup = startPos.has(e.source);
      const tgtInGroup = startPos.has(e.target);
      if (!srcInGroup && !tgtInGroup) return e; // neither end was dragged

      // No waypoints at all → WireEdge will auto-route from the updated pin
      // coords naturally.  Nothing to do here.
      const wps = e.data?.waypoints;
      if (!wps?.length) return e;

      if (srcInGroup && tgtInGroup) {
        // onNodeDrag already translated these waypoints live on every frame,
        // so e.data.waypoints is already at the correct final position.
        // Do NOT translate again — that would push them to origPos + 2*delta.
        return e;
      }
      // Only one endpoint moved — the stored path no longer lines up; fall
      // back to auto-route so WireEdge can re-route from the new pin coords.
      return { ...e, data: { ...e.data, waypoints: [], manualDraw: false } };
    }));

    // NOTE: freeWire nodes are standard React Flow nodes — when selected and
    // dragged as part of a group, React Flow already updates their position to
    // the correct final value before onNodeDragStop fires.  Applying dx/dy here
    // would move them a second time, producing a double-translation jump on drop.
    // No extra setNodes call is needed for freeWire position updates.

    dragStartPositions.current = new Map();
    dragStartWaypoints.current = new Map();
  }, [setEdges]);

  // Keyboard shortcuts: R = rotate selected node 90° CW; Delete/Backspace = remove selected
  // These are window-level so they fire even when the ReactFlow canvas div lacks focus
  // (e.g. after dragging a segment handle, which registers its listeners on window).
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

      // Draw mode: Escape cancels, Enter commits
      if (e.key === 'Escape' && drawStateRef.current) {
        setDrawState(null);
        return;
      }
      if (e.key === 'Enter') {
        const ds = drawStateRef.current;
        if (ds && ds.pts.length >= 2) {
          const lastPt = ds.pts[ds.pts.length - 1];
          const endHandle = findNearestHandle(lastPt);
          createDrawnEdge(ds.pts, ds.startHandle, endHandle, drawNoJumpRef.current);
          setDrawState(null);
          return;
        }
      }

      if (e.key === 'r' || e.key === 'R') {
        const node = selectedNodeRef.current;
        if (!node || node.type === 'note') return;
        const newRotation = ((node.data.rotation || 0) + 90) % 360;
        onNodeDataChange(node.id, { ...node.data, rotation: newRotation });
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        undo();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        e.preventDefault();
        copySelection();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'x') {
        e.preventDefault();
        cutSelection();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        e.preventDefault();
        pasteSelection();
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        deleteSelection();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onNodeDataChange, pushUndo, undo, copySelection, cutSelection, pasteSelection, deleteSelection, setNodes, setEdges, setActiveTool, createDrawnEdge, setDrawState, findNearestHandle]);

  // Drop a shape from the palette onto the canvas
  const onDragStart = (e, item) => {
    e.dataTransfer.setData('application/reactflow-type', item.type);
    e.dataTransfer.setData('application/reactflow-defaults', JSON.stringify(item.defaults));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/reactflow-type');
    if (!type) return;
    const defaults = JSON.parse(e.dataTransfer.getData('application/reactflow-defaults') || '{}');
    const raw = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const position = {
      x: Math.round(raw.x / SNAP_GRID) * SNAP_GRID,
      y: Math.round(raw.y / SNAP_GRID) * SNAP_GRID,
    };
    pushUndo();
    const autoLabel = nextAutoLabel(nodesRef.current, defaults.label);
    const data = (autoLabel && autoLabel !== defaults.label)
      ? { ...defaults, label: autoLabel }
      : { ...defaults };
    const newNode = { id: newId(), type, position, data };
    setNodes(nds => nds.concat(newNode));
  }, [pushUndo, setNodes, screenToFlowPosition]);

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  // ── Shared canvas builder — used by PNG and PDF exports ───────────────────
  // Fits all nodes into view, renders the diagram to a canvas with the info-
  // block footer, then calls `onCanvas(canvas)` before restoring the viewport.
  const buildExportCanvas = useCallback(async (onCanvas, pixelRatio = 2) => {
    const allNodes = getNodes();
    if (!allNodes.length) return;

    const containerEl = document.querySelector('.react-flow');
    if (!containerEl) return;

    const savedViewport = getViewport();
    const uiSelectors = [
      '.react-flow__controls',
      '.react-flow__minimap',
      '.react-flow__attribution',
      '.react-flow__panel',
    ];
    const hiddenEls = uiSelectors.flatMap(s => [...containerEl.querySelectorAll(s)]);
    hiddenEls.forEach(el => { el.style.visibility = 'hidden'; });

    try {
      fitView({ padding: 0.08, duration: 0, maxZoom: 1.5 });
      await new Promise(r => setTimeout(r, 100));

      const rect = containerEl.getBoundingClientRect();
      const diagramDataUrl = await toPng(containerEl, {
        backgroundColor: '#ffffff',
        pixelRatio,
        width: rect.width,
        height: rect.height,
      });

      const img = await loadExportImage(diagramDataUrl);

      const exportW = img.naturalWidth;
      const exportH = img.naturalHeight;

      const hasNotes = (diagramInfo.notes || '').trim().length > 0;
      const ibRowH = 56;
      const ibRows = hasNotes ? 4 : 3;
      const ibW = 680;
      const ibH = ibRowH * ibRows;
      const ibMargin = 40;
      const logoH = 110;
      const logoMargin = 32;
      const logoPad = 8;
      const footerH = Math.max(
        ibH + ibMargin * 2,
        logoH + logoMargin * 2 + logoPad * 2
      );

      const canvas = document.createElement('canvas');
      canvas.width = exportW;
      canvas.height = exportH + footerH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const footerTop = exportH;

      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, footerTop);
      ctx.lineTo(exportW, footerTop);
      ctx.stroke();

      const logo = new Image();
      logo.src = '/AmpNebulaSmall2.jpg';
      await new Promise(r => { logo.onload = r; logo.onerror = r; });
      if (logo.naturalWidth) {
        const logoW = Math.round(logo.naturalWidth * (logoH / logo.naturalHeight));
        const tmSize = Math.round(logoH * 0.26);
        ctx.font = `bold ${tmSize}px sans-serif`;
        const tmW = Math.ceil(ctx.measureText('™').width);
        const pillW = logoW + tmW + 6 + logoPad * 2;
        const logoY = footerTop + footerH - logoH - logoMargin;
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#000000';
        ctx.beginPath();
        ctx.roundRect(logoMargin - logoPad, logoY - logoPad, pillW, logoH + logoPad * 2, 8);
        ctx.fill();
        ctx.strokeStyle = 'rgba(180,180,180,0.6)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.globalAlpha = 0.90;
        ctx.drawImage(logo, logoMargin, logoY, logoW, logoH);
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'top';
        ctx.fillText('™', logoMargin + logoW + 4, logoY + 6);
      }

      const ibX = exportW - ibW - ibMargin;
      const ibY = footerTop + footerH - ibH - ibMargin;

      const ibLabel = (text, x, y) => {
        ctx.font = '17px Arial, sans-serif';
        ctx.fillStyle = '#666666';
        ctx.textBaseline = 'top';
        ctx.fillText(text, x + 8, y + 5);
      };
      const ibValue = (text, x, y, maxW) => {
        ctx.font = 'bold 23px Arial, sans-serif';
        ctx.fillStyle = '#000000';
        ctx.textBaseline = 'top';
        let t = String(text || '');
        while (t.length > 0 && ctx.measureText(t).width > maxW - 20) t = t.slice(0, -1).trimEnd();
        if (t !== String(text || '')) t += '…';
        ctx.fillText(t, x + 8, y + 5 + 17 + 3);
      };

      ctx.globalAlpha = 1;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(ibX, ibY, ibW, ibH);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(ibX, ibY, ibW, ibH);

      ibLabel('Title', ibX, ibY);
      ibValue(diagramTitle, ibX, ibY, ibW);
      ctx.beginPath(); ctx.moveTo(ibX, ibY + ibRowH); ctx.lineTo(ibX + ibW, ibY + ibRowH); ctx.stroke();

      ibLabel('Author', ibX, ibY + ibRowH);
      ibValue(diagramInfo.author || '', ibX, ibY + ibRowH, ibW);
      ctx.beginPath(); ctx.moveTo(ibX, ibY + ibRowH * 2); ctx.lineTo(ibX + ibW, ibY + ibRowH * 2); ctx.stroke();

      const c1 = Math.round(ibW * 0.28);
      const c2 = Math.round(ibW * 0.42);
      const c3 = ibW - c1 - c2;
      ibLabel('Revision', ibX, ibY + ibRowH * 2);
      ibValue(diagramInfo.revision || '1.0', ibX, ibY + ibRowH * 2, c1);
      ctx.beginPath(); ctx.moveTo(ibX + c1, ibY + ibRowH * 2); ctx.lineTo(ibX + c1, ibY + ibRowH * 3); ctx.stroke();
      ibLabel('Date', ibX + c1, ibY + ibRowH * 2);
      ibValue(diagramInfo.revision_date || '', ibX + c1, ibY + ibRowH * 2, c2);
      ctx.beginPath(); ctx.moveTo(ibX + c1 + c2, ibY + ibRowH * 2); ctx.lineTo(ibX + c1 + c2, ibY + ibRowH * 3); ctx.stroke();
      ibLabel('Sheets', ibX + c1 + c2, ibY + ibRowH * 2);
      ibValue('1 of 1', ibX + c1 + c2, ibY + ibRowH * 2, c3);

      if (hasNotes) {
        ctx.beginPath(); ctx.moveTo(ibX, ibY + ibRowH * 3); ctx.lineTo(ibX + ibW, ibY + ibRowH * 3); ctx.stroke();
        ibLabel('Notes', ibX, ibY + ibRowH * 3);
        ibValue(diagramInfo.notes, ibX, ibY + ibRowH * 3, ibW);
      }

      onCanvas(canvas);
    } catch (err) {
      console.error('Export error:', err);
      throw err;
    } finally {
      hiddenEls.forEach(el => { el.style.visibility = ''; });
      setViewport(savedViewport, { duration: 150 });
    }
  }, [getNodes, fitView, getViewport, setViewport, diagramTitle, diagramInfo]);

  // ── Export PNG ────────────────────────────────────────────────────────────
  const exportToPng = useCallback(async () => {
    await buildExportCanvas((canvas) => {
      const link = document.createElement('a');
      link.download = `${diagramTitle || 'wiring-diagram'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  }, [buildExportCanvas, diagramTitle]);

  // ── Shared SVG generation ─────────────────────────────────────────────────
  // Renders the diagram + vector info block + watermark footer, then calls
  // onSvg(svgString). Used by both the SVG download and PDF print paths so
  // they share identical vector output.
  const buildExportSvg = useCallback(async (onSvg) => {
    const allNodes = getNodes();
    if (!allNodes.length) return;

    const containerEl = document.querySelector('.react-flow');
    if (!containerEl) return;

    const savedViewport = getViewport();
    const uiSelectors = [
      '.react-flow__controls',
      '.react-flow__minimap',
      '.react-flow__attribution',
      '.react-flow__panel',
    ];
    const hiddenEls = uiSelectors.flatMap(s => [...containerEl.querySelectorAll(s)]);
    hiddenEls.forEach(el => { el.style.visibility = 'hidden'; });

    try {
      fitView({ padding: 0.08, duration: 0, maxZoom: 1.5 });
      await new Promise(r => setTimeout(r, 100));

      const rect = containerEl.getBoundingClientRect();
      const svgDataUrl = await toSvg(containerEl, {
        backgroundColor: '#ffffff',
        width: rect.width,
        height: rect.height,
      });

      // Decode the SVG data URL so we can inject vector elements into it
      const svgString = decodeURIComponent(svgDataUrl.split(',')[1]);
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
      const svgEl = svgDoc.documentElement;
      const W = parseFloat(svgEl.getAttribute('width') || rect.width);
      const H = parseFloat(svgEl.getAttribute('height') || rect.height);
      const ns = 'http://www.w3.org/2000/svg';

      const hasNotes = (diagramInfo.notes || '').trim().length > 0;
      const rowH = 34;
      const rows = hasNotes ? 4 : 3;
      const ibW = Math.max(280, Math.min(360, Math.round(W * 0.24)));
      const ibH = rowH * rows;
      const ibM = 24;
      const logoBlockH = 34 + 10;
      const footerH = Math.max(ibH + ibM * 2, logoBlockH + 24);
      const totalH = H + footerH;

      svgEl.setAttribute('height', String(totalH));
      const currentViewBox = svgEl.getAttribute('viewBox');
      if (currentViewBox) {
        const parts = currentViewBox.split(/\s+/).map(Number);
        if (parts.length === 4 && parts.every(Number.isFinite)) {
          svgEl.setAttribute('viewBox', `${parts[0]} ${parts[1]} ${parts[2]} ${parts[3] + footerH}`);
        }
      } else {
        svgEl.setAttribute('viewBox', `0 0 ${W} ${totalH}`);
      }

      const mkEl = (tag, attrs) => {
        const el = svgDoc.createElementNS(ns, tag);
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, String(v)));
        return el;
      };
      const mkText = (content, x, y, size, weight, fill, maxW = ibW - 12) => {
        const t = mkEl('text', { x, y, 'font-family': 'Arial, sans-serif', 'font-size': size, 'font-weight': weight, fill });
        const maxChars = Math.max(4, Math.floor(maxW / (size * 0.58)));
        let s = String(content || '');
        if (s.length > maxChars) s = s.slice(0, maxChars - 1) + '\u2026';
        t.textContent = s;
        return t;
      };
      const mkLine = (x1, y1, x2, y2) => mkEl('line', { x1, y1, x2, y2, stroke: '#000000', 'stroke-width': '1.2' });

      const footerTop = H;
      svgEl.appendChild(mkLine(0, footerTop, W, footerTop));

      // ── Watermark — bottom-left footer band (pure SVG text, fully vector) ─
      svgEl.appendChild(mkEl('rect', { x: 14, y: totalH - 34, width: 132, height: 24, rx: 4, fill: '#000000' }));
      const wm = mkEl('text', { x: 21, y: totalH - 18, 'font-family': 'Arial, sans-serif', 'font-size': 14, 'font-weight': 'bold', fill: '#ffffff' });
      wm.textContent = 'AmpNebula\u2122';
      svgEl.appendChild(wm);

      // ── Info block — footer bottom-right (vector rects + text) ───────────
      const ibX = W - ibW - ibM;
      const ibY = footerTop + footerH - ibH - ibM;

      svgEl.appendChild(mkEl('rect', { x: ibX, y: ibY, width: ibW, height: ibH, fill: '#ffffff', stroke: '#000000', 'stroke-width': '1.2' }));

      // Row 1: Title
      svgEl.appendChild(mkText('Title', ibX + 6, ibY + 11, 9, 'normal', '#666666'));
      svgEl.appendChild(mkText(diagramTitle, ibX + 6, ibY + 24, 13, 'bold', '#000000'));
      svgEl.appendChild(mkLine(ibX, ibY + rowH, ibX + ibW, ibY + rowH));

      // Row 2: Author
      svgEl.appendChild(mkText('Author', ibX + 6, ibY + rowH + 11, 9, 'normal', '#666666'));
      svgEl.appendChild(mkText(diagramInfo.author || '', ibX + 6, ibY + rowH + 24, 13, 'bold', '#000000'));
      svgEl.appendChild(mkLine(ibX, ibY + rowH * 2, ibX + ibW, ibY + rowH * 2));

      // Row 3: Revision | Date (two columns)
      const c1 = Math.round(ibW * 0.38);
      svgEl.appendChild(mkText('Revision', ibX + 6, ibY + rowH * 2 + 11, 9, 'normal', '#666666', c1 - 12));
      svgEl.appendChild(mkText(diagramInfo.revision || '1.0', ibX + 6, ibY + rowH * 2 + 24, 13, 'bold', '#000000', c1 - 12));
      svgEl.appendChild(mkLine(ibX + c1, ibY + rowH * 2, ibX + c1, ibY + rowH * 3));
      svgEl.appendChild(mkText('Date', ibX + c1 + 6, ibY + rowH * 2 + 11, 9, 'normal', '#666666', ibW - c1 - 12));
      svgEl.appendChild(mkText(diagramInfo.revision_date || '', ibX + c1 + 6, ibY + rowH * 2 + 24, 13, 'bold', '#000000', ibW - c1 - 12));

      // Row 4: Notes
      if (hasNotes) {
        svgEl.appendChild(mkLine(ibX, ibY + rowH * 3, ibX + ibW, ibY + rowH * 3));
        svgEl.appendChild(mkText('Notes', ibX + 6, ibY + rowH * 3 + 11, 9, 'normal', '#666666'));
        svgEl.appendChild(mkText(diagramInfo.notes, ibX + 6, ibY + rowH * 3 + 24, 13, 'bold', '#000000'));
      }

      const finalSvg = new XMLSerializer().serializeToString(svgDoc);
      onSvg(finalSvg);
    } catch (err) {
      console.error('SVG export error:', err);
      throw err;
    } finally {
      hiddenEls.forEach(el => { el.style.visibility = ''; });
      setViewport(savedViewport, { duration: 150 });
    }
  }, [getNodes, fitView, getViewport, setViewport, diagramTitle, diagramInfo]);

  const exportToSvg = useCallback(async () => {
    await buildExportSvg((svgString) => {
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${diagramTitle || 'wiring-diagram'}.svg`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    });
  }, [buildExportSvg, diagramTitle]);

  // ── Export PDF (high-DPI raster, no OS dialog, cross-platform) ─────────────
  // buildExportCanvas at 3× pixelRatio → ~288 DPI on a standard 96 DPI screen.
  // jsPDF embeds the image directly and triggers a file download without any
  // print or save dialog, so it works identically on Mac, Windows, and Linux.
  const exportToPdf = useCallback(async () => {
    const PDF_PIXEL_RATIO = 3;
    await buildExportCanvas((canvas) => {
      // Convert canvas pixel dimensions → PDF points (1 CSS px = 0.75 pt at 96 DPI)
      const cssW = canvas.width / PDF_PIXEL_RATIO;
      const cssH = canvas.height / PDF_PIXEL_RATIO;
      const ptW = cssW * 0.75;
      const ptH = cssH * 0.75;
      const orientation = ptW > ptH ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ orientation, unit: 'pt', format: [ptW, ptH] });
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, ptW, ptH);
      pdf.save(`${diagramTitle || 'wiring-diagram'}.pdf`);
    }, PDF_PIXEL_RATIO);
  }, [buildExportCanvas, diagramTitle]);

  const readJsonResponse = async (response) => {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (_) {
      return { error: text };
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const save = async (titleOverride, skipFirstLayoutSavePrompt = false) => {
    // Cancel any pending auto-save timer — this save supersedes it.
    // Also suppress auto-save during and briefly after the save so that
    // re-renders triggered by state updates inside save() don't immediately
    // schedule a fresh redundant auto-save timer.
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = null;
    if (!canSave) {
      setSaveStatus('error');
      setSaveMessage(saveDisabledReason);
      return;
    }
    const saveTitle = typeof titleOverride === 'string' ? titleOverride.trim() : diagramTitle.trim();
    if (!skipFirstLayoutSavePrompt
      && diagramType === 'layout'
      && !diagramId
      && (!saveTitle || /^untitled diagram$/i.test(saveTitle))) {
      setFirstLayoutSaveName('');
      return;
    }
    isLoadingDiagramRef.current = true;

    setSaveStatus('saving');
    setSaveMessage('');
    const body = {
      title: saveTitle,
      diagram_type: diagramType,
      is_public: isPublic,
      diagram_data: diagramType === 'layout'
        ? layoutDocument
        : { nodes, edges, info: diagramInfo, labelFontSize },
    };
    try {
      let res;
      if (diagramId) {
        res = await fetch(`${API_URL}/wiring-diagrams/${diagramId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_URL}/wiring-diagrams`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const payload = await readJsonResponse(res);
        const error = new Error(payload?.message || payload?.error || 'Failed to save diagram');
        error.payload = payload;
        error.status = res.status;
        throw error;
      }
      const saved = await res.json();
      if (!diagramId) setDiagramId(saved.diagram_id);
      await loadListSilent();
      setSaveStatus('saved');
      setSaveMessage(''); // success — status shown by inline indicator, not banner
      setTimeout(() => setSaveStatus(''), 2500);
    } catch (err) {
      const isAuthenticationFailure = err?.status === 401
        || (err?.status === 403 && /invalid token|session expired/i.test(err?.message || ''));
      if (!isAuthenticationFailure) console.error('Save error:', err);
      setSaveStatus('error');
      setSaveMessage(isAuthenticationFailure
        ? 'Your session has expired. Please sign in again.'
        : err?.payload?.message || err?.message || 'Failed to save diagram');
      // Log to server error log so it appears in the admin Error Log screen
      if (!isAuthenticationFailure) {
        try {
          fetch(`${API_URL}/errors/log`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              severity: 'error',
              source: 'frontend',
              message: `WiringDiagramStudio save failed: ${err?.message || String(err)}`,
              stack_trace: err?.stack,
              error_code: err?.name || 'SaveError',
              page_url: window.location.href,
              metadata: {
                diagramId,
                diagramTitle,
                isAuthenticated,
                method: diagramId ? 'PUT' : 'POST',
                timestamp: new Date().toISOString(),
              },
            }),
          });
        } catch (_) { /* never suppress the original error */ }
      }
    }
    // Re-enable auto-save after a brief delay so that post-save re-renders
    // don't immediately re-arm the debounce timer.
    setTimeout(() => { isLoadingDiagramRef.current = false; }, 50);
  };

  // ── Template functions ────────────────────────────────────────────────────
  // Keep latestSaveRef pointing at the most-recent save closure so the
  // debounce timer always serializes the latest canvas state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { latestSaveRef.current = save; });

  // Debounced auto-save: 2.5 s after any canvas/metadata change.
  // Skips the initial mount render and any load operation (openDiagram,
  // newDiagram, applyTemplate all set isLoadingDiagramRef while mutating state).
  // Also skips if the canvas is empty and the diagram has never been saved —
  // avoids creating blank "Untitled Diagram" records on every page load.
  useEffect(() => {
    if (isFirstAutoSaveRender.current) { isFirstAutoSaveRender.current = false; return; }
    if (!autoSaveRef.current || !canSave || isLoadingDiagramRef.current) return;
    const isEmpty = diagramType === 'layout'
      ? layoutDocument.objects.length === 0
      : nodes.length === 0 && edges.length === 0;
    if (!diagramId && isEmpty) return;
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (autoSaveRef.current) latestSaveRef.current?.();
    }, 2500);
    return () => clearTimeout(autoSaveTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, layoutDocument, diagramTitle, diagramInfo, isPublic, diagramType, labelFontSize, autoSave, canSave, diagramId]);

  const loadTemplatesSilent = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/wiring-diagram-templates`, { headers });
      if (!res.ok) return [];
      const rows = await res.json();
      setTemplates(rows);
      return rows;
    } catch {
      return [];
    } finally {
      setLoadingTemplates(false);
    }
  }, [token]);

  // Prompt to save a diagram as a template.
  // When called with a diagramId, the full diagram_data is fetched first.
  // When called without one (toolbar), the current canvas state is used.
  const saveAsTemplate = async (titleSuggestion, diagramId_or_data, diagramTypeHint) => {
    if (!isAuthenticated) { setSaveMessage('Sign in to save templates.'); return; }

    let data, type;
    if (typeof diagramId_or_data === 'number') {
      // Fetch full diagram for the list-row bookmark button
      try {
        const res = await fetch(`${API_URL}/wiring-diagrams/${diagramId_or_data}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(await res.text());
        const src = await res.json();
        data = src.diagram_data;
        type = src.diagram_type;
      } catch (err) {
        console.error('Fetch diagram for template error:', err);
        return;
      }
    } else {
      data = diagramId_or_data || (diagramType === 'layout' ? layoutDocument : { nodes, edges });
      type = diagramTypeHint || diagramType || 'schematic';
    }

    setSavingTemplate({
      title: titleSuggestion || diagramTitle || 'Untitled Template',
      description: '',
      is_public: false,
      diagramData: data,
      diagramType: type,
    });
  };

  const confirmSaveAsTemplate = async () => {
    if (!savingTemplate) return;
    try {
      const res = await fetch(`${API_URL}/wiring-diagram-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: savingTemplate.title,
          description: savingTemplate.description || undefined,
          diagram_type: savingTemplate.diagramType,
          diagram_data: savingTemplate.diagramData,
          is_public: savingTemplate.is_public,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavingTemplate(null);
      await loadTemplatesSilent();
      setSaveMessage('Saved as template!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Save template error:', err);
    }
  };

  const deleteTemplate = async (id, title) => {
    if (!window.confirm(`Delete template "${title}"?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/wiring-diagram-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      await loadTemplatesSilent();
    } catch (err) {
      console.error('Delete template error:', err);
    }
  };

  const toggleTemplatePublic = async (id, currentPublic) => {
    try {
      const res = await fetch(`${API_URL}/wiring-diagram-templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_public: !currentPublic }),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadTemplatesSilent();
    } catch (err) {
      console.error('Toggle template public error:', err);
    }
  };

  const applyTemplate = (template) => {
    if (rejectUnauthorizedLayout(template.diagram_type)) return;
    isLoadingDiagramRef.current = true;
    const dd = template.diagram_data || { nodes: [], edges: [] };
    if (template.diagram_type === 'layout') {
      setLayoutDocument(normalizeLayoutDocument(dd));
    } else {
      setNodes(dd.nodes || []);
      setEdges(sanitizeEdges(dd.edges || []));
    }
    setDiagramTitle(`${template.title} (copy)`);
    setDiagramType(template.diagram_type);
    setIsPublic(false);
    setLabelFontSize(dd.labelFontSize ?? 7);
    setDiagramId(null);  // new unsaved diagram
    setSelectedNode(null);
    setShowDiagramList(false);
    setShowNewPicker(false);
    setDiagramInfo(dd.info || {
      author: user?.display_name || user?.username || '',
      doc_number: '', revision: '1.0',
      revision_date: new Date().toLocaleDateString('en-US'), notes: '',
    });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      isLoadingDiagramRef.current = false;
    }));
  };

  // ── Load my diagrams list ─────────────────────────────────────────────────
  // Silent refresh — updates the list without opening/closing the modal.
  const loadList = async (tab = 'diagrams') => {
    if (!isAuthenticated) {
      setSaveMessage('Sign in to access your saved diagrams.');
      return;
    }
    setDiagramListTab(tab);
    await loadListSilent();
    setShowDiagramList(true);
    // Templates load lazily when the Templates tab is opened (see tab click handler)
  };

  // ── Rename a diagram ──────────────────────────────────────────────────────
  const renameDiagram = async (id, newTitle) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    try {
      const res = await fetch(`${API_URL}/wiring-diagrams/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: trimmed }),
      });
      if (!res.ok) throw new Error(await res.text());
      if (diagramId === id) setDiagramTitle(trimmed); // keep editor title in sync
      setRenamingId(null);
      await loadListSilent();
    } catch (err) {
      console.error('Rename error:', err);
    }
  };

  // ── Delete a diagram ──────────────────────────────────────────────────────
  const deleteDiagram = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/wiring-diagrams/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      if (diagramId === id) {
        // The open diagram was deleted — start fresh
        setNodes([]);
        setEdges([]);
        setLayoutDocument(createLayoutDocument());
        setDiagramTitle('Untitled Diagram');
        setDiagramId(null);
        setSelectedNode(null);
        setShowDiagramList(false);
      } else {
        await loadListSilent();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // ── Duplicate a diagram ─────────────────────────────────────────────────
  const duplicateDiagram = async (id, title) => {
    if (!isPro && myDiagrams.length >= FREE_DIAGRAM_LIMIT) {
      alert('Free accounts can save 1 diagram. Upgrade to Pro for unlimited diagrams.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/wiring-diagrams/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const source = await res.json();
      const copyRes = await fetch(`${API_URL}/wiring-diagrams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: `Copy of ${title}`,
          diagram_type: source.diagram_type,
          is_public: false,
          diagram_data: source.diagram_data,
        }),
      });
      if (!copyRes.ok) throw new Error(await copyRes.text());
      await loadListSilent();
    } catch (err) {
      console.error('Duplicate error:', err);
    }
  };

  // ── Open a saved diagram ──────────────────────────────────────────────────
  const openDiagram = async (id) => {
    isLoadingDiagramRef.current = true;
    try {
      const res = await fetch(`${API_URL}/wiring-diagrams/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to open diagram');
      }
      const diagram = await res.json();
      if (rejectUnauthorizedLayout(diagram.diagram_type)) return;
      const dd = diagram.diagram_data || { nodes: [], edges: [] };
      if (diagram.diagram_type === 'layout') {
        setLayoutDocument(normalizeLayoutDocument(dd));
      } else {
        setNodes(dd.nodes || []);
        setEdges(sanitizeEdges(dd.edges || []));
      }
      setDiagramTitle(diagram.title);
      setDiagramType(diagram.diagram_type);
      setIsPublic(diagram.is_public);
      setLabelFontSize(dd.labelFontSize ?? 7);
      setDiagramId(diagram.diagram_id);
      setSelectedNode(null);
      setShowDiagramList(false);
      setDiagramInfo(dd.info || {
        author: user?.display_name || user?.username || '',
        doc_number: '', revision: '1.0', revision_date: '', notes: '',
      });
    } catch (err) {
      console.error('Open diagram error:', err);
      setSaveStatus('error');
      setSaveMessage(err.message || 'Failed to open diagram');
    } finally {
      // Two rAFs ensure the suppression flag clears AFTER the React effects
      // triggered by the state updates above have already fired.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        isLoadingDiagramRef.current = false;
      }));
    }
  };

  // ── New diagram ───────────────────────────────────────────────────────────
  const newDiagram = () => {
    isLoadingDiagramRef.current = true;
    setNodes([]);
    setEdges([]);
    setLayoutDocument(createLayoutDocument());
    setDiagramTitle('Untitled Diagram');
    setDiagramId(null);
    setSelectedNode(null);
    setShowDiagramList(false);
    setLabelFontSize(7);
    setDiagramInfo({
      author: user?.display_name || user?.username || '',
      doc_number: '', revision: '1.0',
      revision_date: new Date().toLocaleDateString('en-US'),
      notes: '',
    });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      isLoadingDiagramRef.current = false;
    }));
  };

  const renumberLabels = () => {
    setNodes(current => {
      const labelRe = /^([A-Za-z]+)(\d+|\?)$/;
      const numbered = current
        .filter(n => n.data?.label && labelRe.test(n.data.label))
        .slice()
        .sort((a, b) => {
          const ay = a.position.y, ax = a.position.x;
          const by = b.position.y, bx = b.position.x;
          const band = 30;
          if (Math.abs(ay - by) > band) return ay - by;
          return ax - bx;
        });
      const counters = {};
      const remap = new Map();
      for (const n of numbered) {
        const m = n.data.label.match(labelRe);
        const prefix = m[1];
        counters[prefix] = (counters[prefix] || 0) + 1;
        remap.set(n.id, `${prefix}${counters[prefix]}`);
      }
      if (remap.size === 0) return current;
      return current.map(n =>
        remap.has(n.id) ? { ...n, data: { ...n.data, label: remap.get(n.id) } } : n
      );
    });
  };

  return (
    <DiagramSettingsCtx.Provider value={{ labelFontSize }}>
    <div className={`wiring-studio${activeTool !== 'pointer' ? ' draw-mode' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', background: '#f7fafc' }}>
      <style>{`
        /* ── Handle sizing ───────────────────────────────────────────────────
         * xyflow positions the wire endpoint at the CENTRE of the Handle div.
         * The default Handle is 8×8px with a 3px border, so its centre sits
         * 4px outside the node's edge — creating a visible gap between the SVG
         * lead tip (which ends exactly at the node edge) and the wire endpoint.
         *
         * Fix: collapse the Handle to 0×0 so the centre is exactly on the node
         * edge, flush with the SVG lead tip.  A pure-CSS ::after dot provides
         * the visible indicator (doesn't move the centre point).
         */
        .wiring-studio .react-flow__handle {
          width:  0 !important;
          height: 0 !important;
          min-width:  0 !important;
          min-height: 0 !important;
          border: none !important;
          border-radius: 0 !important;
          background: transparent !important;
          /* Interaction area kept large via padding so dragging to start a
             connection still works comfortably even at 0×0 logical size.
             No margin offset — padding alone expands symmetrically around
             the 0×0 origin (which is what xyflow reads as the centre). */
          padding: 5px !important;
          box-sizing: content-box !important;
          opacity: 0;
          transition: opacity 0.12s;
          pointer-events: all;
        }
        /* Visible dot — shown only when handle is revealed */
        .wiring-studio .react-flow__handle::after {
          content: '';
          display: block;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #2563eb;
          /* Centre the dot on the handle's 0×0 origin (top-left corner of the
             collpased div) — offset by half the dot size and the padding. */
          position: absolute;
          top:  50%;
          left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
        }
        .wiring-studio .react-flow__handle.react-flow__handle-source::after { background: #2563eb; }
        .wiring-studio .react-flow__handle.react-flow__handle-target::after { background: #38a169; }

        /* Selected component shows its pins */
        .wiring-studio .react-flow__node.selected .react-flow__handle { opacity: 1; }
        /* Hovered component shows its pins */
        .wiring-studio .react-flow__node:hover .react-flow__handle   { opacity: 1; }
        /* Active connection drag */
        .wiring-studio .react-flow__handle.valid,
        .wiring-studio .react-flow__handle.invalid,
        .wiring-studio .react-flow__handle.react-flow__handle-connecting { opacity: 1; }
        /* Draw mode: show all handles */
        .wiring-studio.draw-mode .react-flow__handle { opacity: 1; }

        /* ── Tool-button tooltips ──────────────────────────────────────────
         * Each icon button carries a data-tooltip attribute.  On hover a
         * CSS-only tooltip appears below the button — no JS, no library.
         * The tooltip has a bold first line (tool name) pulled from the
         * data-tooltip-title attribute, followed by a regular description.
         */
        .wiring-studio .tool-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1.5px solid transparent;
          cursor: pointer;
          transition: background 0.12s, border-color 0.12s, transform 0.08s;
          flex-shrink: 0;
        }
        .wiring-studio .tool-btn:hover { transform: translateY(-1px); }
        .wiring-studio .tool-btn svg { display: block; }

        /* Tooltip bubble */
        .wiring-studio .tool-btn::after {
          content: attr(data-tooltip);
          position: absolute;
          top: calc(100% + 10px);
          left: 50%;
          transform: translateX(-50%);
          background: #1a202c;
          color: #e2e8f0;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.45;
          white-space: pre;   /* preserves \n line breaks */
          padding: 7px 11px;
          border-radius: 7px;
          border: 1px solid #4a5568;
          box-shadow: 0 4px 14px rgba(0,0,0,0.35);
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.15s 0.25s;  /* short delay prevents flicker */
          z-index: 9999;
          min-width: 210px;
          text-align: left;
        }
        /* Caret */
        .wiring-studio .tool-btn::before {
          content: '';
          position: absolute;
          top: calc(100% + 4px);
          left: 50%;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-bottom-color: #4a5568;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.15s 0.25s;
          z-index: 10000;
        }
        .wiring-studio .tool-btn:hover::after,
        .wiring-studio .tool-btn:hover::before { opacity: 1; }
      `}</style>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#1a202c', borderBottom: '1px solid #2d3748', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ color: 'white', fontWeight: 700, fontSize: 16, marginRight: 4 }}>⚡ Wiring Studio</span>

        <input
          value={diagramTitle}
          onChange={e => setDiagramTitle(e.target.value)}
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #4a5568', background: '#2d3748', color: 'white', fontSize: 14, minWidth: 180 }}
        />

        <select
          value={diagramType}
          onChange={e => {
            if (!rejectUnauthorizedLayout(e.target.value)) setDiagramType(e.target.value);
          }}
          style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #4a5568', background: '#2d3748', color: 'white', fontSize: 13 }}
        >
          <option value="schematic">Schematic</option>
          {isAdmin && <option value="layout">Layout (Admin Preview)</option>}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
          <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
          Public
        </label>

        {diagramType === 'schematic' && <label style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
          <span>Label size</span>
          <input
            type="range" min="4" max="12" step="0.5" value={labelFontSize}
            onChange={e => setLabelFontSize(parseFloat(e.target.value))}
            style={{ width: 72, accentColor: '#7c3aed' }}
          />
          <span style={{ fontFamily: 'monospace', minWidth: 22, color: 'rgba(255,255,255,0.5)' }}>{labelFontSize}</span>
        </label>}

        {/* ── Save group: button + Auto toggle visually joined as one unit ── */}
        <div style={{ display: 'flex', alignItems: 'stretch', borderRadius: 6, overflow: 'hidden', border: '1px solid #276749' }}>
          <button
            onClick={() => save()}
            title={saveDisabledReason || 'Save diagram'}
            style={{
              padding: '6px 14px', background: '#276749', color: '#c6f6d5',
              fontSize: 13, fontWeight: 600, cursor: canSave ? 'pointer' : 'not-allowed',
              border: 'none', borderRight: '1px solid rgba(255,255,255,0.15)',
              opacity: !canSave ? 0.5 : saveStatus === 'saving' ? 0.65 : 1,
              minWidth: 84,
            }}
          >
            💾 Save
          </button>
          <label
            title={autoSave ? 'Auto-save ON — click to turn off' : 'Auto-save OFF — click to turn on'}
            style={{
              display: 'flex', alignItems: 'center', gap: 4, padding: '0 10px',
              cursor: 'pointer', background: '#1e4d30',
              color: autoSave ? '#68d391' : 'rgba(255,255,255,0.45)',
              fontSize: 11, userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={autoSave}
              onChange={e => setAutoSave(e.target.checked)}
              style={{ accentColor: '#68d391', width: 13, height: 13, cursor: 'pointer' }}
            />
            Auto
          </label>
        </div>
        {/* Inline save-status indicator — fixed width so it never shifts layout.
            Fades in when saving/saved, fades out smoothly when status clears. */}
        <span
          aria-live="polite"
          style={{
            fontSize: 11,
            minWidth: 100,
            whiteSpace: 'nowrap',
            opacity: saveStatus ? 1 : 0,
            transition: 'opacity 0.55s ease',
            color: lastSaveStatus === 'saving' ? 'rgba(255,255,255,0.55)'
                 : lastSaveStatus === 'error'  ? '#fc8181'
                 : '#68d391',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {lastSaveStatus === 'saving' ? '● Saving…'
           : lastSaveStatus === 'error'  ? '✗ Save failed'
           : '✓ All changes saved'}
        </span>
        {diagramType === 'schematic' && <button onClick={() => setShowInfoModal(true)} title="Edit diagram info block (printed on exported files)" style={tbBtn('#2c5282', '#bee3f8', '#1a365d')}>ℹ️ Info</button>}
        {/* ── Export dropdown ─────────────────────────────────────────── */}
        <div ref={exportMenuRef} style={{ position: 'relative', display: 'inline-block' }}>
          <button
            onClick={() => {
              const hasContent = diagramType === 'layout' ? layoutDocument.objects.length > 0 : nodes.length > 0;
              if (!isPro || !hasContent || exportStatus === 'exporting') return;
              setExportStatus('idle');
              setShowExportMenu(v => !v);
            }}
            disabled={exportStatus === 'exporting'}
            title={
              !isPro ? 'Export is a Pro feature — upgrade to unlock'
              : exportStatus === 'exporting' ? 'Preparing export'
              : diagramType === 'layout' && layoutDocument.objects.length === 0 ? 'Add physical parts before exporting'
              : diagramType === 'schematic' && !nodes.length ? 'Add components to the diagram before exporting'
              : 'Export diagram'
            }
            style={{
              ...tbBtn('#6b46c1', '#e9d8fd', '#44337a'),
              ...(!isPro || (diagramType === 'layout' ? layoutDocument.objects.length === 0 : !nodes.length) ? { opacity: 0.45, cursor: 'not-allowed' } : {}),
            }}
          >
            {exportStatus === 'exporting' ? 'Preparing…' : `⬇ Export${!isPro ? ' 🔒' : ''}`}
          </button>
          <span aria-live="polite" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 3, color: '#fc8181', fontSize: 11, whiteSpace: 'nowrap' }}>
            {exportStatus === 'error' ? 'Export failed' : ''}
          </span>
          {showExportMenu && isPro && (diagramType === 'layout' ? layoutDocument.objects.length > 0 : nodes.length > 0) && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: '#1a202c',
              border: '1px solid #4a5568',
              borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
              zIndex: 1000,
              minWidth: 160,
              overflow: 'hidden',
            }}>
              {(diagramType === 'layout' ? [
                { fmt: 'pdf-vector', label: '📄 PDF vector', desc: 'Scalable · best for print' },
                { fmt: 'pdf', label: '📄 PDF compatibility', desc: '300 DPI · widest support' },
                { fmt: 'svg', label: '📐 SVG vector', desc: 'Infinitely scalable' },
                { fmt: 'png', label: '🖼 PNG image', desc: '300 DPI raster' },
              ] : [
                { fmt: 'png', label: '🖼 PNG image', desc: 'High-resolution raster' },
                { fmt: 'svg', label: '📐 SVG vector', desc: 'Infinitely scalable' },
                { fmt: 'pdf', label: '📄 PDF document', desc: 'High-res · downloads directly' },
              ]).map(({ fmt, label, desc }, index, formats) => (
                <button
                  key={fmt}
                  onClick={() => exportDiagram(fmt)}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: '10px 14px',
                    textAlign: 'left',
                    color: '#e2e8f0',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    borderBottom: index < formats.length - 1 ? '1px solid #2d3748' : 'none',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#2d3748'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >
                  {label}
                  <div style={{ fontSize: 11, fontWeight: 400, color: '#718096', marginTop: 1 }}>{desc}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => loadList()}
          title={isAuthenticated ? 'Open your saved diagrams' : 'Sign in to access saved diagrams'}
          style={{
            ...tbBtn('#2b6cb0', '#bee3f8', '#1a365d'),
            ...(isAuthenticated ? null : { opacity: 0.5, cursor: 'not-allowed' }),
          }}
        >
          📂 My Diagrams
        </button>
        <button
          onClick={() => { setShowNewPicker(true); loadTemplatesSilent(); }}
          style={tbBtn('#4a5568', '#e2e8f0', '#1a202c')}
          title="Create a blank diagram or start from a template"
        >＋ New</button>
        {diagramType === 'schematic' && <button
          onClick={nodes.length ? renumberLabels : undefined}
          title={nodes.length ? 'Renumber all labels top-left → bottom-right (R1, R2… C1, C2…)' : 'Add components first'}
          style={{ ...tbBtn('#744210', '#fefcbf', '#5c370a'), ...(nodes.length ? null : { opacity: 0.45, cursor: 'not-allowed' }) }}
        >⟳ Renumber</button>}
        {accessBannerText && (
          <div style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.78)', fontSize: 12 }}>
            {accessBannerText}
          </div>
        )}
      </div>

      {saveMessage && saveStatus !== 'saved' && (
        <div style={{ padding: '10px 16px', background: saveStatus === 'error' ? '#fff7ed' : '#eff6ff', borderBottom: `1px solid ${saveStatus === 'error' ? '#fdba74' : '#bfdbfe'}`, color: saveStatus === 'error' ? '#9a3412' : '#1d4ed8', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{saveStatus === 'error' ? `✗ ${saveMessage}` : saveMessage}</span>
          {!isAuthenticated && (
            <a href="/signup" style={{ color: 'inherit', fontWeight: 700 }}>Create account</a>
          )}
          {isAuthenticated && !isPro && hasReachedDiagramLimit && (
            <a href="/profile" style={{ color: 'inherit', fontWeight: 700 }}>Upgrade to Pro</a>
          )}
        </div>
      )}

      {diagramType === 'layout' && isAdmin ? (
        <LayoutStudio ref={layoutStudioRef} document={layoutDocument} onChange={setLayoutDocument} />
      ) : (<>
      {/* ── Tool Bar ── */}
      <div style={{ display: 'flex', flexDirection: 'column', background: '#2d3748', borderBottom: '1px solid #4a5568', flexShrink: 0 }}>

        {/* Main toolbar row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px' }}>
          <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginRight: 2, userSelect: 'none' }}>Tools</span>

          {/* ── Grouped tool buttons: Pointer | Wire | Marquee ── */}
          <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 9, overflow: 'hidden', flexShrink: 0 }}>

            {/* Pointer */}
            <button
              className="tool-btn"
              onClick={() => { setActiveTool('pointer'); setDrawState(null); setMarqueeState(null); }}
              data-tooltip={'Pointer\nSelect and drag components or wires.\nDefault mode.'}
              style={{
                borderRadius: 0, border: 'none',
                background: activeTool === 'pointer' ? '#2b6cb0' : 'rgba(255,255,255,0.07)',
                color: activeTool === 'pointer' ? '#bee3f8' : 'rgba(255,255,255,0.65)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 2l12 7-6 1.5L7.5 17 3 2z" fill="currentColor"/>
              </svg>
            </button>

            <div style={{ width: 1, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }}/>

            {/* Draw Wire */}
            <button
              className="tool-btn"
              onClick={() => { setActiveTool(activeTool === 'wire' ? 'pointer' : 'wire'); setDrawState(null); setMarqueeState(null); }}
              data-tooltip={'Draw Wire\nClick to place wire points.\nDouble-click or press Enter to finish.\nEscape cancels.\n\nWhen active, a Crossing sub-bar\nappears below to set wire style.'}
              style={{
                borderRadius: 0, border: 'none',
                background: activeTool === 'wire' ? '#6b21a8' : 'rgba(255,255,255,0.07)',
                color: activeTool === 'wire' ? '#e9d5ff' : 'rgba(255,255,255,0.65)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M13.5 2.5l2 2-9 9-2.5.5.5-2.5 9-9z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <line x1="3" y1="15.5" x2="15" y2="15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="2 2"/>
              </svg>
            </button>

            <div style={{ width: 1, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }}/>

            {/* Marquee Select */}
            <button
              className="tool-btn"
              onClick={() => { setActiveTool(activeTool === 'marquee' ? 'pointer' : 'marquee'); setDrawState(null); setMarqueeState(null); }}
              data-tooltip={'Marquee Select\nDrag a rectangle to select\nmultiple components at once.\nThen switch to Pointer to move them.'}
              style={{
                borderRadius: 0, border: 'none',
                background: activeTool === 'marquee' ? '#c05621' : 'rgba(255,255,255,0.07)',
                color: activeTool === 'marquee' ? '#feebc8' : 'rgba(255,255,255,0.65)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="2.5" y="2.5" width="13" height="13" rx="1.5"
                  stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" fill="none"/>
                <line x1="9" y1="6" x2="9" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                <line x1="6" y1="9" x2="12" y2="9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Separator — tool group | Undo */}
          <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.15)', margin: '0 2px', flexShrink: 0 }}/>

          {/* Undo */}
          <button
            className="tool-btn"
            onClick={undo}
            data-tooltip={'Undo  (⌘Z / Ctrl+Z)\nStep back one change.'}
            style={{
              background: 'rgba(255,255,255,0.07)',
              borderColor: 'transparent',
              color: 'rgba(255,255,255,0.65)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M3.5 9A5.5 5.5 0 1 0 9 3.5H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="3,6 6,3.5 3,1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Wire crossing sub-bar — only visible when Draw Wire is active */}
        {activeTool === 'wire' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px 5px 14px', borderTop: '1px solid rgba(192,132,252,0.25)', background: 'rgba(107,33,168,0.2)' }}>
            <span style={{ color: '#c084fc', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', userSelect: 'none', marginRight: 2 }}>Crossing</span>

            {/* Jump arc mode */}
            <button
              className="tool-btn"
              onClick={() => setDrawNoJump(false)}
              data-tooltip={'Jump: arc over crossing wires\n(non-connected crossing)'}
              style={{
                background: !drawNoJump ? '#4c1d95' : 'rgba(255,255,255,0.07)',
                borderColor: !drawNoJump ? '#c084fc' : 'transparent',
                color: !drawNoJump ? '#e9d5ff' : 'rgba(255,255,255,0.5)',
              }}
            >
              <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                <line x1="0" y1="6" x2="6" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M6 6 A4 4 0 0 1 14 6" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <line x1="14" y1="6" x2="20" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="10" y1="2" x2="10" y2="10" stroke="currentColor" strokeWidth="1" strokeDasharray="2 1" opacity="0.45"/>
              </svg>
            </button>

            {/* Connect mode */}
            <button
              className="tool-btn"
              onClick={() => setDrawNoJump(true)}
              data-tooltip={'Connect: wire joins at crossing\n(junction dot)'}
              style={{
                background: drawNoJump ? '#4c1d95' : 'rgba(255,255,255,0.07)',
                borderColor: drawNoJump ? '#c084fc' : 'transparent',
                color: drawNoJump ? '#e9d5ff' : 'rgba(255,255,255,0.5)',
              }}
            >
              <svg width="20" height="12" viewBox="0 0 20 12" fill="none">
                <line x1="0" y1="6" x2="20" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <line x1="10" y1="2" x2="10" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="10" cy="6" r="2.5" fill="currentColor"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Shape Palette ── */}
        <div style={{ width: 140, background: '#f8fafc', borderRight: '1px solid #e2e8f0', padding: '10px 6px', overflowY: 'auto', flexShrink: 0 }}>
          {/* Grouped, collapsible palette — groups derived from PALETTE.category order */}
          {(() => {
            const groups = [];
            const seenCats = new Map();
            PALETTE.forEach(item => {
              if (!seenCats.has(item.category)) {
                const items = [];
                seenCats.set(item.category, items);
                groups.push({ cat: item.category, items });
              }
              seenCats.get(item.category).push(item);
            });
            return groups.map(({ cat, items }, gi) => {
              const isOpen = openCategories.has(cat);
              const toggle = () => setOpenCategories(prev => {
                const next = new Set(prev);
                if (next.has(cat)) next.delete(cat); else next.add(cat);
                return next;
              });
              return (
                <div key={cat}>
                  <button
                    onClick={toggle}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '4px 6px 3px', marginTop: gi === 0 ? 0 : 8, marginBottom: isOpen ? 5 : 0,
                      border: 'none', borderRadius: 5, background: '#e8edf3',
                      cursor: 'pointer', color: '#475569',
                      fontSize: 9, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                    }}
                  >
                    <span>{cat}</span>
                    <span style={{ fontSize: 9, lineHeight: 1 }}>{isOpen ? '▾' : '▸'}</span>
                  </button>
                  {isOpen && items.map((item, i) => (
                    <div
                      key={i}
                      draggable
                      onDragStart={e => onDragStart(e, item)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        padding: '7px 4px 5px', marginBottom: 5, borderRadius: 7,
                        background: 'white', cursor: 'grab',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        transition: 'box-shadow 0.15s, border-color 0.15s',
                        userSelect: 'none',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(37,99,235,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
                        {palettePreview(item)}
                      </div>
                      <span style={{ color: '#374151', fontSize: 9.5, textAlign: 'center', lineHeight: 1.3, fontWeight: 500 }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </div>

        {/* ── Canvas ── */}
        <div ref={reactFlowWrapper} style={{ flex: 1, position: 'relative' }} onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={drawMode ? undefined : onPaneClick}
            onNodeContextMenu={onNodeContextMenu}
            onEdgeContextMenu={onEdgeContextMenu}
            onNodeDragStart={onNodeDragStart}
            onNodeDrag={onNodeDrag}
            onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            deleteKeyCode="Delete"
            snapToGrid
            snapGrid={[10, 10]}
            elevateEdgesOnSelect
            edgesReconnectable
            onReconnect={onReconnect}
            onReconnectStart={onReconnectStart}
            onReconnectEnd={onReconnectEnd}
            nodesDraggable={activeTool === 'pointer'}
            panOnDrag={activeTool === 'pointer'}
            multiSelectionKeyCode={['Meta', 'Control']}
            connectionMode="loose"
            connectionRadius={12}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#cbd5e0" />
            <Controls />
          </ReactFlow>

          {/* ── Context menu ── */}
          {ctxMenu && (
            <>
              {/* Transparent backdrop closes the menu on any outside click */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                onClick={closeCtxMenu}
                onContextMenu={e => { e.preventDefault(); closeCtxMenu(); }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: ctxMenu.y,
                  left: ctxMenu.x,
                  zIndex: 9999,
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.14)',
                  minWidth: 178,
                  overflow: 'hidden',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: 13,
                  userSelect: 'none',
                }}
                onMouseDown={e => e.stopPropagation()}
              >
                {/* Header label */}
                <div style={{ padding: '6px 12px 5px', borderBottom: '1px solid #f1f5f9', color: '#94a3b8', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {ctxMenu.isEdge ? 'Wire' : 'Component'}
                </div>
                {/* Edit operations */}
                {!ctxMenu.isEdge && (
                  <div style={{ padding: '3px 0 3px', borderBottom: '1px solid #f1f5f9' }}>
                    {[
                      { label: 'Copy',   hint: '⌘C', action: () => { copySelection(); closeCtxMenu(); } },
                      { label: 'Cut',    hint: '⌘X', action: () => { cutSelection();  closeCtxMenu(); } },
                      { label: 'Paste',  hint: '⌘V', action: () => { pasteSelection(); closeCtxMenu(); }, disabled: !clipboardRef.current },
                      { label: 'Delete', hint: '⌫',  action: () => { deleteSelection(); closeCtxMenu(); }, red: true },
                    ].map(({ label, hint, action, disabled, red }) => (
                      <button
                        key={label}
                        onClick={disabled ? undefined : action}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '6px 14px',
                          border: 'none', background: 'transparent',
                          cursor: disabled ? 'default' : 'pointer', textAlign: 'left',
                          color: disabled ? '#cbd5e1' : red ? '#dc2626' : '#1e293b', fontSize: 13,
                        }}
                        onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = red ? '#fef2f2' : '#f1f5f9'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span>{label}</span>
                        <span style={{ fontSize: 11, color: disabled ? '#e2e8f0' : '#94a3b8', marginLeft: 16 }}>{hint}</span>
                      </button>
                    ))}
                  </div>
                )}
                {/* Z-order section */}
                <div style={{ padding: '3px 0 3px' }}>
                  {[
                    { label: 'Bring to Front', icon: '⬆⬆', op: 'front' },
                    { label: 'Bring Forward',  icon: '⬆',   op: 'forward' },
                    { label: 'Send Back',       icon: '⬇',   op: 'backward' },
                    { label: 'Send to Back',    icon: '⬇⬇', op: 'back' },
                  ].map(({ label, icon, op }) => (
                    <button
                      key={op}
                      onClick={() => applyZOrder(ctxMenu.targetId, ctxMenu.isEdge, op)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9,
                        width: '100%', padding: '6px 14px',
                        border: 'none', background: 'transparent',
                        cursor: 'pointer', textAlign: 'left',
                        color: '#1e293b', fontSize: 13,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ width: 20, textAlign: 'center', fontSize: 11, opacity: 0.5 }}>{icon}</span>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Draw mode: transparent overlay captures clicks; SVG shows live preview */}
          {drawMode && (
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: drawState ? 'crosshair' : 'cell' }}
              onClick={onOverlayClick}
              onMouseMove={onOverlayMouseMove}
              onDoubleClick={onOverlayDblClick}
            />
          )}

          {/* Marquee Select: transparent overlay captures mouse for rubber-band rect */}
          {activeTool === 'marquee' && (
            <div
              style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'crosshair' }}
              onMouseDown={e => {
                if (e.button !== 0) return;
                const rect = reactFlowWrapper.current.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                setMarqueeState({ x0: x, y0: y, x1: x, y1: y });
              }}
              onMouseMove={e => {
                if (!marqueeState) return;
                const rect = reactFlowWrapper.current.getBoundingClientRect();
                setMarqueeState(s => s ? ({ ...s, x1: e.clientX - rect.left, y1: e.clientY - rect.top }) : s);
              }}
              onMouseUp={e => {
                if (!marqueeState) return;
                const wrapRect = reactFlowWrapper.current.getBoundingClientRect();
                const x1 = e.clientX - wrapRect.left;
                const y1 = e.clientY - wrapRect.top;
                const ms = { ...marqueeState, x1, y1 };
                // Convert the two corners to flow coordinates
                const flowA = screenToFlowPosition({ x: wrapRect.left + Math.min(ms.x0, ms.x1), y: wrapRect.top + Math.min(ms.y0, ms.y1) });
                const flowB = screenToFlowPosition({ x: wrapRect.left + Math.max(ms.x0, ms.x1), y: wrapRect.top + Math.max(ms.y0, ms.y1) });
                // Select all nodes whose bounding box overlaps the marquee rect
                const selectedIds = new Set(
                  getNodes().filter(n => {
                    const nw = n.measured?.width  ?? n.width  ?? 60;
                    const nh = n.measured?.height ?? n.height ?? 40;
                    return n.position.x < flowB.x && n.position.x + nw > flowA.x &&
                           n.position.y < flowB.y && n.position.y + nh > flowA.y;
                  }).map(n => n.id)
                );
                setNodes(nds => nds.map(n => ({ ...n, selected: selectedIds.has(n.id) })));
                // Also select edges whose both endpoints are inside the marquee
                setEdges(eds => eds.map(e => ({
                  ...e,
                  selected: selectedIds.has(e.source) && selectedIds.has(e.target),
                })));
                setMarqueeState(null);
                // Stay in marquee mode so the user can do multiple selections
              }}
            />
          )}
          {/* Marquee: SVG rubber-band rectangle preview */}
          {activeTool === 'marquee' && marqueeState && (() => {
            const x = Math.min(marqueeState.x0, marqueeState.x1);
            const y = Math.min(marqueeState.y0, marqueeState.y1);
            const w = Math.abs(marqueeState.x1 - marqueeState.x0);
            const h = Math.abs(marqueeState.y1 - marqueeState.y0);
            return (
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9 }} overflow="visible">
                <rect x={x} y={y} width={w} height={h}
                  fill="rgba(194,124,0,0.08)" stroke="#c05621" strokeWidth={1.5} strokeDasharray="5 3"
                />
              </svg>
            );
          })()}
          {drawMode && drawState && reactFlowWrapper.current && (() => {
            const previewPts = drawState.snapPt
              ? [...drawState.pts, drawState.snapPt]
              : drawState.pts;
            const svgPts = previewPts.map(toSvgCoord);
            const d = svgPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
            const startSvg = svgPts[0];
            return (
              <svg
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9 }}
                overflow="visible"
              >
                <path d={d} fill="none" stroke="#7c3aed" strokeWidth={2} strokeDasharray="6 3" />
                {/* Confirmed waypoints — filled circles */}
                {svgPts.slice(0, -1).map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={i === 0 ? 5 : 4} fill="#7c3aed" />
                ))}
                {/* Start label */}
                <text x={startSvg.x + 8} y={startSvg.y - 6} fontSize={11} fill="#7c3aed" fontFamily="monospace">
                  {drawState.startHandle ? '⚡ pin' : 'start'}
                </text>
              </svg>
            );
          })()}
        </div>

        {/* ── Properties Panel ── */}
        <div style={{ width: 220, background: 'white', borderLeft: '1px solid #e2e8f0', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '10px 16px', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13, color: '#1a202c' }}>
            Properties
          </div>
          {selectedEdge ? (
            <WirePropertiesPanel
              edge={selectedEdge}
              onChange={onEdgeDataChange}
              onDelete={onDeleteEdge}
            />
          ) : (
            <PropertiesPanel
              node={selectedNode}
              onChange={onNodeDataChange}
              onDelete={onDeleteNode}
            />
          )}
        </div>
      </div>
      </>)}

      {/* ── My Diagrams Modal ── */}
      {firstLayoutSaveName !== null && (
        <div
          role="presentation"
          onClick={() => setFirstLayoutSaveName(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 10020, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(17,24,39,0.58)' }}
        >
          <form
            role="dialog"
            aria-modal="true"
            aria-labelledby="first-layout-save-title"
            onClick={event => event.stopPropagation()}
            onSubmit={event => {
              event.preventDefault();
              const title = firstLayoutSaveName.trim();
              if (!title) return;
              setDiagramTitle(title);
              setFirstLayoutSaveName(null);
              save(title, true);
            }}
            style={{ width: 'min(420px, 100%)', padding: 22, borderRadius: 8, background: '#fff', boxShadow: '0 18px 50px rgba(0,0,0,0.3)' }}
          >
            <h2 id="first-layout-save-title" style={{ margin: '0 0 8px', color: '#1a202c', fontSize: 19 }}>Name this layout</h2>
            <p style={{ margin: '0 0 16px', color: '#4a5568', fontSize: 13, lineHeight: 1.5 }}>
              Give the layout a useful name before its first save.
            </p>
            <label style={{ ...labelStyle, marginBottom: 18 }}>
              Layout name
              <input
                autoFocus
                style={inputStyle}
                value={firstLayoutSaveName}
                onChange={event => setFirstLayoutSaveName(event.target.value)}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => {
                  setAutoSave(false);
                  setFirstLayoutSaveName(null);
                }}
                style={{ padding: '8px 12px', border: '1px solid #cbd5e0', borderRadius: 6, background: '#f7fafc', color: '#4a5568', fontWeight: 600, cursor: 'pointer' }}
              >
                Turn autosave off
              </button>
              <button
                type="submit"
                disabled={!firstLayoutSaveName.trim()}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#276749', color: '#fff', fontWeight: 700, cursor: firstLayoutSaveName.trim() ? 'pointer' : 'not-allowed', opacity: firstLayoutSaveName.trim() ? 1 : 0.5 }}
              >
                Save layout
              </button>
            </div>
          </form>
        </div>
      )}
      {/* ── Diagram Info Modal ── */}
      {showInfoModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: 28, width: 460, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#1a202c' }}>ℹ️ Diagram Info</h3>
              <button onClick={() => setShowInfoModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#4a5568', lineHeight: 1 }}>✕</button>
            </div>
            <p style={{ margin: '0 0 18px', fontSize: 12, color: '#718096' }}>Printed in the lower-right corner of exported PNGs. Title comes from the diagram title above.</p>
            {[['Author', 'author', 'Your name'], ['Document #', 'doc_number', 'e.g. DOC-001'], ['Revision', 'revision', 'e.g. 1.0'], ['Date', 'revision_date', 'MM/DD/YYYY']].map(([label, field, ph]) => (
              <div key={field} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>{label}</label>
                <input
                  type="text"
                  value={diagramInfo[field]}
                  onChange={e => setDiagramInfo(prev => ({ ...prev, [field]: e.target.value }))}
                  placeholder={ph}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #cbd5e0', fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4a5568', marginBottom: 4 }}>Notes</label>
              <textarea
                value={diagramInfo.notes}
                onChange={e => setDiagramInfo(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={3}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #cbd5e0', fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
              />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button onClick={() => setShowInfoModal(false)} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: '#2c5282', color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}

      {showDiagramList && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { setShowDiagramList(false); setRenamingId(null); }}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: 24, width: 680, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#1a202c' }}>My Wiring Diagrams</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => { setShowDiagramList(false); setShowNewPicker(true); }}
                  style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  ＋ New Diagram
                </button>
                <button
                  onClick={() => { setShowDiagramList(false); setRenamingId(null); }}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#4a5568', lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Top-level tabs: Diagrams | Templates */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderBottom: '2px solid #e2e8f0', flexShrink: 0 }}>
              {[['diagrams', '📂 Diagrams'], ['templates', '📋 Templates']].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => { setDiagramListTab(key); if (key === 'templates') loadTemplatesSilent(); }}
                  style={{
                    padding: '7px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14,
                    fontWeight: diagramListTab === key ? 700 : 500,
                    color: diagramListTab === key ? '#1d4ed8' : '#64748b',
                    borderBottom: `2px solid ${diagramListTab === key ? '#1d4ed8' : 'transparent'}`,
                    marginBottom: -2,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* ── DIAGRAMS TAB ── */}
            {diagramListTab === 'diagrams' && (
              <>
                {/* Column headers */}
                {myDiagrams.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 90px 100px 120px', gap: '0 8px', padding: '4px 10px 6px', borderBottom: '1px solid #e2e8f0', marginBottom: 4, flexShrink: 0 }}>
                    {['Name', 'Type', 'Visibility', 'Updated', ''].map((h, i) => (
                      <span key={i} style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.6 }}>{h}</span>
                    ))}
                  </div>
                )}

                {/* Rows */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {myDiagrams.length === 0
                    ? <p style={{ color: '#888', fontSize: 14, padding: '8px 4px' }}>No saved diagrams yet. Click «＋ New Diagram» to start.</p>
                    : myDiagrams.map(d => {
                    const isRenaming = renamingId === d.diagram_id;
                    const isCurrent = diagramId === d.diagram_id;
                    return (
                      <div
                        key={d.diagram_id}
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr 80px 90px 100px 120px', gap: '0 8px',
                          alignItems: 'center', padding: '8px 10px',
                          borderRadius: 8, marginBottom: 2,
                          background: isCurrent ? '#eff6ff' : 'white',
                          border: `1px solid ${isCurrent ? '#bfdbfe' : '#e2e8f0'}`,
                          transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'white'; }}
                      >
                        {/* Title / inline rename */}
                        {isRenaming ? (
                          <form
                            style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                            onSubmit={e => { e.preventDefault(); renameDiagram(d.diagram_id, renameValue); }}
                          >
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Escape') setRenamingId(null); }}
                              style={{ flex: 1, fontSize: 13, padding: '3px 7px', border: '1.5px solid #3b82f6', borderRadius: 5, outline: 'none', minWidth: 0 }}
                            />
                            <button type="submit" title="Save" style={{ background: '#22c55e', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', color: 'white', fontSize: 12, fontWeight: 700 }}>✓</button>
                            <button type="button" title="Cancel" onClick={() => setRenamingId(null)} style={{ background: '#e2e8f0', border: 'none', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', fontSize: 12 }}>✕</button>
                          </form>
                        ) : (
                          <button
                            onClick={() => openDiagram(d.diagram_id)}
                            title="Open diagram"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: isCurrent ? 700 : 600, color: isCurrent ? '#1d4ed8' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
                          >
                            {d.title}
                            {isCurrent && <span style={{ fontSize: 10, fontWeight: 500, color: '#3b82f6', marginLeft: 6, background: '#dbeafe', borderRadius: 4, padding: '1px 5px' }}>open</span>}
                          </button>
                        )}

                        {/* Type */}
                        <span style={{ fontSize: 12, color: '#64748b' }}>{d.diagram_type}</span>

                        {/* Visibility */}
                        <span style={{ fontSize: 12, color: d.is_public ? '#059669' : '#64748b' }}>
                          {d.is_public ? '🌐 public' : '🔒 private'}
                        </span>

                        {/* Updated */}
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>
                          {new Date(d.updated_at).toLocaleDateString()}
                        </span>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          {/* Rename */}
                          <button
                            title="Rename"
                            onClick={e => { e.stopPropagation(); setRenamingId(d.diagram_id); setRenameValue(d.title); }}
                            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center' }}
                          >
                            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                              <path d="M10 2l2 2-7 7H3v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {/* Duplicate */}
                          <button
                            title="Duplicate"
                            onClick={e => { e.stopPropagation(); duplicateDiagram(d.diagram_id, d.title); }}
                            style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center' }}
                          >
                            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                              <rect x="4" y="4" width="8" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.3"/>
                              <path d="M2 10V2.8A.8.8 0 0 1 2.8 2H10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                            </svg>
                          </button>
                          {/* Save as Template */}
                          <button
                            title="Save as template"
                            onClick={e => {
                              e.stopPropagation();
                              saveAsTemplate(d.title, d.diagram_id, d.diagram_type);
                            }}
                            style={{ background: 'none', border: '1px solid #c4b5fd', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', color: '#6d28d9', display: 'flex', alignItems: 'center' }}
                          >
                            {/* bookmark icon */}
                            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                              <path d="M3 2h8a1 1 0 0 1 1 1v9l-5-3-5 3V3a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          {/* Delete */}
                          <button
                            title="Delete"
                            onClick={e => { e.stopPropagation(); deleteDiagram(d.diagram_id, d.title); }}
                            style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 5, padding: '3px 6px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center' }}
                          >
                            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                              <path d="M2 4h10M5 4V2.5h4V4M5.5 6v5M8.5 6v5M3 4l.7 7.5a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L11 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })
              }
            </div>

            {/* Diagrams tab footer */}
            {diagramListTab === 'diagrams' && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>
                {myDiagrams.length} diagram{myDiagrams.length !== 1 ? 's' : ''} · Click a name to open · ✏ rename · ⧉ duplicate · 🔖 save as template · 🗑 delete
              </div>
            )}
            </>
            )}

            {/* ── TEMPLATES TAB ── */}
            {diagramListTab === 'templates' && (
              <>
                {/* Sub-tabs: Public Library | My Templates */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                  {[['public', '🌐 Public Library'], ['mine', '📁 My Templates']].map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTemplateTab(key)}
                      style={{
                        padding: '6px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
                        fontWeight: templateTab === key ? 700 : 500,
                        color: templateTab === key ? '#553c9a' : '#64748b',
                        borderBottom: `2px solid ${templateTab === key ? '#553c9a' : 'transparent'}`,
                        marginBottom: -1,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Template rows */}
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {loadingTemplates ? (
                    <p style={{ color: '#888', fontSize: 14, padding: '8px 4px' }}>Loading templates…</p>
                  ) : (() => {
                    const isAdmin = user?.is_admin;
                    const userId = user?.user_id || user?.userId;
                const shown = templates.filter(t =>
                  templateTab === 'public' ? t.is_public : t.created_by === userId
                );
                if (shown.length === 0) {
                  return (
                    <p style={{ color: '#94a3b8', fontSize: 14, padding: '16px 4px' }}>
                      {templateTab === 'public'
                        ? 'No public templates yet. Save a diagram as a template, then make it public from My Templates.'
                        : 'No personal templates yet. Click 🔖 on a diagram in My Diagrams to save it as a template.'}
                    </p>
                  );
                }
                return shown.map(t => (
                  <div
                    key={t.template_id}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 10px',
                      borderRadius: 8, marginBottom: 6,
                      border: '1px solid #e2e8f0', background: '#fafafa',
                    }}
                  >
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>
                        {t.title}
                        <span style={{ marginLeft: 7, fontSize: 11, padding: '1px 6px', borderRadius: 4, background: '#e2e8f0', color: '#475569', verticalAlign: 'middle' }}>
                          {t.diagram_type}
                        </span>
                        {t.is_public && (
                          <span style={{ marginLeft: 5, fontSize: 11, padding: '1px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', verticalAlign: 'middle' }}>
                            public
                          </span>
                        )}
                      </div>
                      {t.description && (
                        <div style={{ fontSize: 12, color: '#64748b' }}>{t.description}</div>
                      )}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        by {t.created_by_username} · {new Date(t.updated_at).toLocaleDateString()}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      {/* Owner/admin: toggle public */}
                      {(t.created_by === userId || isAdmin) && (
                        <button
                          title={t.is_public ? 'Make private' : 'Make public'}
                          onClick={() => toggleTemplatePublic(t.template_id, t.is_public)}
                          style={{
                            padding: '5px 10px', borderRadius: 6, border: `1px solid ${t.is_public ? '#fca5a5' : '#86efac'}`,
                            background: t.is_public ? '#fee2e2' : '#dcfce7', color: t.is_public ? '#dc2626' : '#16a34a',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          {t.is_public ? '🔒 Make Private' : '🌐 Make Public'}
                        </button>
                      )}
                      {/* Delete (own or admin) */}
                      {(t.created_by === userId || isAdmin) && (
                        <button
                          title="Delete template"
                          onClick={() => deleteTemplate(t.template_id, t.title)}
                          style={{ background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '5px 7px', cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                            <path d="M2 4h10M5 4V2.5h4V4M5.5 6v5M8.5 6v5M3 4l.7 7.5a1 1 0 0 0 1 .9h4.6a1 1 0 0 0 1-.9L11 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      {/* Use Template */}
                      <button
                        onClick={() => applyTemplate(t)}
                        style={{ padding: '5px 14px', borderRadius: 6, border: 'none', background: '#553c9a', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                      >
                        Use Template
                      </button>
                    </div>
                  </div>
                ));
              })()}
                </div>

                {/* Templates tab footer */}
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0', fontSize: 12, color: '#94a3b8', flexShrink: 0 }}>
                  "Use Template" starts a new unsaved diagram pre-filled with the template's content.
                  {user?.is_admin && ' Admins can toggle public/private on any template.'}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── New Diagram Picker ── */}
      {showNewPicker && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowNewPicker(false)}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: 24, width: 600, maxWidth: '95vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#1a202c' }}>New Diagram</h3>
              <button onClick={() => setShowNewPicker(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#4a5568', lineHeight: 1 }}>✕</button>
            </div>

            {/* Blank */}
            <button
              onClick={() => { setShowNewPicker(false); newDiagram(); }}
              style={{
                width: '100%', padding: '16px 20px', borderRadius: 10, border: '2px solid #e2e8f0',
                background: 'white', cursor: 'pointer', textAlign: 'left', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 14,
                transition: 'border-color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
            >
              <span style={{ fontSize: 28 }}>📄</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Blank Drawing</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Start with an empty canvas</div>
              </div>
            </button>

            {/* Templates */}
            {templates.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10, flexShrink: 0 }}>
                  — or start from a template —
                </div>
                <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {templates.map(t => (
                    <button
                      key={t.template_id}
                      onClick={() => { setShowNewPicker(false); applyTemplate(t); }}
                      style={{
                        width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                        background: 'white', cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#c4b5fd'; e.currentTarget.style.background = '#faf5ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'white'; }}
                    >
                      <span style={{ fontSize: 20 }}>📋</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                          {t.title}
                          <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 5px', borderRadius: 4, background: '#e2e8f0', color: '#475569', verticalAlign: 'middle' }}>
                            {t.diagram_type}
                          </span>
                          {t.is_public && (
                            <span style={{ marginLeft: 4, fontSize: 11, padding: '1px 5px', borderRadius: 4, background: '#dcfce7', color: '#166534', verticalAlign: 'middle' }}>
                              public
                            </span>
                          )}
                        </div>
                        {t.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{t.description}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
            {templates.length === 0 && loadingTemplates && (
              <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>Loading templates…</p>
            )}
          </div>
        </div>
      )}

      {/* ── Save-as-Template Dialog ── */}
      {savingTemplate && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1010 }}
          onClick={() => setSavingTemplate(null)}
        >
          <div
            style={{ background: 'white', borderRadius: 12, padding: 28, width: 440, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 17, color: '#1a202c' }}>Save as Template</h3>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Template Name
            </label>
            <input
              autoFocus
              value={savingTemplate.title}
              onChange={e => setSavingTemplate(s => ({ ...s, title: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1.5px solid #cbd5e0', fontSize: 14, boxSizing: 'border-box', marginBottom: 14 }}
              placeholder="Untitled Template"
            />

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Description <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
            </label>
            <textarea
              value={savingTemplate.description}
              onChange={e => setSavingTemplate(s => ({ ...s, description: e.target.value }))}
              rows={2}
              placeholder="Brief description of this template…"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1.5px solid #cbd5e0', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', marginBottom: 14 }}
            />

            {user?.is_admin && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', marginBottom: 18, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={savingTemplate.is_public}
                  onChange={e => setSavingTemplate(s => ({ ...s, is_public: e.target.checked }))}
                  style={{ width: 15, height: 15 }}
                />
                <span>Make public (visible to all users)</span>
              </label>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => setSavingTemplate(null)}
                style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSaveAsTemplate}
                disabled={!savingTemplate.title.trim()}
                style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: savingTemplate.title.trim() ? '#553c9a' : '#c4b5fd', color: 'white', fontSize: 14, fontWeight: 600, cursor: savingTemplate.title.trim() ? 'pointer' : 'not-allowed' }}
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DiagramSettingsCtx.Provider>
  );
};

// Toolbar button helper
const tbBtn = (bg, color, hoverBg) => ({
  padding: '6px 14px', borderRadius: 6, border: 'none', background: bg,
  color: color, fontSize: 13, fontWeight: 600, cursor: 'pointer',
});

const WiringDiagramStudio = () => (
  <ReactFlowProvider>
    <WiringDiagramStudioInner />
  </ReactFlowProvider>
);

export default WiringDiagramStudio;
